import * as THREE from 'three';
import { boxGeo, cylGeo, sphereGeo, sagGeo, taperGeo, timberGeo, sheetGeo, trs, vary, shade, tint, accent, surfaceTexel, TEXEL, TUNING as GT } from './Geometry.js';

/**
 * Procedural prop library for the back-lot.
 *
 * Every function here writes into a `Batcher`, so a hundred oil drums cost one draw
 * call and still differ in tint, rotation and dent. Props are authored at real-world
 * scale in metres — a shipping container is 6.06 x 2.44 x 2.59 because that number
 * is what makes a player read it as a shipping container rather than "a big box".
 *
 * Collision policy: bulky props register their own AABB; thin decoration (awning
 * canvas, cables, wall pipes, signage) is visual-only, and anything angled gets a
 * hand-placed axis-aligned approximation instead of a lying bounding box.
 */

export const TUNING = {
  containerL: 6.06,
  containerW: 2.44,
  /** ISO 1C height is 2.591 m. Rounding it to 2.5 costs nothing at the desk and
      shows up in game as a container a player can very nearly see over. */
  containerH: 2.59,
  drumR: 0.29,
  drumH: 0.88,
  palletH: 0.15,
  sandbagW: 0.52,
  sandbagH: 0.21,
  /* 2R + G = 0.65, inside the 0.60–0.65 comfort rule. The old 0.22/0.40 gave 0.84:
     treads so deep the player's stride never lands twice on the same step, which is
     one of the loudest possible tells that a level was laid out on a grid. */
  stepRise: 0.185,
  stepRun: 0.28,
  /** Clear height under a door head. Anything taller stops reading as a door. */
  doorHead: 2.10,
};

const CONTAINER_COLOURS = [0x8a4a32, 0x3f6070, 0x6d7a52, 0x9b8340, 0x5a4a55, 0x7a3a34, 0x46685c];
const CLOTH_COLOURS = [0xb0563c, 0x2f6070, 0x9c8a4a, 0x7a4a68, 0x486a48, 0xa8a08c];

const _UP = new THREE.Vector3(0, 1, 0);
const _ONE = new THREE.Vector3(1, 1, 1);
const _dir = new THREE.Vector3();
const _pos = new THREE.Vector3();

/**
 * A rod along an arbitrary direction. Euler triples are unusable for anything that
 * genuinely points somewhere — bent rebar, a guy wire, a snapped joist — so this
 * takes the direction vector and builds the matrix from it.
 */
export function rod(b, surface, x, y, z, dx, dy, dz, len, r, color, o = {}) {
  _dir.set(dx, dy, dz);
  if (_dir.lengthSq() < 1e-8) return;
  _dir.normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(_UP, _dir);
  const m = new THREE.Matrix4().compose(_pos.set(x, y, z), q, _ONE);
  b.add(surface, cylGeo(r, r, len, o.seg ?? 4, o.tile ?? 0.4), m, color, o.shadow ?? 'none');
}

/* -------------------------------------------------------------------------- */
/*                              small utilities                               */
/* -------------------------------------------------------------------------- */

/** Half-extents of a w x d footprint after a Y rotation — for colliders and cover. */
export function rotExtent(w, d, rotY) {
  const c = Math.abs(Math.cos(rotY));
  const s = Math.abs(Math.sin(rotY));
  return [(w * c + d * s) / 2, (w * s + d * c) / 2];
}

/** A box at an arbitrary orientation. Visual only unless you add a collider yourself. */
export function angled(b, surface, x, y, z, w, h, d, rx, ry, rz, o = {}) {
  const [at0, at1] = surfaceTexel(surface);
  b.add(surface, boxGeo(w, h, d, o.tile ?? at0, 0, 0, o.tileV ?? (o.tile ? 0 : at1)), trs(x, y, z, rx, ry, rz), o.color, o.shadow ?? 'both', true, o.mat);
}

/** A timber member at an arbitrary orientation, length along its local X. */
export function beam(b, x, y, z, len, h, d, rx, ry, rz, o = {}) {
  b.add('wood', timberGeo(len, h, d), trs(x, y, z, rx, ry, rz), o.color, o.shadow ?? 'both');
}

/** A cylinder at an arbitrary orientation, visual only. */
export function tube(b, surface, x, y, z, r, len, rx, ry, rz, o = {}) {
  b.add(
    surface,
    cylGeo(r, r, len, o.seg ?? 8, o.tile ?? GT.fineTile, o.open ?? false),
    trs(x, y, z, rx, ry, rz),
    o.color,
    o.shadow ?? 'cast'
  );
}

/**
 * A wall run with door/window openings punched out of it, emitted as solid slabs.
 * `axis` is the direction the wall runs; `at` is its centre on the other axis.
 * Openings are `{ a0, a1, y0, y1 }` in world coordinates along `axis`.
 */
export function wall(b, surface, o) {
  const { axis, from, to, at, y0, y1, thick, openings = [] } = o;
  const opts = {
    tile: o.tile,
    color: o.color,
    shadow: o.shadow ?? 'both',
    matId: o.matId,
    cover: o.cover,
  };
  const sorted = openings.slice().sort((p, q) => p.a0 - q.a0);
  let cursor = from;
  const put = (a0, a1, b0, b1) => {
    if (a1 - a0 < 0.02 || b1 - b0 < 0.02) return;
    const mid = (a0 + a1) / 2;
    const len = a1 - a0;
    const h = b1 - b0;
    const cy = (b0 + b1) / 2;
    if (axis === 'x') b.solid(surface, mid, cy, at, len, h, thick, opts);
    else b.solid(surface, at, cy, mid, thick, h, len, opts);
  };
  for (const op of sorted) {
    put(cursor, op.a0, y0, y1);
    put(op.a0, op.a1, y0, op.y0);
    put(op.a0, op.a1, op.y1, y1);
    cursor = Math.max(cursor, op.a1);
  }
  put(cursor, to, y0, y1);

  // Every opening gets trimmed. Doing it here rather than at 30-odd call sites is
  // the whole point: a wall that knows where its holes are can dress them, and a
  // hole that nobody dressed is the single most common way a level reads as a
  // blockout. `window_` has always done this; openings had been left raw.
  if (o.dress === false || !o.rng) return;
  for (const op of sorted) {
    opening(b, o.rng, {
      axis, at, a0: op.a0, a1: op.a1, y0: op.y0, y1: op.y1, thick,
      floor: y0, kind: op.kind, color: o.trim, masonry: surface === 'brick',
    });
  }
}

/**
 * Trim for a hole punched through a wall: reveal, head, threshold, and — for a
 * pedestrian door — a head capped at 2.1 m with the wall above it infilled.
 *
 * That cap matters more than it sounds. A doorway is the one object in a level whose
 * real-world size every player knows in their body, so a 3 m "door" silently rescales
 * the entire building around it and the player stops being able to judge any distance
 * in the space. Openings wider than a car stay full height and get a goalpost head
 * instead, because those genuinely are vehicle openings.
 */
export function opening(b, rng, o) {
  const { axis, at, a0, a1, y1, thick } = o;
  const y0 = o.y0 ?? 0;
  const span = a1 - a0;
  if (span < 0.35 || y1 - y0 < 0.35) return;
  const mid = (a0 + a1) / 2;
  const grounded = y0 <= (o.floor ?? 0) + 0.05;
  const ped = grounded && span <= 3.2;
  const head = ped ? Math.min(y1 - 0.15, y0 + TUNING.doorHead) : y1;
  const stone = o.color ?? tint(0xa8a08e);
  const jw = 0.14;

  const put = (ac, yc, along, h, deep, opts) => {
    if (h < 0.01 || along < 0.01) return;
    if (axis === 'x') b.solid(opts.surface ?? 'concrete', ac, yc, at, along, h, deep, opts);
    else b.solid(opts.surface ?? 'concrete', at, yc, ac, deep, h, along, opts);
  };

  // Reveals. The jambs stand proud of both wall faces so the opening carries a
  // shadow line down each side at every sun angle.
  for (const s of [-1, 1]) {
    put(mid + s * (span / 2 + jw / 2), (y0 + head) / 2, jw, head - y0, thick + 0.14, {
      surface: 'concrete', color: stone, collide: false, shadow: 'cast',
    });
  }
  // Head beam, overhanging the jambs so its shadow crosses the opening.
  put(mid, head + 0.1, span + jw * 2 + 0.18, 0.2, thick + 0.22, {
    surface: 'concrete', color: stone, collide: false, shadow: 'cast',
  });
  if (!ped) {
    // Vehicle opening: goalpost columns, so the head has something to land on.
    for (const s of [-1, 1]) {
      put(mid + s * (span / 2 + jw + 0.09), (y0 + head) / 2 + 0.05, 0.22, head - y0 + 0.2, thick + 0.26, {
        surface: 'concrete', color: stone, collide: false, shadow: 'cast',
      });
    }
  }
  // Transom: the wall above a capped door head, recessed rather than left as sky.
  const gap = y1 - head - 0.2;
  if (gap > 0.25) {
    put(mid, head + 0.2 + gap / 2, span - 0.03, gap, thick * 0.5, {
      surface: rng() < 0.4 ? 'wood' : 'concrete',
      color: tint(0x7a7264), collide: false, shadow: 'none',
    });
  }
  // Threshold: a worn step, proud of the floor on both sides.
  put(mid, y0 + 0.035, span + 0.18, 0.07, thick + 0.3, {
    surface: 'concrete', color: tint(0xb3aa97), collide: false, shadow: 'none',
  });

  if (!grounded) {
    // Not a door — a punched window or balcony opening. It gets a sill instead.
    put(mid, y0 - 0.07, span + 0.3, 0.13, thick + 0.3, {
      surface: 'concrete', color: tint(0xb0a695), collide: false, shadow: 'cast',
    });
    return;
  }

  // A door leaf, hung and mostly derelict: swung open, off one hinge, or gone.
  const roll = rng();
  if (ped && roll < 0.6) {
    const sgn = rng() < 0.5 ? -1 : 1;
    const ajar = (0.35 + rng() * 0.9) * sgn;
    const lw = span * 0.94;
    const hx = mid + sgn * span * 0.47;
    const dcol = vary(rng, rng() < 0.5 ? 0x5e6a5c : 0x6a5a44, 0.16, 0.05, { sat: 0.9 });
    const cx = hx - sgn * Math.cos(ajar) * lw / 2;
    const off = Math.sin(Math.abs(ajar)) * lw / 2;
    const ang = axis === 'x' ? ajar : -ajar;
    if (axis === 'x') {
      angled(b, 'wood', cx, (y0 + head) / 2, at + off * sgn, lw, head - y0 - 0.08, 0.06, 0, ang, 0,
        { color: dcol });
    } else {
      angled(b, 'wood', at + off * sgn, (y0 + head) / 2, cx, 0.06, head - y0 - 0.08, lw, 0, ang, 0,
        { color: dcol });
    }
  } else if (ped && roll < 0.78) {
    // Gone entirely — but the hinges stayed.
    for (const hy of [y0 + 0.35, head - 0.35]) {
      put(mid - span / 2 + 0.06, hy, 0.1, 0.12, thick * 0.4, {
        surface: 'metalPainted', tile: 0.25, color: shade(0x4a4038, 0.42), collide: false, shadow: 'none',
      });
    }
  }
}

/** Straight flight of steps built from solid blocks; returns the landing point. */
export function stairs(b, o) {
  const {
    x, y, z, dirX = 0, dirZ = 1, width = 2.0, steps = 12,
    rise = TUNING.stepRise, run = TUNING.stepRun, surface = 'concrete', color, cheek = true,
  } = o;
  for (let i = 0; i < steps; i++) {
    const along = i * run + run / 2;
    const cx = x + dirX * along;
    const cz = z + dirZ * along;
    const top = y + (i + 1) * rise;
    const h = top - (y - 0.4);
    const cy = (top + y - 0.4) / 2;
    if (dirX !== 0) b.solid(surface, cx, cy, cz, run, h, width, { color, shadow: 'both' });
    else b.solid(surface, cx, cy, cz, width, h, run, { color, shadow: 'both' });
  }
  const total = steps * run;
  if (cheek) {
    // Diagonal stringer walls hide the stair's stepped silhouette from the side.
    const len = Math.hypot(total, steps * rise) + 0.3;
    const ang = Math.atan2(steps * rise, total);
    for (const s of [-1, 1]) {
      const ox = dirZ !== 0 ? (s * (width / 2 + 0.09)) : 0;
      const oz = dirX !== 0 ? (s * (width / 2 + 0.09)) : 0;
      const cx = x + dirX * total / 2 + ox;
      const cz = z + dirZ * total / 2 + oz;
      const cy = y + (steps * rise) / 2 - 0.1;
      if (dirX !== 0) angled(b, surface, cx, cy, cz, len, 0.34, 0.18, 0, 0, dirX > 0 ? ang : -ang, { color });
      else angled(b, surface, cx, cy, cz, 0.18, 0.34, len, dirZ > 0 ? -ang : ang, 0, 0, { color });
    }
  }
  return new THREE.Vector3(x + dirX * total, y + steps * rise, z + dirZ * total);
}

/** Handrail: two pipes on posts, with one thin collider so you cannot walk through. */
export function railing(b, o) {
  const { x, y, z, len, axis = 'x', height = 1.02, color = shade(0x6a6258), solid: makeSolid = true } = o;
  const posts = Math.max(2, Math.round(len / 1.3));
  for (let i = 0; i <= posts; i++) {
    const t = -len / 2 + (len * i) / posts;
    const px = axis === 'x' ? x + t : x;
    const pz = axis === 'x' ? z : z + t;
    tube(b, 'metalPainted', px, y + height / 2, pz, 0.032, height, 0, 0, 0, { color, seg: 6 });
  }
  for (const hy of [height, height * 0.55]) {
    tube(b, 'metalPainted', x, y + hy, z, 0.032, len, axis === 'x' ? 0 : Math.PI / 2, 0, axis === 'x' ? Math.PI / 2 : 0, {
      color,
      seg: 6,
    });
  }
  if (makeSolid) {
    const hw = axis === 'x' ? len / 2 : 0.06;
    const hd = axis === 'x' ? 0.06 : len / 2;
    b.collider(x - hw, y, z - hd, x + hw, y + height, z + hd, 'metal');
  }
}

/* -------------------------------------------------------------------------- */
/*                             primary silhouette                             */
/* -------------------------------------------------------------------------- */

/**
 * ISO shipping container.
 *
 * Two things had it reading as a ribbed rectangle rather than a container. The rib
 * pitch was cladding pitch — a container side is corrugated at about 275 mm, four
 * times coarser than a wall sheet, and getting that wrong turns a 6 m box into
 * picket fencing. And it carried no *writing*: every real container is covered in
 * stencilled data, and at 3 m that lettering is most of what you are looking at.
 */
export function container(b, rng, x, z, o = {}) {
  const L = o.len ?? TUNING.containerL;
  const W = TUNING.containerW;
  const H = TUNING.containerH;
  const y = o.y ?? 0;
  const rotY = o.rotY ?? 0;
  const col = o.color ?? vary(rng, CONTAINER_COLOURS[Math.floor(rng() * CONTAINER_COLOURS.length)], 0.12, 0.03);
  const cy = y + H / 2;
  const rib = TEXEL.containerRib;
  b.solid('corrugated', x, cy, z, L, H, W, {
    rotY,
    tile: rib,
    color: col,
    cover: 'high',
    matId: 'metal',
    uOff: rng() * 4,
    vOff: rng() * 4,
  });
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const local = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const dark = col.clone().multiplyScalar(0.8);

  // Top and bottom rails, and the corner castings between them: the frame is what
  // makes the silhouette read as a box that can be picked up by a crane.
  for (const ry of [y + 0.09, y + H - 0.09]) {
    b.solid('metalPainted', x, ry, z, L + 0.02, 0.19, W + 0.02, {
      tile: 0.7, rotY, color: dark, collide: false, shadow: 'none',
    });
  }
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const [px, pz] = local(sx * (L / 2 - 0.1), sz * (W / 2 - 0.05));
      for (const ry of [y + 0.15, y + H - 0.15]) {
        b.solid('metalPainted', px, ry, pz, 0.3, 0.3, 0.26, {
          tile: 0.5, rotY, color: shade(0x585048, 0.5), collide: false, shadow: 'cast',
        });
      }
      // Corner post.
      b.solid('metalPainted', px, cy, pz, 0.14, H - 0.3, 0.14, {
        tile: 0.4, rotY, color: dark, collide: false, shadow: 'none',
      });
    }
  }

  // Door end: leaves, hinges, four locking bars with their cams and keepers.
  const [dx, dz] = local(L / 2 + 0.02, 0);
  b.solid('metalPainted', dx, cy, dz, 0.06, H - 0.24, W - 0.14, {
    tile: 1.0, rotY, color: col.clone().multiplyScalar(0.94), collide: false, shadow: 'cast',
  });
  for (const bar of [-0.66, -0.24, 0.24, 0.66]) {
    const [px, pz] = local(L / 2 + 0.06, bar);
    tube(b, 'metalPainted', px, cy, pz, 0.032, H - 0.36, 0, 0, 0, { color: dark, seg: 6 });
    for (const ry of [y + 0.42, y + H - 0.42]) {
      b.solid('metalPainted', px, ry, pz, 0.09, 0.1, 0.1, {
        tile: 0.2, rotY, color: shade(0x5a5248, 0.5), collide: false, shadow: 'none',
      });
    }
    const [hx, hz] = local(L / 2 + 0.04, bar);
    b.solid('metalPainted', hx, cy - 0.1, hz, 0.05, 0.16, 0.14, {
      tile: 0.2, rotY, color: shade(0x6a6054, 0.55), collide: false, shadow: 'none',
    });
  }
  // CSC plate by the door: a small pale rectangle, and one of the strongest
  // "this is a real container" cues there is.
  const [cpx, cpz] = local(L / 2 + 0.05, W / 2 - 0.45);
  b.solid('metalPainted', cpx, y + 1.35, cpz, 0.04, 0.2, 0.28, {
    tile: 0.2, rotY, color: shade(0xa8a294, 1.15), collide: false, shadow: 'none',
  });

  /* --- stencilling. Owner code high on the side, weight panel low, a number under
     the owner code. Built as glyph blocks rather than a texture, because there is no
     texture budget and a blocked-in code reads correctly from 3 m and mips to a
     smudge at 30 m, which is exactly what real lettering does. */
  const inkHex = 0xe8e2d2;
  const stencilSide = (sgn) => {
    const nlz = sgn * (W / 2 + 0.035);
    // The owner code gets real glyphs; the serial under it and the weight panel are
    // bar blocks, because below about 0.2 m cap height a glyph costs 45 quads to
    // render something the eye resolves as a line anyway.
    stencil(b, rng, local, rotY, nlz, -L / 2 + 0.55, y + H - 0.62, 0.26, inkHex, 4, sgn);
    for (let k = 0; k < 4; k++) {
      const [bx, bz] = local(-L / 2 + 0.62 + k * 0.3, nlz);
      b.solid('metalPainted', bx, y + H - 1.0, bz, 0.2, 0.13, 0.015, {
        tile: 0.12, rotY, color: shade(inkHex, 1.3, 0.25), collide: false, shadow: 'none',
      });
    }
    // MAX GROSS / TARE panel, low and at the door end.
    for (let r = 0; r < 3; r++) {
      const [bx, bz] = local(L / 2 - 1.5, nlz);
      b.solid('metalPainted', bx, y + 0.62 - r * 0.13, bz, 1.5 - r * 0.35, 0.05, 0.018, {
        tile: 0.3, rotY, color: shade(inkHex, 1.25, 0.25), collide: false, shadow: 'none',
      });
    }
  };
  stencilSide(1);
  stencilSide(-1);

  /* --- roof. The corrugation on the top face aliases into moiré from every roof and
     balcony on the map, and a container roof is not corrugated anyway: it is a plain
     sheet with a few cross members. Capping it kills the shimmer outright. */
  b.solid('metalPainted', x, y + H + 0.015, z, L - 0.06, 0.06, W - 0.06, {
    tile: 1.4, rotY, color: col.clone().multiplyScalar(0.88), collide: false, shadow: 'none',
    uOff: rng() * 3, vOff: rng() * 3,
  });
  for (let i = -2; i <= 2; i++) {
    const [px, pz] = local(i * (L / 6), 0);
    b.solid('metalPainted', px, y + H + 0.055, pz, 0.11, 0.04, W - 0.1, {
      tile: 0.6, rotY, color: dark, collide: false, shadow: 'none',
    });
  }
  // Rust running out from under the top rail, and a repair patch or two.
  const runs = 2 + Math.floor(rng() * 4);
  for (let i = 0; i < runs; i++) {
    const sgn = rng() < 0.5 ? 1 : -1;
    const lx = (rng() - 0.5) * (L - 0.8);
    const drop = 0.3 + rng() * 1.3;
    const [px, pz] = local(lx, sgn * (W / 2 + 0.03));
    b.solid('metalPainted', px, y + H - 0.2 - drop / 2, pz, 0.05 + rng() * 0.1, drop, 0.02, {
      tile: 0.4, rotY, color: shade(0x6e3b1e, 0.4, 0.95), collide: false, shadow: 'none',
    });
  }
  if (rng() < 0.45) {
    const sgn = rng() < 0.5 ? 1 : -1;
    const [px, pz] = local((rng() - 0.5) * (L - 1.6), sgn * (W / 2 + 0.045));
    b.solid('corrugated', px, y + 0.7 + rng() * 1.0, pz, 0.7 + rng() * 0.7, 0.6 + rng() * 0.6, 0.05, {
      tile: rib, rotY, color: vary(rng, CONTAINER_COLOURS[Math.floor(rng() * CONTAINER_COLOURS.length)], 0.1, 0.03),
      collide: false, shadow: 'cast',
    });
  }

  // Timber sleepers under it, if it is standing on dirt rather than stacked.
  if (o.sleepers) {
    for (const sx of [-1, 1]) {
      const [px, pz] = local(sx * (L / 2 - 0.7), 0);
      b.solid('wood', px, y + 0.06, pz, 0.5, 0.14, W + 0.3, {
        tile: 0.5, rotY, color: vary(rng, 0x6b5540, 0.16), collide: false, shadow: 'cast',
      });
    }
  }
  // Doors standing open, so the yard is not a field of sealed boxes.
  if (o.doorsOpen) {
    for (const sgn of [-1, 1]) {
      const swing = (o.doorsOpen === true ? 1.1 + rng() * 0.4 : o.doorsOpen) * sgn;
      const lw = W / 2 - 0.08;
      const hx = L / 2 + 0.04;
      const hz = sgn * (W / 2 - 0.05);
      const [px, pz] = local(hx + Math.sin(Math.abs(swing)) * lw / 2, hz - sgn * (1 - Math.cos(swing)) * lw / 2);
      angled(b, 'metalPainted', px, cy, pz, lw, H - 0.3, 0.07, 0, rotY + swing, 0,
        { tile: 0.8, color: col.clone().multiplyScalar(0.9) });
    }
  }
  return { top: y + H, color: col };
}

/**
 * A stencilled numeral/letter block run. Digits are 3x5 cell glyphs assembled from
 * small quads — one draw call's worth of geometry that survives merging, reads as
 * lettering at conversational range, and mips down to a grey smudge at distance
 * exactly the way paint on steel does.
 */
const GLYPHS = [
  0b111101101101111, 0b010110010010111, 0b111001111100111, 0b111001111001111,
  0b101101111001001, 0b111100111001111, 0b111100111101111, 0b111001010010010,
  0b111101111101111, 0b111101111001111,
];
function stencil(b, rng, local, rotY, outLocalZ, lx0, y, size, hex, count, facing) {
  const cw = size * 0.2;
  const ch = size * 0.2;
  const col = shade(hex, 1.35, 0.25);
  for (let g = 0; g < count; g++) {
    const bits = GLYPHS[Math.floor(rng() * 10)];
    const gx = lx0 + g * size * 0.78;
    for (let r = 0; r < 5; r++) {
      for (let cN = 0; cN < 3; cN++) {
        if (!(bits & (1 << (14 - (r * 3 + cN))))) continue;
        const [px, pz] = local(gx + cN * cw * 1.02 * facing, outLocalZ);
        b.solid('metalPainted', px, y - r * ch * 1.02 + size * 0.4, pz, cw, ch, 0.015, {
          tile: 0.12, rotY, color: col, collide: false, shadow: 'none',
        });
      }
    }
  }
}

/** Concrete T-wall / blast barrier — the vertebra of every modern combat zone. */
export function tWall(b, x, z, rotY = 0, o = {}) {
  const h = o.h ?? 3.2;
  const w = o.w ?? 1.6;
  const col = o.color;
  b.add('concrete', taperGeo(1.5, 0.62, 0.42, 0.9, 1.6), trs(x, 0, z, 0, rotY, 0), col, 'both');
  b.collider(x - 0.78, 0, z - 0.46, x + 0.78, 0.42, z + 0.46, 'concrete');
  b.solid('concrete', x, 0.42 + h / 2, z, w, h, 0.3, { rotY, color: col, cover: 'high' });
  b.solid('concrete', x, 0.42 + h + 0.07, z, w + 0.1, 0.14, 0.42, { rotY, color: col, collide: false, shadow: 'cast' });
}

/** Jersey barrier — crouch cover, and the thing that makes a road look controlled. */
export function jersey(b, x, z, rotY = 0, o = {}) {
  const col = o.color;
  const len = o.len ?? 2.2;
  b.add('concrete', taperGeo(0.62, 0.28, 0.86, len, 1.6), trs(x, 0, z, 0, rotY, 0), col, 'both');
  const c = Math.abs(Math.cos(rotY));
  const s = Math.abs(Math.sin(rotY));
  const hw = (0.62 * c + len * s) / 2;
  const hd = (0.62 * s + len * c) / 2;
  b.collider(x - hw, 0, z - hd, x + hw, 0.86, z + hd, 'concrete');
  b.markCover(x, z, hw, hd, 0.86, 'low');
}

/** HESCO bastion: wire-cage gabions filled with spoil. Reads instantly as fortified. */
export function hesco(b, rng, x, z, dirX, dirZ, cells = 4, o = {}) {
  const h = o.h ?? 1.1;
  const cell = 1.05;
  for (let i = 0; i < cells; i++) {
    const t = (i - (cells - 1) / 2) * cell;
    const px = x + dirX * t;
    const pz = z + dirZ * t;
    // Granular spoil, not a cast slab: the dirt sampler's dried-mud crack network
    // reads as a cracked pavement standing on end when it is wrapped round a cage.
    // The aggregate surface gives the right broken-fill read; matId keeps it soil
    // for footsteps and impacts.
    b.solid('concrete', px, h / 2, pz, dirX ? cell - 0.02 : 1.0, h, dirX ? 1.0 : cell - 0.02, {
      tile: 0.5,
      color: vary(rng, 0xa08a68, 0.09, 0.03, { sat: 0.7 }),
      cover: h > 1.3 ? 'high' : 'low',
      matId: 'dirt',
    });
    // Wire cage: horizontal bands so the fill reads as contained, not as a mud loaf.
    for (const by of [0.12, h / 2, h - 0.1]) {
      b.solid('metalPainted', px, by, pz, dirX ? cell : 1.06, 0.035, dirX ? 1.06 : cell, {
        tile: 0.5, color: shade(0x5c554a), collide: false, shadow: 'none',
      });
    }
    for (const s of [-1, 1]) {
      const cx = px + (dirX ? s * cell / 2 : 0);
      const cz = pz + (dirZ ? s * cell / 2 : 0);
      tube(b, 'metalPainted', cx, h / 2, cz, 0.028, h, 0, 0, 0, { color: shade(0x5c554a), seg: 5, shadow: 'none' });
    }
  }
}

/**
 * Sandbag emplacement. Bags are squashed spheres in a running bond.
 *
 * `o.y` is the floor it stands on. It had no such option, which is why five of these
 * were authored under a "roof kit" comment and every one of them was built on the
 * ground floor of the building underneath — cover for nobody, in a room nobody fights
 * in, while the roof it was meant to protect had none.
 */
export function sandbags(b, rng, x, z, dirX, dirZ, len, rows = 3, o = {}) {
  const y0 = o.y ?? 0;
  const bw = TUNING.sandbagW;
  const bh = TUNING.sandbagH;
  const n = Math.max(1, Math.round(len / bw));
  const geo = sphereGeo(0.5, 8, 5, TEXEL.fabric);
  for (let r = 0; r < rows; r++) {
    const offset = (r % 2) * bw * 0.5;
    const count = n + (r % 2 ? -1 : 0);
    for (let i = 0; i < count; i++) {
      const t = (i - (count - 1) / 2) * bw + offset * 0;
      const px = x + dirX * t + (rng() - 0.5) * 0.04;
      const pz = z + dirZ * t + (rng() - 0.5) * 0.04;
      const py = y0 + bh / 2 + r * bh * 0.92;
      const jitter = 0.9 + rng() * 0.2;
      b.add(
        'fabric',
        geo.clone(),
        trs(
          px, py, pz,
          0, Math.atan2(dirZ, dirX) + (rng() - 0.5) * 0.25, (rng() - 0.5) * 0.12,
          bw * 1.06 * jitter, bh * 1.18, 0.62
        ),
        vary(rng, 0x9d8763, 0.13, 0.04),
        'both'
      );
    }
  }
  geo.dispose();
  const h = rows * bh * 0.92;
  const hw = dirX ? len / 2 : 0.32;
  const hd = dirZ ? len / 2 : 0.32;
  b.collider(x - hw, y0, z - hd, x + hw, y0 + h, z + hd, 'sand');
  b.markCover(x, z, hw, hd, y0 + h, h > 1.25 ? 'high' : 'low', y0);
  if (o.posts) {
    for (const s of [-1, 1]) {
      tube(b, 'wood', x + dirX * s * len / 2, y0 + h + 0.35, z + dirZ * s * len / 2, 0.06, h + 0.7, 0, 0, 0, {
        color: shade(0x6b5540), seg: 6,
      });
    }
  }
}

/* -------------------------------------------------------------------------- */
/*                             secondary clutter                              */
/* -------------------------------------------------------------------------- */

export function drum(b, rng, x, z, o = {}) {
  const r = TUNING.drumR;
  const h = TUNING.drumH;
  const y = o.y ?? 0;
  const col = o.color ?? vary(rng, [0x7a4038, 0x3d5a66, 0x6a6a4a, 0x8a7a3a][Math.floor(rng() * 4)], 0.14, 0.03);
  const tipped = o.tipped ?? false;
  if (tipped) {
    const rotY = o.rotY ?? rng() * Math.PI;
    b.add('metalPainted', cylGeo(r, r, h, 14, 0.8), trs(x, y + r, z, 0, rotY, Math.PI / 2), col, 'both');
    b.collider(x - h / 2, y, z - h / 2, x + h / 2, y + r * 2, z + h / 2, 'metal');
    b.markCover(x, z, h / 2, h / 2, y + r * 2, 'low', y);
    return;
  }
  b.post('metalPainted', x, y + h / 2, z, r, h, { seg: 14, tile: 0.8, color: col, cover: y > 0.4 ? 'high' : 'low' });
  for (const ry of [0.24, 0.64]) {
    b.add('metalPainted', cylGeo(r + 0.025, r + 0.025, 0.06, 14, 0.5, true), trs(x, y + ry, z, 0, 0, 0),
      col.clone().multiplyScalar(0.82), 'cast');
  }
  b.add('metalPainted', cylGeo(r * 0.86, r * 0.86, 0.04, 12, 0.5), trs(x, y + h + 0.02, z, 0, 0, 0),
    col.clone().multiplyScalar(1.06), 'none');
}

export function crate(b, rng, x, y, z, w, h, d, o = {}) {
  const rotY = o.rotY ?? 0;
  // Packing-case pine, weathered. The chroma comes down because the forge's wood
  // albedo is already a warm orange-brown, and a 0.82-saturation orange tint on top of
  // it is what turned every crate, pallet and bench on the map into the same shade of
  // rust — the loudest colour in a frame that is meant to be dust.
  const col = o.color ?? vary(rng, 0x8a7458, 0.13, 0.02, { sat: 0.66 });
  b.solid('wood', x, y + h / 2, z, w, h, d, { rotY, color: col, cover: o.cover, matId: 'wood' });
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const dark = col.clone().multiplyScalar(0.78);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const lx = sx * (w / 2 - 0.045);
      const lz = sz * (d / 2 - 0.045);
      b.solid('wood', x + lx * c + lz * s, y + h / 2, z - lx * s + lz * c, 0.09, h + 0.01, 0.09, {
        tile: 0.5, rotY, color: dark, collide: false, shadow: 'cast',
      });
    }
  }
  // Mid strap on the long faces.
  for (const sz of [-1, 1]) {
    const lz = sz * (d / 2 + 0.012);
    b.solid('wood', x + lz * s, y + h * 0.62, z + lz * c, w - 0.02, 0.1, 0.03, {
      tile: 0.5, rotY, color: dark, collide: false, shadow: 'none',
    });
  }
}

export function pallet(b, rng, x, y, z, o = {}) {
  const rotY = o.rotY ?? 0;
  const col = o.color ?? vary(rng, 0x9a7c52, 0.12);
  const W = 1.2;
  const D = 0.82;
  for (const sx of [-1, 0, 1]) {
    b.solid('wood', x + sx * 0.45 * Math.cos(rotY), y + 0.045, z - sx * 0.45 * Math.sin(rotY), 0.11, 0.09, D, {
      tile: 0.5, rotY, color: col.clone().multiplyScalar(0.85), collide: false, shadow: 'cast',
    });
  }
  for (let i = 0; i < 5; i++) {
    const t = -D / 2 + 0.06 + (i * (D - 0.12)) / 4;
    b.solid('wood', x + t * Math.sin(rotY), y + 0.115, z + t * Math.cos(rotY), W, 0.045, 0.13, {
      tile: 0.5, rotY, color: vary(rng, col.getHex(), 0.08), collide: false, shadow: 'cast',
    });
  }
  b.collider(x - 0.62, y, z - 0.45, x + 0.62, y + TUNING.palletH, z + 0.45, 'wood');
}

export function palletStack(b, rng, x, z, count, o = {}) {
  const rotY = o.rotY ?? 0;
  for (let i = 0; i < count; i++) {
    pallet(b, rng, x + (rng() - 0.5) * 0.06, i * 0.155, z + (rng() - 0.5) * 0.06, { rotY: rotY + (rng() - 0.5) * 0.06 });
  }
  const top = count * 0.155;
  if (top > 0.6) b.markCover(x, z, 0.62, 0.45, top, top > 1.25 ? 'high' : 'low');
  return top;
}

export function tireStack(b, rng, x, z, n = 4, o = {}) {
  const col = shade(0x1b1a19);
  for (let i = 0; i < n; i++) {
    b.add('polymer', cylGeo(0.36, 0.36, 0.2, 14, 0.6), trs(x + (rng() - 0.5) * 0.1, 0.1 + i * 0.2, z + (rng() - 0.5) * 0.1, 0, rng() * 3, 0),
      col.clone().multiplyScalar(0.85 + rng() * 0.3), 'both');
  }
  b.collider(x - 0.38, 0, z - 0.38, x + 0.38, n * 0.2, z + 0.38, 'wood');
  if (n * 0.2 > 0.6) b.markCover(x, z, 0.38, 0.38, n * 0.2, 'low');
}

export function gasBottles(b, rng, x, z, n = 3, o = {}) {
  const rotY = o.rotY ?? 0;
  for (let i = 0; i < n; i++) {
    const t = (i - (n - 1) / 2) * 0.36;
    const px = x + t * Math.cos(rotY);
    const pz = z - t * Math.sin(rotY);
    const col = vary(rng, [0xb08030, 0x30506a, 0x8a3830][i % 3], 0.1);
    b.post('metalPainted', px, 0.62, pz, 0.16, 1.24, { seg: 10, tile: 0.6, color: col, collide: i === 0 });
    b.post('metalPainted', px, 1.3, pz, 0.06, 0.16, { seg: 8, tile: 0.4, color: col.clone().multiplyScalar(0.7), collide: false });
  }
  b.collider(x - 0.6, 0, z - 0.25, x + 0.6, 1.3, z + 0.25, 'metal');
}

/* -------------------------------------------------------------------------- */
/*                                 vehicles                                   */
/* -------------------------------------------------------------------------- */

function wheels(b, x, y, z, rotY, positions, r = 0.35, w = 0.26) {
  const col = shade(0x141312);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  for (const [lx, lz] of positions) {
    const px = x + lx * c + lz * s;
    const pz = z - lx * s + lz * c;
    b.add('polymer', cylGeo(r, r, w, 12, 0.7), trs(px, y + r, pz, 0, rotY, Math.PI / 2), col, 'both');
    b.add('metalPainted', cylGeo(r * 0.5, r * 0.5, w + 0.02, 10, 0.5), trs(px, y + r, pz, 0, rotY, Math.PI / 2),
      shade(0x54504a), 'cast');
  }
}

/** Burnt-out saloon car. Charcoal body, no glass, sitting on its rims. */
export function carWreck(b, rng, x, z, rotY = 0, o = {}) {
  const col = o.color ?? vary(rng, 0x2a2724, 0.22);
  const y = 0;
  const L = 4.3;
  const W = 1.82;
  b.solid('metalPainted', x, y + 0.62, z, L, 0.62, W, { tile: 0.6, rotY, color: col, cover: 'low', matId: 'metal' });
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const [bx, bz] = at(-0.25, 0);
  b.solid('metalPainted', bx, y + 1.22, bz, 2.05, 0.6, W - 0.16, { tile: 0.6, rotY, color: col.clone().multiplyScalar(0.85), cover: 'low', matId: 'metal' });
  // Bonnet and boot lips, plus a sagged bumper — burnt cars never sit square.
  const [nx, nz] = at(L / 2 - 0.15, 0);
  b.solid('metalPainted', nx, y + 0.9, nz, 0.4, 0.16, W - 0.1, { tile: 0.8, rotY, color: col, collide: false, shadow: 'cast' });
  const [tx, tz] = at(-L / 2 + 0.1, 0);
  b.solid('metalPainted', tx, y + 0.88, tz, 0.35, 0.14, W - 0.2, { tile: 0.8, rotY, color: col, collide: false, shadow: 'cast' });
  const [px2, pz2] = at(L / 2 + 0.08, 0.25);
  angled(b, 'metalPainted', px2, y + 0.5, pz2, 0.5, 0.2, 1.0, 0, rotY, 0.4, { tile: 0.7, color: col.clone().multiplyScalar(0.7) });
  if (o.flat) {
    wheels(b, x, y - 0.08, z, rotY, [[1.32, 0.78], [1.32, -0.78], [-1.3, 0.78], [-1.3, -0.78]], 0.3, 0.24);
  } else {
    wheels(b, x, y, z, rotY, [[1.32, 0.78], [1.32, -0.78], [-1.3, 0.78], [-1.3, -0.78]], 0.34, 0.24);
  }
  // Roof rack / debris on top adds silhouette noise.
  if (rng() < 0.5) {
    const [rx, rz] = at(-0.25, 0);
    b.solid('metalPainted', rx, y + 1.57, rz, 1.4, 0.06, 1.1, { tile: 0.6, rotY, color: shade(0x4a453e), collide: false, shadow: 'cast' });
  }
}

/** Flatbed / cargo truck, optionally overturned. Big enough to be a lane blocker. */
export function truckWreck(b, rng, x, z, rotY = 0, o = {}) {
  const col = o.color ?? vary(rng, o.hex ?? 0x5a5f4a, 0.16);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  // Chassis
  b.solid('metalPainted', x, 0.72, z, 6.6, 0.28, 2.1, { tile: 0.6, rotY, color: col.clone().multiplyScalar(0.6), cover: 'low', matId: 'metal' });
  // Cab
  const [cx, cz] = at(2.35, 0);
  b.solid('metalPainted', cx, 1.5, cz, 1.9, 1.32, 2.2, { tile: 0.65, rotY, color: col, cover: 'high', matId: 'metal' });
  const [wx, wz] = at(1.5, 0);
  b.solid('polymer', wx, 1.85, wz, 0.12, 0.62, 1.9, { tile: 0.7, rotY, color: shade(0x5a6a74, 0.8, 0.6), collide: false, shadow: 'none' });
  // Cargo bed with slat sides
  const [gx, gz] = at(-1.2, 0);
  b.solid('wood', gx, 0.94, gz, 4.2, 0.16, 2.2, { rotY, color: vary(rng, 0x7d6244, 0.1), cover: 'low', matId: 'wood' });
  for (const sz of [-1, 1]) {
    const [sx2, sz2] = at(-1.2, sz * 1.06);
    b.solid('wood', sx2, 1.42, sz2, 4.2, 0.86, 0.1, { rotY, color: vary(rng, 0x7d6244, 0.1), cover: 'high', matId: 'wood' });
  }
  const [ex, ez] = at(-3.3, 0);
  b.solid('wood', ex, 1.42, ez, 0.1, 0.86, 2.2, { rotY, color: vary(rng, 0x7d6244, 0.1), collide: false, shadow: 'cast' });
  const [px, pz] = at(1.4, 0.95);
  tube(b, 'metalPainted', px, 1.9, pz, 0.07, 1.6, 0, 0, 0, { color: shade(0x3a352e), seg: 8 });
  wheels(b, x, 0, z, rotY, [[2.3, 0.98], [2.3, -0.98], [-1.5, 1.0], [-1.5, -1.0], [-2.4, 1.0], [-2.4, -1.0]], 0.48, 0.3);
  const [thw, thd] = rotExtent(6.6, 2.2, rotY);
  b.markCover(x, z, thw, thd, 1.9, 'high');
}

/** Gutted city bus — the single best lane-breaker there is. */
export function busWreck(b, rng, x, z, rotY = 0, o = {}) {
  const col = o.color ?? vary(rng, 0x8a8266, 0.12);
  const L = 10.6;
  const W = 2.55;
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];

  /* --- lower body, built with the wheel arches missing.
     The wheels used to sit 7 cm inside the skin — geometrically impossible to see,
     so the bus stood on an invisible undercarriage and read as a shed. Without CSG
     the fix is to author the skirt as segments and simply leave the arches out; the
     wheel then shows through a real hole in the bodywork the way it does on a bus. */
  const arches = [[2.98, 4.22], [-4.25, -1.85]];
  const skirt = [];
  let cursor = -L / 2;
  for (const [a0, a1] of arches.slice().sort((p, q) => p[0] - q[0])) {
    if (a0 > cursor) skirt.push([cursor, a0]);
    cursor = Math.max(cursor, a1);
  }
  skirt.push([cursor, L / 2]);
  for (const [a0, a1] of skirt) {
    const [px, pz] = at((a0 + a1) / 2, 0);
    b.solid('metalPainted', px, 0.76, pz, a1 - a0, 0.62, W, {
      tile: 0.7, rotY, color: col.clone().multiplyScalar(0.94), collide: false, shadow: 'cast',
    });
  }
  // Inner arch panel: dark, inset, so an arch is a recess rather than a window
  // straight through the vehicle.
  for (const [a0, a1] of arches) {
    const [px, pz] = at((a0 + a1) / 2, 0);
    b.solid('metalPainted', px, 0.76, pz, a1 - a0, 0.62, W - 0.62, {
      tile: 0.5, rotY, color: shade(0x2a2724, 0.3), collide: false, shadow: 'none',
    });
  }
  // Main body above the arch line, and the chassis rail the wheels hang off.
  b.solid('metalPainted', x, 1.26, z, L, 0.42, W, { tile: 0.7, rotY, color: col, cover: 'high', matId: 'metal' });
  b.solid('metalPainted', x, 0.56, z, L - 0.6, 0.16, W - 0.9, {
    tile: 0.5, rotY, color: shade(0x3a352e, 0.35), collide: false, shadow: 'none',
  });
  // Painted skirt band under the glazing: the strongest single "this is a bus" read,
  // and one of the few places on this map allowed a fully saturated colour.
  b.solid('metalPainted', x, 1.30, z, L + 0.02, 0.28, W + 0.03, {
    tile: 0.5, rotY, color: o.band ?? accent(rng() < 0.5 ? 0x2f6a8c : 0xb04a2a, { lum: 0.86 }), collide: false, shadow: 'none',
  });
  // No env map exists, so smooth glass renders black and a window band turns into a
  // hole in a wall. The rough dielectric picks up ambient and reads as dusty glazing.
  b.solid('polymer', x, 1.90, z, L - 0.5, 0.74, W + 0.02, { tile: 0.9, rotY, color: shade(0x5a6a74, 0.85, 0.6), collide: false, shadow: 'none' });
  b.solid('metalPainted', x, 1.50, z, L - 0.4, 0.1, W + 0.05, { tile: 0.5, rotY, color: col.clone().multiplyScalar(1.15), collide: false, shadow: 'none' });
  b.solid('metalPainted', x, 2.29, z, L - 0.4, 0.1, W + 0.05, { tile: 0.5, rotY, color: col.clone().multiplyScalar(1.15), collide: false, shadow: 'none' });
  for (let i = -4; i <= 4; i++) {
    const [mx, mz] = at(i * 1.15, 0);
    b.solid('metalPainted', mx, 1.90, mz, 0.11, 0.76, W + 0.06, { tile: 0.4, rotY, color: col.clone().multiplyScalar(1.18), collide: false, shadow: 'none' });
  }
  b.solid('metalPainted', x, 2.42, z, L, 0.34, W, { tile: 0.8, rotY, color: col.clone().multiplyScalar(0.92), collide: false, shadow: 'cast' });
  // Ends: a raked cap, a screen and burst headlamps, so it has a front and a back.
  for (const end of [-1, 1]) {
    const [fx, fz] = at(end * (L / 2 + 0.09), 0);
    b.solid('metalPainted', fx, 1.62, fz, 0.24, 2.1, W - 0.14, { tile: 0.5, rotY, color: col.clone().multiplyScalar(0.96), collide: false, shadow: 'cast' });
    b.solid('polymer', fx - end * 0.07, 2.02, fz, 0.12, 0.5, W - 0.55, { tile: 0.6, rotY, color: shade(0x5a6a74, 0.8, 0.6), collide: false, shadow: 'none' });
    for (const sgn of [-1, 1]) {
      const [hx, hz] = at(end * (L / 2 + 0.11), sgn * (W / 2 - 0.38));
      b.solid('polymer', hx, 0.88, hz, 0.1, 0.24, 0.3, { tile: 0.4, rotY, color: shade(0xb9b6ac, 1.05), collide: false, shadow: 'none' });
    }
  }
  b.solid('metalPainted', x, 2.62, z, L - 0.6, 0.08, W - 0.35, { tile: 1.0, rotY, color: shade(0x54504a), collide: false, shadow: 'cast' });
  // Roof hatches / AC hump — silhouette interest from the elevated positions.
  for (const lx of [-2.6, 1.8]) {
    const [hx, hz] = at(lx, 0);
    b.solid('metalPainted', hx, 2.74, hz, 1.1, 0.18, 1.0, { tile: 0.6, rotY, color: shade(0x6a6458), collide: false, shadow: 'cast' });
  }
  /* --- the things that say "bus" rather than "large vehicle" ---------------- */
  // Destination blind above the windscreen, its lamp long dead.
  const front = o.front ?? 1;
  const [blx, blz] = at(front * (L / 2 - 0.06), 0);
  b.solid('metalPainted', blx, 2.30, blz, 0.16, 0.34, W - 0.5, {
    tile: 0.4, rotY, color: shade(0x2e2b26, 0.28), collide: false, shadow: 'none',
  });
  for (let k = 0; k < 5; k++) {
    const [tx2, tz2] = at(front * (L / 2 - 0.02), (k - 2) * 0.32);
    b.solid('metalPainted', tx2, 2.30, tz2, 0.08, 0.17, 0.2, {
      tile: 0.12, rotY, color: shade(0xd8d2c2, 1.15, 0.2), collide: false, shadow: 'none',
    });
  }
  // Passenger doors: a recessed pair of leaves with a centre rebate, near side only.
  for (const lx of [front * 3.1, front * -0.6]) {
    const [dx2, dz2] = at(lx, W / 2 - 0.06);
    b.solid('polymer', dx2, 1.44, dz2, 1.15, 1.96, 0.1, {
      tile: 0.5, rotY, color: shade(0x3a3a38, 0.34), collide: false, shadow: 'none',
    });
    const [rx2, rz2] = at(lx, W / 2 + 0.01);
    b.solid('metalPainted', rx2, 1.44, rz2, 0.06, 1.96, 0.06, {
      tile: 0.2, rotY, color: col.clone().multiplyScalar(1.2), collide: false, shadow: 'none',
    });
    for (const sgn of [-1, 1]) {
      const [jx, jz] = at(lx + sgn * 0.6, W / 2 + 0.01);
      b.solid('metalPainted', jx, 1.44, jz, 0.07, 2.0, 0.07, {
        tile: 0.2, rotY, color: col.clone().multiplyScalar(1.2), collide: false, shadow: 'none',
      });
    }
  }
  // Bumpers, and a pair of mirror arms on the front corners.
  for (const end of [-1, 1]) {
    const [bx2, bz2] = at(end * (L / 2 + 0.13), 0);
    b.solid('polymer', bx2, 0.72, bz2, 0.2, 0.28, W - 0.06, {
      tile: 0.4, rotY, color: shade(0x35322c, 0.34), collide: false, shadow: 'cast',
    });
  }
  for (const sgn of [-1, 1]) {
    const [ax2, az2] = at(front * (L / 2 - 0.1), sgn * (W / 2 - 0.05));
    tube(b, 'metalPainted', ax2, 2.42, az2, 0.028, 0.5, 0, 0, 0, { color: shade(0x4a453e, 0.4), seg: 5 });
    const [mx2, mz2] = at(front * (L / 2 - 0.1), sgn * (W / 2 + 0.22));
    tube(b, 'metalPainted', mx2, 2.6, mz2, 0.026, 0.55, 0, 0, Math.PI / 2, { color: shade(0x4a453e, 0.4), seg: 5 });
    b.solid('polymer', mx2, 2.42, mz2, 0.1, 0.34, 0.16, {
      tile: 0.2, rotY, color: shade(0x2e2b26, 0.3), collide: false, shadow: 'none',
    });
  }

  wheels(b, x, 0, z, rotY, [[3.6, 1.06], [3.6, -1.06], [-2.6, 1.06], [-2.6, -1.06], [-3.5, 1.06], [-3.5, -1.06]], 0.52, 0.3);
  b.collider(
    x - Math.abs(L / 2 * c) - Math.abs(W / 2 * s), 0, z - Math.abs(L / 2 * s) - Math.abs(W / 2 * c),
    x + Math.abs(L / 2 * c) + Math.abs(W / 2 * s), 2.55, z + Math.abs(L / 2 * s) + Math.abs(W / 2 * c),
    'metal'
  );
}

/** Small counterbalance forklift — warehouse scale reference. */
export function forklift(b, rng, x, z, rotY = 0) {
  const col = vary(rng, 0xb07a20, 0.1);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  b.solid('metalPainted', x, 0.62, z, 2.0, 0.7, 1.1, { tile: 1.0, rotY, color: col, cover: 'low', matId: 'metal' });
  const [sx, sz] = at(-0.35, 0);
  b.solid('polymer', sx, 1.12, sz, 0.5, 0.34, 0.6, { tile: 0.5, rotY, color: shade(0x24211d), collide: false, shadow: 'cast' });
  for (const sgn of [-1, 1]) {
    const [mx, mz] = at(1.05, sgn * 0.38);
    tube(b, 'metalPainted', mx, 1.35, mz, 0.06, 2.6, 0, 0, 0, { color: shade(0x53504a) });
    const [rx, rz] = at(-0.9, sgn * 0.5);
    tube(b, 'metalPainted', rx, 1.35, rz, 0.045, 1.5, 0, 0, 0, { color: col });
    const [fx, fz] = at(1.5, sgn * 0.3);
    b.solid('metalPainted', fx, 0.09, fz, 0.9, 0.06, 0.13, { tile: 0.4, rotY, color: shade(0x4a4640), collide: false, shadow: 'cast' });
  }
  const [tx, tz] = at(-0.6, 0);
  b.solid('metalPainted', tx, 2.16, tz, 1.1, 0.06, 1.1, { tile: 0.6, rotY, color: col.clone().multiplyScalar(0.8), collide: false, shadow: 'cast' });
  wheels(b, x, 0, z, rotY, [[0.75, 0.5], [0.75, -0.5], [-0.7, 0.42], [-0.7, -0.42]], 0.28, 0.2);
}

/* -------------------------------------------------------------------------- */
/*                             tertiary detail                                */
/* -------------------------------------------------------------------------- */

/**
 * Cloth the sun gets through.
 *
 * Every awning, tarpaulin and banner on the map shares one material override. Thin
 * fabric at golden hour is not an opaque surface: a good third of what you see when
 * you stand under a market awning is the sun *behind* it, and a shaded cloth with no
 * transmission renders as a flat dark card whatever its geometry does. A small warm
 * emissive is the honest cheap stand-in — it lifts the shadowed side of the cloth and
 * nothing else. `DoubleSide` is the other half of it: a sagging sheet is one layer of
 * cloth, not a box, so it has to be lit from underneath as well.
 *
 * It lives as one shared constant because the batcher keys on the override object —
 * two awnings that disagree by 0.01 on intensity are two extra draw calls.
 */
export const CLOTH_MAT = { side: THREE.DoubleSide, emissive: 0x8a4a20, emissiveIntensity: 0.33 };

/**
 * Fabric awning: a sheet under tension between a wall batten and a front rail on two
 * poles.
 *
 * The old one was a single flat box, and it was the loudest remaining prop defect on
 * the map — a constant-value rectangle in the middle of the best-composed view. Cloth
 * has none of those properties. It sags into a catenary between its supports, it
 * ripples down the fall line, it pulls into a point at every fixing, it is brighter
 * where it is taut and darker in the valleys, the sun comes through it, and its leading
 * edge is a row of separate tabs that have frayed at different rates.
 *
 * All of that is in the geometry rather than in a texture, because at 3 m the thing the
 * eye reads is the *silhouette of the sag* and the value break across it, and neither
 * of those can be painted onto a plane.
 */
export function awning(b, rng, x, y, z, w, depth, rotY = 0, o = {}) {
  const hex = o.hex ?? CLOTH_COLOURS[Math.floor(rng() * CLOTH_COLOURS.length)];
  // Dyed cotton that has been in this sun for years: the hue survives, the chroma
  // does not, and the jute the forge draws underneath is already warm.
  const col = o.color ?? vary(rng, hex, 0.14, 0.04, { sat: 0.74 });
  const drop = o.drop ?? (0.3 + rng() * 0.36);
  // Belly scales with the span: a 4 m awning between two poles sags a lot more than a
  // 2 m one, and holding one number for both is what makes a row of them read as a kit.
  const sag = (o.sag ?? (0.09 + rng() * 0.14)) * Math.min(1.9, w / 2.2);
  const folds = 2 + Math.floor(rng() * 3);
  const phase = rng() * Math.PI * 2;
  // One pole has settled, or the rope that end is tied with has stretched.
  const skew = (rng() - 0.5) * 0.42;
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];

  // The taut plane the cloth would occupy if it were the card it used to be.
  const plane = (u, v) => -drop * v + skew * u * v;
  const fn = (u, v) => {
    const bay = Math.cos(u * Math.PI);              // 1 mid-span, 0 at the poles
    const belly = -sag * bay * (0.22 + 0.78 * v);
    const ripple = Math.sin((u + 0.5) * Math.PI * folds + phase) * sag * 0.3 * bay * v * v;
    return plane(u, v) + belly + ripple;
  };
  // Value across the sheet: the crest of every fold catches the low sun and the valley
  // between two folds does not. Baked per-vertex, so it costs no draw call at all.
  const shadeFn = (u, v, yy) => {
    const dip = plane(u, v) - yy;
    return Math.max(0.42, Math.min(1.3, (0.78 + 0.34 * v) * (1 - Math.min(0.5, dip * 1.25))));
  };

  const nu = Math.max(6, Math.min(18, Math.round(w / 0.2)));
  b.add('fabric', sheetGeo(w, depth, nu, 5, fn, { tile: TEXEL.canvas, color: col, shade: shadeFn }),
    trs(x, y, z, 0, rotY, 0), null, 'both', true, CLOTH_MAT);

  // Wall batten the head of the cloth is nailed to, and the front rail it hangs off.
  const [bx0, bz0] = at(0, 0.02);
  b.solid('wood', bx0, y + 0.03, bz0, w + 0.1, 0.09, 0.07, {
    rotY, color: vary(rng, 0x7a6a52, 0.12), collide: false, shadow: 'cast',
  });
  const railY = y + fn(0, 1) + sag * 0.25;
  const [rx0, rz0] = at(0, depth);
  tube(b, 'metalPainted', rx0, railY, rz0, 0.022, w + 0.12, 0, rotY, Math.PI / 2,
    { color: shade(0x5a544a, 0.5), seg: 5, shadow: 'none' });

  // Frayed valance: separate tabs following the sagging front edge, unequal, and one
  // or two torn off. A single scalloped strip is a flat card with a wavy bottom.
  const tabs = Math.max(4, Math.round(w / 0.24));
  for (let i = 0; i < tabs; i++) {
    if (rng() < 0.1) continue;
    const u = -0.5 + (i + 0.5) / tabs;
    const ty = y + fn(u, 1);
    const th = 0.11 + rng() * 0.19;
    const [px, pz] = at(u * w, depth - 0.008);
    angled(b, 'fabric', px, ty - th / 2, pz, (w / tabs) * (0.82 + rng() * 0.16), th, 0.012,
      0, rotY + (rng() - 0.5) * 0.05, (rng() - 0.5) * 0.3, {
        tile: TEXEL.canvas, color: col.clone().multiplyScalar(0.82 + rng() * 0.24),
        shadow: 'both', mat: CLOTH_MAT,
      });
  }

  // A sewn-on repair in a mismatched cloth, riding just clear of the sheet.
  if (rng() < 0.55) {
    const pu = (rng() - 0.5) * 0.7;
    const pv = 0.25 + rng() * 0.5;
    const [px, pz] = at(pu * w, pv * depth);
    angled(b, 'fabric', px, y + fn(pu, pv) + 0.012, pz, 0.24 + rng() * 0.3, 0.01, 0.22 + rng() * 0.26,
      -Math.atan2(drop, depth), rotY, 0, {
        tile: TEXEL.canvas, color: vary(rng, CLOTH_COLOURS[Math.floor(rng() * CLOTH_COLOURS.length)], 0.12, 0.04, { sat: 0.8 }),
        shadow: 'both', mat: CLOTH_MAT,
      });
  }

  // Poles, stays, and the cord that ties the outer corner back to the wall.
  const ang = Math.atan2(drop, depth);
  for (const sgn of [-1, 1]) {
    const u = sgn * 0.5;
    const footY = y + fn(u, 1) - 0.02;
    const [px, pz] = at(sgn * (w / 2 - 0.06), depth);
    tube(b, 'metalPainted', px, footY / 2, pz, 0.032, footY, 0, 0, 0, { color: shade(0x5a544a), seg: 6 });
    b.collider(px - 0.09, 0, pz - 0.09, px + 0.09, footY, pz + 0.09, 'metal');
    const [ax, az] = at(sgn * (w / 2 - 0.06), 0.05);
    tube(b, 'metalPainted', (px + ax) / 2, (footY + y) / 2 + 0.03, (pz + az) / 2, 0.02, Math.hypot(depth, drop),
      Math.PI / 2 - ang, rotY, 0, { color: shade(0x5a544a), seg: 5, shadow: 'none' });
    const [tx2, tz2] = at(sgn * (w / 2 + 0.32), 0.06);
    cable(b, new THREE.Vector3(px, footY + 0.02, pz), new THREE.Vector3(tx2, y + 0.28, tz2),
      0.06, { r: 0.011, color: shade(0x8a8070, 0.7) });
  }
  // A centre pole once the span is long enough that two would not hold it.
  if (w > 3.4) {
    const [px, pz] = at(0, depth - 0.03);
    tube(b, 'metalPainted', px, (y + fn(0, 1)) / 2, pz, 0.028, y + fn(0, 1), 0, 0, 0,
      { color: shade(0x5a544a), seg: 6, shadow: 'cast' });
  }
}

/* Produce is one of the two or three things on a back-lot allowed to be saturated —
   a crate of tomatoes is genuinely that colour, and it is the only reason the market
   reads as a market rather than a row of grey tables. */
const PRODUCE = [0xc4471f, 0xd8a01c, 0x6f8a24, 0x8a3a4a, 0xb86a18, 0x4a6a2a];
const SACK_HEX = [0xa89272, 0xb3a486, 0x9c8a68, 0xc0b294];

/**
 * A pile of wares on a stall top.
 *
 * The goods used to be a crate or a squashed sphere, which is two silhouettes for the
 * whole market. Silhouette is the entire job here: at 15 m nobody resolves a tomato,
 * but everybody can tell the difference between a *slump*, a *stack*, a *row of necks*
 * and a *heap*, and it is that alternation down a row of stalls that reads as trade.
 */
export function goodsPile(b, rng, x, y, z, rotY = 0, size = 0.6) {
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const roll = rng();

  if (roll < 0.2) {
    // Slumped sacks: the one shape a box cannot fake. Two lobes, the upper one
    // sliding off the lower, and a gathered neck tied with cord.
    const n = 1 + Math.floor(rng() * 3);
    for (let i = 0; i < n; i++) {
      const [px, pz] = at((rng() - 0.5) * size * 0.7, (rng() - 0.5) * size * 0.5);
      const h = size * (0.42 + rng() * 0.3);
      const col = vary(rng, SACK_HEX[Math.floor(rng() * SACK_HEX.length)], 0.12);
      const lean = (rng() - 0.5) * 0.5;
      b.add('fabric', sphereGeo(0.5, 8, 6, TEXEL.fabric),
        trs(px, y + h * 0.4, pz, 0, rng() * 3, lean, size * 0.78, h * 0.9, size * 0.62), col, 'both');
      b.add('fabric', sphereGeo(0.5, 7, 5, TEXEL.fabric),
        trs(px + Math.sin(lean) * 0.05, y + h * 0.86, pz, 0, rng() * 3, lean * 1.6, size * 0.5, h * 0.55, size * 0.4),
        col.clone().multiplyScalar(1.06), 'both');
      // Gathered neck.
      b.add('fabric', cylGeo(0.03, 0.06, size * 0.2, 6, TEXEL.fabric),
        trs(px + Math.sin(lean) * 0.1, y + h * 1.1, pz, 0, 0, lean * 2), col.clone().multiplyScalar(0.88), 'cast');
    }
    return;
  }
  if (roll < 0.38) {
    // Crates stacked out of square, with the top one's lid propped open.
    const n = 2 + Math.floor(rng() * 2);
    let cy = y;
    for (let i = 0; i < n; i++) {
      const cw = size * (0.9 - i * 0.1);
      const ch = size * (0.32 + rng() * 0.12);
      const [px, pz] = at((rng() - 0.5) * 0.12 * (i + 1), (rng() - 0.5) * 0.1 * (i + 1));
      crate(b, rng, px, cy, pz, cw, ch, cw * 0.82, {
        rotY: rotY + (rng() - 0.5) * 0.7, color: vary(rng, 0x9a7a4c, 0.14),
      });
      cy += ch;
    }
    const [lx, lz] = at(size * 0.4, 0);
    angled(b, 'wood', lx, cy + size * 0.16, lz, size * 0.8, 0.03, size * 0.6,
      0, rotY + 0.2, -0.9, { color: vary(rng, 0x9a7c52, 0.12) });
    return;
  }
  if (roll < 0.54) {
    // Bolts of cloth, rolled and stacked into a pyramid — long horizontal accents in
    // a stall otherwise made of lumps.
    const len = size * (1.0 + rng() * 0.5);
    const r = size * 0.13;
    const rows = 2;
    for (let row = 0; row < rows; row++) {
      const n = 3 - row;
      for (let i = 0; i < n; i++) {
        const t = (i - (n - 1) / 2) * r * 2.1;
        const [px, pz] = at(0, t);
        b.add('fabric', cylGeo(r, r, len, 9, TEXEL.canvas),
          trs(px, y + r + row * r * 1.8, pz, 0, rotY, Math.PI / 2),
          vary(rng, CLOTH_COLOURS[Math.floor(rng() * CLOTH_COLOURS.length)], 0.14, 0.05, { sat: 0.95 }), 'both');
      }
    }
    // A length pulled off the top and left hanging over the front edge.
    const [hx, hz] = at(0, size * 0.5);
    angled(b, 'fabric', hx, y - size * 0.2, hz, len * 0.7, size * 0.7, 0.02,
      0, rotY, (rng() - 0.5) * 0.2, {
        tile: TEXEL.canvas, color: vary(rng, CLOTH_COLOURS[Math.floor(rng() * CLOTH_COLOURS.length)], 0.14, 0.05),
        shadow: 'both', mat: CLOTH_MAT,
      });
    return;
  }
  if (roll < 0.72) {
    // Glazed jars and pots: a row of necks, which is a silhouette nothing else here has.
    const n = 3 + Math.floor(rng() * 3);
    for (let i = 0; i < n; i++) {
      const [px, pz] = at((i - (n - 1) / 2) * size * 0.34, (rng() - 0.5) * size * 0.3);
      const jr = size * (0.11 + rng() * 0.08);
      const jh = size * (0.3 + rng() * 0.4);
      const col = vary(rng, [0x7a5a3a, 0x4a5a5a, 0x8a6a4a, 0x5a4a42][Math.floor(rng() * 4)], 0.12, 0.03);
      b.add('tile', cylGeo(jr * 0.72, jr, jh, 9, TEXEL.ceramic), trs(px, y + jh / 2, pz), col, 'both');
      b.add('tile', cylGeo(jr * 0.34, jr * 0.62, jh * 0.3, 8, TEXEL.ceramic), trs(px, y + jh * 1.12, pz),
        col.clone().multiplyScalar(0.9), 'both');
      if (rng() < 0.5) {
        b.add('wood', cylGeo(jr * 0.36, jr * 0.36, 0.03, 7, 0.3), trs(px, y + jh * 1.28, pz),
          vary(rng, 0x8a6a44, 0.12), 'none');
      }
    }
    return;
  }
  if (roll < 0.88) {
    // Shallow tray heaped with produce. The heap is what reads; the fruit is texture.
    const tw = size * 1.05;
    const td = size * 0.78;
    b.solid('wood', x, y + 0.04, z, tw, 0.08, td, { rotY, color: vary(rng, 0x9a7c52, 0.12), collide: false, shadow: 'cast' });
    for (const [ox, oz, bw, bd] of [[0, -td / 2, tw, 0.04], [0, td / 2, tw, 0.04], [-tw / 2, 0, 0.04, td], [tw / 2, 0, 0.04, td]]) {
      const [px, pz] = at(ox, oz);
      b.solid('wood', px, y + 0.11, pz, bw, 0.1, bd, { rotY, color: vary(rng, 0x8a6a44, 0.12), collide: false, shadow: 'none' });
    }
    const hex = PRODUCE[Math.floor(rng() * PRODUCE.length)];
    const n = 9 + Math.floor(rng() * 8);
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const rr = Math.sqrt(rng());
      const pr = size * (0.05 + rng() * 0.035);
      const [px, pz] = at(Math.cos(a) * rr * tw * 0.4, Math.sin(a) * rr * td * 0.4);
      b.add('polymer', sphereGeo(pr, 6, 4, 0.3),
        trs(px, y + 0.14 + pr * (1.4 - rr), pz, rng() * 3, rng() * 3, 0, 1, 0.86, 1),
        accent(hex, { lum: 0.72 + rng() * 0.3 }), 'both');
    }
    return;
  }
  // Open grain sack with the collar rolled down and a scoop standing in it.
  const gr = size * 0.3;
  const col = vary(rng, SACK_HEX[Math.floor(rng() * SACK_HEX.length)], 0.1);
  b.add('fabric', cylGeo(gr, gr * 0.86, size * 0.5, 10, TEXEL.fabric), trs(x, y + size * 0.25, z), col, 'both');
  b.add('fabric', cylGeo(gr * 1.14, gr * 1.14, size * 0.12, 10, TEXEL.fabric), trs(x, y + size * 0.5, z),
    col.clone().multiplyScalar(1.08), 'cast');
  b.add('polymer', sphereGeo(gr * 0.92, 8, 4, 0.4), trs(x, y + size * 0.5, z, 0, rng() * 3, 0, 1, 0.4, 1),
    vary(rng, 0xc0a870, 0.1, 0.02, { sat: 0.7 }), 'both');
  const sa = rng() * Math.PI * 2;
  rod(b, 'wood', x + Math.cos(sa) * gr * 0.4, y + size * 0.72, z + Math.sin(sa) * gr * 0.4,
    Math.cos(sa) * 0.5, 1, Math.sin(sa) * 0.5, size * 0.5, 0.014, vary(rng, 0x8a6a44, 0.12), { seg: 4 });
}

/**
 * Goods hung off the awning rail: bundles, strings of dried produce, a lamp.
 *
 * Hanging stock is what fills the band between the stall top and the awning, which is
 * otherwise the emptiest 1.2 m in the frame and sits exactly at standing eye height.
 */
export function hangingGoods(b, rng, x, y, z, w, rotY = 0) {
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const n = Math.max(2, Math.round(w / 0.7));
  for (let i = 0; i < n; i++) {
    if (rng() < 0.25) continue;
    const lx = (i - (n - 1) / 2) * (w / n) + (rng() - 0.5) * 0.1;
    const [px, pz] = at(lx, 0);
    const drop = 0.25 + rng() * 0.4;
    const roll = rng();
    tube(b, 'metalPainted', px, y - 0.04, pz, 0.008, 0.09, 0, 0, 0, { color: shade(0x6a6255, 0.5), seg: 4, shadow: 'none' });
    if (roll < 0.4) {
      // A bunch: small fruit threaded onto a cord.
      const hex = PRODUCE[Math.floor(rng() * PRODUCE.length)];
      const k = 3 + Math.floor(rng() * 4);
      for (let j = 0; j < k; j++) {
        const pr = 0.035 + rng() * 0.025;
        b.add('polymer', sphereGeo(pr, 6, 4, 0.3),
          trs(px + (rng() - 0.5) * 0.05, y - 0.12 - (drop * j) / k, pz + (rng() - 0.5) * 0.05),
          accent(hex, { lum: 0.7 + rng() * 0.3 }), 'both');
      }
    } else if (roll < 0.75) {
      // A bolt of cloth over the rail, hanging in two unequal tails.
      const col = vary(rng, CLOTH_COLOURS[Math.floor(rng() * CLOTH_COLOURS.length)], 0.14, 0.05, { sat: 0.95 });
      for (const sgn of [-1, 1]) {
        const dh = drop * (sgn > 0 ? 1 : 0.65 + rng() * 0.4);
        const [tx2, tz2] = at(lx, sgn * 0.03);
        angled(b, 'fabric', tx2, y - 0.06 - dh / 2, tz2, 0.16 + rng() * 0.14, dh, 0.012,
          0, rotY, (rng() - 0.5) * 0.2, { tile: TEXEL.canvas, color: col, shadow: 'both', mat: CLOTH_MAT });
      }
    } else {
      // A pan scale, or a lantern.
      b.add('metalPainted', cylGeo(0.11, 0.11, 0.02, 9, 0.3), trs(px, y - drop, pz), shade(0x8a8479, 0.85), 'cast');
      for (const a of [0, 2.1, 4.2]) {
        rod(b, 'metalPainted', px + Math.cos(a) * 0.05, y - drop / 2 - 0.04, pz + Math.sin(a) * 0.05,
          Math.cos(a) * 0.2, 1, Math.sin(a) * 0.2, drop * 0.9, 0.005, shade(0x6a6255, 0.55), { seg: 3 });
      }
    }
  }
}

/** Market stall: trestle, goods, awning. Reads as commerce, blocks as crouch cover. */
export function marketStall(b, rng, x, z, rotY = 0, o = {}) {
  const w = o.w ?? 2.4;
  const d = 1.0;
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const wood = vary(rng, 0x8a7458, 0.12, 0.02, { sat: 0.6 });
  b.solid('wood', x, 0.86, z, w, 0.5, d, { rotY, color: wood, matId: 'wood', collide: false });
  // The trestle plus its cloth skirt reads as a solid mass, so collide as one.
  const [shw, shd] = rotExtent(w, d, rotY);
  b.collider(x - shw, 0, z - shd, x + shw, 0.92, z + shd, 'wood');
  b.markCover(x, z, shw, shd, 0.92, 'low');
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const [px, pz] = at(sx * (w / 2 - 0.12), sz * (d / 2 - 0.1));
      b.solid('wood', px, 0.42, pz, 0.08, 0.85, 0.08, { rotY, color: wood.clone().multiplyScalar(0.8), collide: false, shadow: 'cast' });
    }
  }
  // Cloth skirt: a hanging sheet, not a painted board. It gathers at the corners where
  // it is tied and bellies between them, which is the same reason the awning sags.
  const [fx, fz] = at(0, d / 2 + 0.02);
  const skirtCol = vary(rng, CLOTH_COLOURS[Math.floor(rng() * CLOTH_COLOURS.length)], 0.12, 0.03, { sat: 0.7 });
  const skirtSag = 0.05 + rng() * 0.05;
  // Built flat and tipped upright: local +Z becomes "down the cloth", so the sheet's
  // own displacement axis becomes the outward belly between the two tied corners.
  b.add('fabric',
    sheetGeo(w, 0.86, Math.max(5, Math.round(w / 0.24)), 3,
      (u, v) => Math.cos(u * Math.PI) * skirtSag * v,
      {
        tile: TEXEL.canvas, color: skirtCol,
        shade: (u, v) => 0.7 + 0.32 * (1 - v) + Math.cos(u * Math.PI * 3) * 0.06,
      }),
    trs(fx, 0.88, fz, Math.PI / 2, rotY, 0), null, 'both', true, CLOTH_MAT);

  // Goods, and the band of hanging stock between the table and the awning.
  const slots = Math.max(2, Math.round(w / 0.95));
  for (let i = 0; i < slots; i++) {
    const [gx, gz] = at((i - (slots - 1) / 2) * (w / slots), (rng() - 0.5) * 0.24);
    goodsPile(b, rng, gx, 1.11, gz, rotY + (rng() - 0.5) * 0.7, Math.min(0.66, (w / slots) * 0.82));
  }
  const [ax, az] = at(0, -0.1);
  awning(b, rng, ax, 2.35, az, w + 0.5, d + 0.9, rotY, {});
  const [hx, hz] = at(0, d * 0.55);
  hangingGoods(b, rng, hx, 2.02, hz, w, rotY);
  // A price board propped against the trestle end.
  if (rng() < 0.6) {
    const sgn = rng() < 0.5 ? -1 : 1;
    const [px, pz] = at(sgn * (w / 2 + 0.06), d * 0.42);
    angled(b, 'wood', px, 0.52, pz, 0.34, 0.46, 0.03, 0, rotY + sgn * 0.3, (rng() - 0.5) * 0.2,
      { color: shade(0x3a352e, 0.3), shadow: 'cast' });
  }
}

/**
 * Rooftop air-conditioning condenser.
 *
 * `uOff`/`vOff` matter more here than anywhere else on the map: these get placed in
 * pairs two metres apart, and two identical boxes carrying the *same rust blotch in
 * the same corner* is the loudest instancing tell there is. Sliding the UVs costs
 * nothing and breaks it completely.
 */
export function acUnit(b, rng, x, y, z, rotY = 0, o = {}) {
  const col = vary(rng, [0x8f8e88, 0x9a9084, 0x86867e][Math.floor(rng() * 3)], 0.11);
  const sc = o.scale ?? (0.86 + rng() * 0.3);
  const w = 0.95 * sc;
  const d = 0.78 * sc;
  const h = 0.72 * sc;
  const uv = { uOff: rng() * 6, vOff: rng() * 6 };
  b.solid('metalPainted', x, y + h / 2, z, w, h, d, { tile: 0.7, rotY, color: col, matId: 'metal', ...uv });
  b.add('metalPainted', cylGeo(0.3 * sc, 0.3 * sc, 0.05, 12, 0.5), trs(x, y + h + 0.02, z, 0, rng() * 3, 0),
    col.clone().multiplyScalar(0.6), 'none');
  // Fan guard: concentric rings, so the top is not a blank disc from every roof.
  for (const rr of [0.28, 0.19, 0.1]) {
    b.add('metalPainted', cylGeo(rr * sc, rr * sc, 0.012, 10, 0.3, true), trs(x, y + h + 0.05, z, 0, 0, 0),
      shade(0x4a453e, 0.42), 'none');
  }
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  for (let i = 0; i < 5; i++) {
    b.solid('metalPainted', x + nx * d * 0.52, y + 0.12 + i * (h - 0.24) / 4, z + nz * d * 0.52, w * 0.94, 0.05, 0.03,
      { tile: 0.3, rotY, color: col.clone().multiplyScalar(0.66), collide: false, shadow: 'none' });
  }
  b.solid('metalPainted', x, y + 0.03, z, w + 0.06, 0.06, d + 0.07, { tile: 0.5, rotY, color: shade(0x4a453e), collide: false, shadow: 'none' });
  // Anti-vibration feet and the condensate stain under them.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      b.solid('polymer', x + sx * w * 0.4 * nz + sz * d * 0.4 * nx, y + 0.03, z - sx * w * 0.4 * nx + sz * d * 0.4 * nz,
        0.1, 0.07, 0.1, { tile: 0.2, rotY, color: shade(0x2e2b26, 0.3), collide: false, shadow: 'none' });
    }
  }
  b.solid('concrete', x + nx * (d * 0.5 + 0.3), y + 0.012, z + nz * (d * 0.5 + 0.3), w * 1.1, 0.02, 0.7, {
    tile: 0.8, rotY, color: shade(0x6f6a5e, 0.66), collide: false, shadow: 'none',
  });
}

/** Wall-mounted split unit — the thing that makes a blank facade look inhabited. */
export function wallAC(b, rng, x, y, z, rotY = 0) {
  const col = vary(rng, [0xb9b6ac, 0xc0bcae, 0xada79a][Math.floor(rng() * 3)], 0.08);
  const w = 0.74 + rng() * 0.18;
  const uv = { uOff: rng() * 6, vOff: rng() * 6 };
  b.solid('metalPainted', x, y, z, w, 0.32 + rng() * 0.06, 0.3, { tile: 0.5, rotY, color: col, collide: false, shadow: 'cast', ...uv });
  b.solid('metalPainted', x, y - 0.2, z, w + 0.08, 0.05, 0.36, { tile: 0.4, rotY, color: shade(0x5a554c), collide: false, shadow: 'none' });
  // Louvre blade across the underside, and the pipe run leaving the back.
  b.solid('metalPainted', x, y - 0.13, z, w - 0.08, 0.04, 0.34, { tile: 0.3, rotY, color: shade(0x8e8a80, 0.8), collide: false, shadow: 'none' });
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  tube(b, 'polymer', x - nx * 0.02 + nz * w * 0.42, y - 0.42, z - nz * 0.02 - nx * w * 0.42, 0.022, 0.5, 0, 0, 0,
    { color: shade(0xa8a294, 0.9), seg: 5, shadow: 'none' });
  // The stain it drips onto the wall below.
  b.solid('concrete', x - nx * 0.005 + nz * w * 0.42, y - 1.0, z - nz * 0.005 - nx * w * 0.42, 0.14, 1.1, 0.015, {
    rotY, color: shade(0x6f6a5e, 0.7), collide: false, shadow: 'none',
  });
}

export function satDish(b, rng, x, y, z, rotY = 0, r = 0.42) {
  const col = shade(0xa8a49a);
  const g = new THREE.SphereGeometry(r, 12, 6, 0, Math.PI * 2, 0, Math.PI * 0.38);
  const uv = g.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * 2, uv.getY(i) * 2);
  b.add('metalPainted', g, trs(x, y, z, Math.PI * 0.62, rotY, 0), col, 'cast');
  tube(b, 'metalPainted', x, y - 0.28, z, 0.03, 0.6, 0, 0, 0, { color: shade(0x6a655c), seg: 6, shadow: 'none' });
  tube(b, 'metalPainted', x + Math.sin(rotY) * r * 0.55, y + 0.16, z + Math.cos(rotY) * r * 0.55, 0.035, 0.2,
    Math.PI * 0.35, rotY, 0, { color: shade(0x2a2724), seg: 6, shadow: 'none' });
}

/** Run of pipe along a wall with brackets. Pure decoration, no collision. */
export function pipeRun(b, x, y, z, len, axis = 'x', o = {}) {
  const r = o.r ?? 0.07;
  const col = o.color ?? shade(0x6e6255);
  tube(b, 'metalPainted', x, y, z, r, len, axis === 'x' ? 0 : Math.PI / 2, 0, axis === 'x' ? Math.PI / 2 : 0, {
    color: col, seg: 8, shadow: 'cast', tile: 0.6,
  });
  const n = Math.max(2, Math.round(len / 2.2));
  for (let i = 0; i <= n; i++) {
    const t = -len / 2 + (len * i) / n;
    const px = axis === 'x' ? x + t : x;
    const pz = axis === 'x' ? z : z + t;
    b.solid('metalPainted', px, y, pz, 0.1, 0.16, 0.16, { tile: 0.3, color: col.clone().multiplyScalar(0.7), collide: false, shadow: 'none' });
  }
}

export function cable(b, a, c, sag = 0.8, o = {}) {
  b.add('polymer', sagGeo(a, c, sag, o.r ?? 0.028, o.seg ?? 10), null, o.color ?? shade(0x1c1a18), 'none');
}

/**
 * Laundry line with hanging cloth — instant "people live here".
 *
 * These hang against the sky from every roof on the map, which makes them the piece of
 * fabric most often seen backlit, and they were flat opaque boxes: a row of black
 * rectangles on the skyline. They are now single sagging sheets on the shared cloth
 * material, so the sun comes through them and each one bellies differently.
 */
export function laundry(b, rng, a, c) {
  cable(b, a, c, 0.5, { r: 0.018 });
  const n = Math.max(2, Math.floor(a.distanceTo(c) / 1.1));
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const p = new THREE.Vector3().lerpVectors(a, c, t);
    p.y -= Math.sin(t * Math.PI) * 0.5;
    const w = 0.5 + rng() * 0.35;
    const h = 0.6 + rng() * 0.5;
    const ang = Math.atan2(c.z - a.z, c.x - a.x);
    const belly = 0.06 + rng() * 0.1;
    const twist = (rng() - 0.5) * 0.3;
    b.add('fabric',
      sheetGeo(w, h, 4, 4,
        // Pegged at the top corners, so it bows out in the middle and lifts at the hem.
        (u, v) => Math.cos(u * Math.PI) * belly * (0.3 + v) + Math.sin(v * Math.PI * 1.4) * belly * 0.4 * twist,
        {
          tile: TEXEL.canvas,
          color: vary(rng, CLOTH_COLOURS[Math.floor(rng() * CLOTH_COLOURS.length)], 0.16, 0.05, { sat: 0.88 }),
          shade: (u, v) => 0.74 + 0.34 * (1 - Math.abs(u) * 1.6) - v * 0.1,
        }),
      trs(p.x, p.y - 0.02, p.z, Math.PI / 2, -ang, 0), null, 'both', true, CLOTH_MAT);
  }
}

/** Scaffolding tower: pipe frame with plank decks. Decks are solid, pipes are not. */
export function scaffold(b, rng, x, z, w, d, levels, o = {}) {
  const lh = o.levelHeight ?? 2.0;
  const col = shade(0x8e8478);
  const H = levels * lh;
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const px = x + sx * w / 2;
      const pz = z + sz * d / 2;
      tube(b, 'metalPainted', px, H / 2, pz, 0.05, H, 0, 0, 0, { color: col, seg: 6 });
      b.collider(px - 0.09, 0, pz - 0.09, px + 0.09, H, pz + 0.09, 'metal');
    }
  }
  for (let l = 1; l <= levels; l++) {
    const y = l * lh;
    for (const sz of [-1, 1]) {
      tube(b, 'metalPainted', x, y, z + sz * d / 2, 0.045, w, 0, 0, Math.PI / 2, { color: col, seg: 6, shadow: 'none' });
      tube(b, 'metalPainted', x, y - lh * 0.5, z + sz * d / 2, 0.04, Math.hypot(w, lh), 0, 0, Math.PI / 2 - Math.atan2(lh, w), { color: col, seg: 5, shadow: 'none' });
    }
    for (const sx of [-1, 1]) {
      tube(b, 'metalPainted', x + sx * w / 2, y, z, 0.045, d, Math.PI / 2, 0, 0, { color: col, seg: 6, shadow: 'none' });
    }
    // Plank deck — omitted on levels not listed in `decks` so a stair can pass through.
    if (o.decks && !o.decks.includes(l)) continue;
    const planks = Math.max(2, Math.round(d / 0.32));
    for (let i = 0; i < planks; i++) {
      const pz = z - d / 2 + 0.16 + (i * (d - 0.32)) / (planks - 1);
      b.solid('wood', x, y + 0.03, pz, w - 0.05, 0.06, 0.3, {
        tile: 0.6, color: vary(rng, 0x9d7f56, 0.12), collide: false, shadow: 'both',
      });
    }
    b.collider(x - w / 2, y - 0.06, z - d / 2, x + w / 2, y + 0.06, z + d / 2, 'wood');
    b.markCover(x, z, w / 2, d / 2, y + 1.0, 'high', y);
  }
  return H;
}

/** Steel shelving rack with pallets on it. Warehouse interior backbone. */
export function shelfRack(b, rng, x, z, w, rotY = 0, o = {}) {
  const d = 1.1;
  const H = o.h ?? 4.2;
  const col = vary(rng, 0x9a5a30, 0.1);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const bays = Math.max(1, Math.round(w / 2.6));
  for (let i = 0; i <= bays; i++) {
    const lx = -w / 2 + (i * w) / bays;
    for (const sz of [-1, 1]) {
      const [px, pz] = at(lx, sz * d / 2);
      b.solid('metalPainted', px, H / 2, pz, 0.12, H, 0.12, { tile: 0.5, rotY, color: col, matId: 'metal' });
    }
  }
  for (const ly of [1.3, 2.7, 4.0]) {
    if (ly > H) continue;
    b.solid('metalPainted', x, ly, z, w, 0.09, d, { tile: 1.0, rotY, color: col.clone().multiplyScalar(0.9), matId: 'metal' });
    for (let i = 0; i < bays; i++) {
      if (rng() < 0.35) continue;
      const [px, pz] = at(-w / 2 + (i + 0.5) * (w / bays), 0);
      pallet(b, rng, px, ly + 0.05, pz, { rotY });
      crate(b, rng, px, ly + 0.2, pz, 1.1, 0.7 + rng() * 0.4, 0.8, { rotY, color: vary(rng, 0x8a6a44, 0.14) });
    }
  }
  const [rhw, rhd] = rotExtent(w, d, rotY);
  b.markCover(x, z, rhw, rhd, 1.4, 'high');
}

/** Diesel generator / utility box. */
export function generator(b, rng, x, z, rotY = 0) {
  const col = vary(rng, 0x6b7a52, 0.1);
  b.solid('metalPainted', x, 0.62, z, 2.2, 1.24, 1.1, { tile: 0.9, rotY, color: col, cover: 'low', matId: 'metal' });
  b.solid('metalPainted', x, 1.28, z, 2.3, 0.08, 1.2, { tile: 0.6, rotY, color: col.clone().multiplyScalar(0.85), collide: false, shadow: 'cast' });
  tube(b, 'metalPainted', x + Math.cos(rotY) * 0.9, 1.9, z - Math.sin(rotY) * 0.9, 0.07, 1.3, 0, 0, 0, { color: shade(0x2e2a25) });
  for (let i = 0; i < 6; i++) {
    b.solid('metalPainted', x - Math.cos(rotY) * 1.11, 0.4 + i * 0.12, z + Math.sin(rotY) * 1.11, 0.04, 0.07, 0.8, {
      tile: 0.3, rotY, color: shade(0x39352e), collide: false, shadow: 'none',
    });
  }
}

export function streetLamp(b, x, z, rotY = 0, o = {}) {
  const h = o.h ?? 6.4;
  const col = shade(0x5e5850);
  b.post('metalPainted', x, h / 2, z, 0.09, h, { seg: 8, tile: 0.8, color: col });
  b.post('concrete', x, 0.22, z, 0.22, 0.44, { seg: 8, color: shade(0x8a8479), collide: false });
  const dx = Math.sin(rotY) * 0.85;
  const dz = Math.cos(rotY) * 0.85;
  tube(b, 'metalPainted', x + dx / 2, h - 0.1, z + dz / 2, 0.06, 1.0, rotY === 0 ? Math.PI / 2 : 0, 0, rotY === 0 ? 0 : Math.PI / 2, { color: col });
  b.solid('metalPainted', x + dx, h - 0.22, z + dz, 0.62, 0.16, 0.34, { tile: 0.4, rotY, color: col.clone().multiplyScalar(0.9), collide: false, shadow: 'cast' });
  b.solid('glass', x + dx, h - 0.31, z + dz, 0.5, 0.05, 0.26, { tile: 0.4, rotY, color: shade(0x22242a), collide: false, shadow: 'none' });
}

export function utilityPole(b, rng, x, z, o = {}) {
  const h = o.h ?? 8.0;
  const lean = o.lean ?? 0;
  const col = vary(rng, 0x6b5540, 0.1);
  b.add('wood', cylGeo(0.13, 0.17, h, 8, 0.30, false, h * 6.0), trs(x, h / 2, z, lean, 0, o.leanZ ?? 0), col, 'both');
  b.collider(x - 0.2, 0, z - 0.2, x + 0.2, h, z + 0.2, 'wood');
  for (const ay of [h - 0.6, h - 1.5]) {
    b.solid('wood', x, ay, z, 0.14, 0.14, 2.2, { color: col.clone().multiplyScalar(0.9), collide: false, shadow: 'cast' });
    for (const s of [-0.9, 0, 0.9]) {
      b.post('glass', x, ay + 0.19, z + s, 0.06, 0.18, { seg: 6, tile: 0.3, color: shade(0x3a5a52), collide: false, shadow: 'none' });
    }
  }
  return h;
}

/** Chain-link fence panel: posts, rails and a translucent mesh stand-in. */
export function fence(b, x, z, len, axis = 'x', o = {}) {
  const h = o.h ?? 2.4;
  const col = shade(0x6d675d);
  const n = Math.max(1, Math.round(len / 2.6));
  for (let i = 0; i <= n; i++) {
    const t = -len / 2 + (len * i) / n;
    const px = axis === 'x' ? x + t : x;
    const pz = axis === 'x' ? z : z + t;
    b.post('metalPainted', px, h / 2, pz, 0.05, h, { seg: 6, tile: 0.6, color: col, collide: false });
  }
  for (const ry of [h - 0.06, h * 0.5, 0.1]) {
    tube(b, 'metalPainted', x, ry, z, 0.035, len, axis === 'x' ? 0 : Math.PI / 2, 0, axis === 'x' ? Math.PI / 2 : 0, {
      color: col, seg: 5, shadow: 'none',
    });
  }
  // Chain-link, built as actual wire rather than a textured slab. A solid panel of
  // fine stipple is what turns a fence into a pinstripe moiré at distance; a coarse
  // lattice of real bars aliases gracefully because it mips down to flat grey.
  const wire = shade(0x8a8880, 0.72);
  const pitch = 0.11;                       // 50 mm mesh reads as ~110 mm of lattice
  const strands = Math.max(2, Math.round(len / pitch));
  for (let i = 0; i <= strands; i++) {
    const t = -len / 2 + (len * i) / strands;
    const px = axis === 'x' ? x + t : x;
    const pz = axis === 'x' ? z : z + t;
    b.solid('metalPainted', px, (h - 0.1) / 2 + 0.05, pz, 0.012, h - 0.14, 0.012, {
      tile: 0.2, color: wire, collide: false, shadow: 'none',
    });
  }
  const courses = Math.max(2, Math.round((h - 0.14) / pitch));
  for (let j = 1; j < courses; j++) {
    const wy = 0.05 + ((h - 0.14) * j) / courses;
    b.solid('metalPainted', x, wy, z, axis === 'x' ? len : 0.012, 0.012, axis === 'x' ? 0.012 : len, {
      tile: 0.2, color: wire, collide: false, shadow: 'none',
    });
  }
  const hw = axis === 'x' ? len / 2 : 0.08;
  const hd = axis === 'x' ? 0.08 : len / 2;
  b.collider(x - hw, 0, z - hd, x + hw, h, z + hd, 'metal');
}

/**
 * A blocked-in alphabet, 3 x 5 cells per glyph, sharing the numeral encoding used by
 * the container stencils.
 *
 * Shop signage was the thing missing from every terrace on the map: four bays with
 * four blank boards over them read as a model of a shop row, because a shop's *name*
 * is the single strongest signal that a person owns it. Real letterforms are out of
 * reach without a texture budget, but a 3 x 5 blocked glyph is exactly what a hand-
 * painted fascia looks like from across a street, and it mips to a smudge at distance
 * the way paint does.
 */
const FONT = {
  A: 0b010101111101101, B: 0b110101110101110, C: 0b011100100100011, D: 0b110101101101110,
  E: 0b111100110100111, F: 0b111100110100100, G: 0b011100101101011, H: 0b101101111101101,
  I: 0b111010010010111, J: 0b001001001101010, K: 0b101101110101101, L: 0b100100100100111,
  M: 0b101111111101101, N: 0b101111111111101, O: 0b010101101101010, P: 0b110101110100100,
  Q: 0b010101101111011, R: 0b110101110101101, S: 0b011100010001110, T: 0b111010010010010,
  U: 0b101101101101011, V: 0b101101101101010, W: 0b101101111111101, X: 0b101101010101101,
  Y: 0b101101010010010, Z: 0b111001010100111, '-': 0b000000111000000, "'": 0b010010000000000,
  ' ': 0,
};

/**
 * Painted lettering on a face. `size` is the cap height; the advance is 0.8 of it, so
 * a word's width is `text.length * 0.8 * size`.
 */
export function signText(b, x, y, z, rotY, text, o = {}) {
  const size = o.size ?? 0.3;
  const cw = size / 5;
  const col = o.color ?? shade(0xe4dcc6, 1.35, 0.25);
  const surface = o.surface ?? 'metalPainted';
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  const out = o.out ?? 0.05;
  const adv = size * 0.8;
  const t = text.toUpperCase();
  for (let g = 0; g < t.length; g++) {
    const bits = FONT[t[g]];
    if (!bits) continue;
    const gx = (g - (t.length - 1) / 2) * adv;
    for (let r = 0; r < 5; r++) {
      for (let cN = 0; cN < 3; cN++) {
        if (!(bits & (1 << (14 - (r * 3 + cN))))) continue;
        const side = gx + (cN - 1) * cw * 1.02;
        b.solid(surface, x + nx * out + nz * side, y + (2 - r) * cw * 1.02, z + nz * out - nx * side,
          cw, cw, 0.02, { tile: 0.12, rotY, color: col, collide: false, shadow: 'none' });
      }
    }
  }
}

/** Painted sign board / shop hoarding, optionally carrying a name. */
export function sign(b, rng, x, y, z, w, h, rotY = 0, hex = 0x3a5a6a, o = {}) {
  b.solid('metalPainted', x, y, z, w, h, 0.06, { tile: 0.6, rotY, color: vary(rng, hex, 0.1, 0.04), collide: false, shadow: 'cast' });
  b.solid('metalPainted', x, y, z, w + 0.08, h + 0.08, 0.03, { tile: 0.4, rotY, color: shade(0x3a3630), collide: false, shadow: 'none' });
  if (o.text) {
    // Fit the cap height to whichever of the two dimensions runs out first, so a name
    // never overruns its board and never floats in the middle of an empty one.
    const size = Math.min(h * 0.62, (w - 0.18) / (o.text.length * 0.8));
    signText(b, x, y, z, rotY, o.text, { size, out: 0.05, color: o.textColor });
  }
}

/** A projecting shop sign hung off a bracket — reads down the street, not across it. */
export function hangingSign(b, rng, x, y, z, rotY = 0, text = '', o = {}) {
  const w = o.w ?? 1.1;
  const h = o.h ?? 0.5;
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  const col = shade(0x5a5348, 0.5);
  // Bracket: a horizontal arm off the wall with a diagonal stay under it.
  tube(b, 'metalPainted', x + nx * (w / 2 + 0.08), y + h / 2 + 0.28, z + nz * (w / 2 + 0.08), 0.022, w + 0.2,
    Math.PI / 2 * nz, rotY, Math.PI / 2 * nx, { color: col, seg: 5 });
  rod(b, 'metalPainted', x + nx * (w / 2 - 0.1), y + h / 2 - 0.05, z + nz * (w / 2 - 0.1),
    nx, 0.9, nz, w * 0.9, 0.014, col, { seg: 4 });
  for (const sgn of [-1, 1]) {
    const sx = x + nx * (w / 2 + 0.08 + sgn * w * 0.42);
    const sz = z + nz * (w / 2 + 0.08 + sgn * w * 0.42);
    tube(b, 'metalPainted', sx, y + h / 2 + 0.16, sz, 0.008, 0.22, 0, 0, 0, { color: col, seg: 4, shadow: 'none' });
  }
  // The board hangs perpendicular to the wall, so it is legible along the footway.
  const bx = x + nx * (w / 2 + 0.08);
  const bz = z + nz * (w / 2 + 0.08);
  b.solid('metalPainted', bx, y, bz, 0.05, h, w, {
    tile: 0.5, rotY, color: vary(rng, o.hex ?? 0x7a3a34, 0.1, 0.04), collide: false, shadow: 'cast',
  });
  if (text) signText(b, bx, y, bz, rotY + Math.PI / 2, text, { size: Math.min(h * 0.6, (w - 0.14) / (text.length * 0.8)), out: 0.04 });
}

/**
 * Window opening.
 *
 * A plain dark quad on a wall renders as pure black, and pure black never occurs on
 * lit geometry outdoors — it reads as a hole punched in the world, and a row of them
 * reads as a row of holes. So every opening gets three things: a recessed interior
 * box so there is always a dim surface behind the glass, a genuinely reflective pane
 * that catches the sky from `scene.environment`, and a per-window state — glazed,
 * boarded, sheeted, broken or lit — so no two along a facade match.
 */
export function window_(b, rng, x, y, z, w, h, rotY = 0, o = {}) {
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  // Along-wall unit vector, so shutters and planks offset sideways correctly.
  const tx = nz;
  const tz = -nx;
  const at = (out, side) => [x + nx * out + tx * side, z + nz * out + tz * side];

  // --- interior panel ------------------------------------------------------
  // The facade behind is solid, so a recessed box would simply be buried inside it.
  // Instead the interior sits a hair proud of the wall and the frame stands further
  // proud and casts onto it, which is what actually reads as depth from the street.
  const [ix, iz] = at(0.02, 0);
  b.solid('concrete', ix, y, iz, w, h, 0.03, {
    rotY, uOff: rng() * 6, vOff: rng() * 6, color: shade(0x4a443b, 0.26 + rng() * 0.1), collide: false, shadow: 'none',
  });

  const state = o.state ?? (o.lit ? 'lit' : pickState(rng));

  if (state === 'glazed' || state === 'lit') {
    // Low roughness plus the scene environment map: the pane mirrors the sky, which
    // is what separates a window from a hole at every viewing angle.
    const [px, pz] = at(0.05, 0);
    b.solid('glass', px, y, pz, w, h, 0.04, {
      tile: 0.7, rotY, color: shade(o.glass ?? 0x8fa6c2, 0.62, 0.5), collide: false, shadow: 'none',
      matOverrides: { roughness: 0.08, metalness: 0, envMapIntensity: 2.2 },
    });
  }
  if (state === 'lit') {
    const [lx, lz] = at(-0.1, 0);
    b.solid('glass', lx, y, lz, w - 0.08, h - 0.08, 0.04, {
      tile: 0.7, rotY, color: shade(0xffc27a, 1.0, 0.8), collide: false, shadow: 'none',
      matOverrides: { emissive: 0xffc27a, emissiveIntensity: 2.2, roughness: 0.9, metalness: 0 },
    });
  }
  if (state === 'boarded') {
    const n = 3 + Math.floor(rng() * 2);
    for (let i = 0; i < n; i++) {
      const t = -h / 2 + (h * (i + 0.5)) / n;
      const [px, pz] = at(0.08, (rng() - 0.5) * 0.12);
      b.solid('wood', px, y + t, pz, w * (1.02 + rng() * 0.12), h / n - 0.03, 0.045, {
        tile: 0.4, rotY: rotY + (rng() - 0.5) * 0.07, color: vary(rng, 0x8a6a44, 0.14),
        collide: false, shadow: 'cast',
      });
    }
  }
  if (state === 'sheeted') {
    const [px, pz] = at(0.07, 0);
    b.solid('fabric', px, y + 0.02, pz, w * 1.04, h * 0.98, 0.03, {
      tile: TEXEL.canvas, rotY: rotY + (rng() - 0.5) * 0.05,
      color: vary(rng, rng() < 0.5 ? 0x9fb6c2 : 0xb0a48c, 0.12, 0.03, { sat: 0.6 }),
      collide: false, shadow: 'cast',
    });
  }
  if (state === 'broken') {
    // Shards clinging to the frame — the silhouette is what sells "blown out".
    for (let i = 0; i < 3; i++) {
      const side = (rng() - 0.5) * w * 0.72;
      const up = (rng() < 0.5 ? 1 : -1) * h * (0.3 + rng() * 0.16);
      const [px, pz] = at(0.05, side);
      b.solid('glass', px, y + up, pz, w * (0.12 + rng() * 0.2), h * (0.14 + rng() * 0.2), 0.03, {
        tile: 0.5, rotY: rotY + (rng() - 0.5) * 0.5, color: shade(0x8fa6c2, 0.62, 0.5),
        collide: false, shadow: 'none',
        matOverrides: { roughness: 0.08, metalness: 0, envMapIntensity: 2.2 },
      });
    }
  }

  // --- frame, lintel and sill ----------------------------------------------
  // The lintel overhangs further than the jambs so it throws a hard shadow down the
  // glazing; that shadow is the whole reason the opening reads as a hole and not a
  // painted rectangle.
  const [hx2, hz2] = at(0.15, 0);
  b.solid('concrete', hx2, y + h / 2 + 0.07, hz2, w + 0.26, 0.14, 0.3, {
    rotY, color: shade(0xa89f8d), collide: false, shadow: 'cast',
  });
  const [sx2, sz2] = at(0.17, 0);
  b.solid('concrete', sx2, y - h / 2 - 0.07, sz2, w + 0.3, 0.13, 0.34, {
    rotY, color: shade(0xb0a695), collide: false, shadow: 'cast',
  });
  for (const sgn of [-1, 1]) {
    const [jx, jz] = at(0.09, sgn * (w / 2 + 0.06));
    b.solid('concrete', jx, y, jz, 0.12, h + 0.02, 0.18, {
      rotY, color: shade(0x9e9787), collide: false, shadow: 'cast',
    });
  }
  if (state === 'glazed' || state === 'lit' || state === 'broken') {
    const [mx, mz] = at(0.09, 0);
    b.solid('metalPainted', mx, y, mz, 0.05, h, 0.05, { tile: 0.3, rotY, color: shade(0x5a5348), collide: false, shadow: 'none' });
    b.solid('metalPainted', mx, y, mz, w - 0.04, 0.05, 0.05, { tile: 0.3, rotY, color: shade(0x5a5348), collide: false, shadow: 'none' });
  }

  if (o.shutter && rng() < 0.55) {
    const sgn = rng() < 0.5 ? -1 : 1;
    const [sx, sz] = at(0.17, sgn * w * 0.62);
    b.solid('wood', sx, y, sz, w * 0.5, h * 0.96, 0.05, {
      tile: 0.4, rotY, color: vary(rng, 0x4a6a6a, 0.14, 0.05, { sat: 0.75, lum: 0.72 }),
      collide: false, shadow: 'cast',
    });
  }
}

/** Weighted pick so a facade shows a mix rather than a row of identical openings. */
function pickState(rng) {
  const r = rng();
  if (r < 0.42) return 'glazed';
  if (r < 0.60) return 'boarded';
  if (r < 0.74) return 'broken';
  if (r < 0.86) return 'sheeted';
  if (r < 0.94) return 'open';
  return 'lit';
}

/** Roll-down shop shutter. */
export function shutter(b, rng, x, y, z, w, h, rotY = 0) {
  b.solid('corrugated', x, y, z, w, h, 0.08, { rotY, color: vary(rng, 0x7a6a52, 0.14, 0.05), collide: false, shadow: 'cast' });
  b.solid('metalPainted', x, y + h / 2 + 0.14, z, w + 0.2, 0.28, 0.24, { tile: 0.5, rotY, color: shade(0x635c50), collide: false, shadow: 'cast' });
}

/**
 * A tuft of scrub pushing through a crack — the smallest scale of the hierarchy,
 * and at 180 instances one of the most-seen objects on the map.
 *
 * It used to be three to five upright cones, which is a shape that occurs nowhere in
 * botany: cones read as traffic furniture or party hats, never as a plant. What makes
 * a tuft read is that the blades are *flat*, they *splay*, and the outer ones fall
 * over. Three species keep a hundred and eighty of them from being one plant.
 */
export function plant(b, rng, x, y, z, scale = 1) {
  const species = rng();
  if (species < 0.2) {
    // Woody dead scrub: no leaf at all, just a bleached skeleton.
    const col = vary(rng, 0x8a7c58, 0.16, 0.04);
    const n = 5 + Math.floor(rng() * 5);
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const lean = 0.4 + rng() * 0.9;
      const len = (0.22 + rng() * 0.3) * scale;
      rod(b, 'wood', x, y + 0.02, z,
        Math.cos(a) * Math.sin(lean), Math.cos(lean), Math.sin(a) * Math.sin(lean),
        len, 0.008 * scale, col.clone().multiplyScalar(0.8 + rng() * 0.4), { seg: 3 });
    }
    return;
  }
  const dry = species > 0.72;
  const col = vary(rng, dry ? 0x8a8340 : 0x4e6428, 0.22, 0.07, { sat: 0.95 });
  const n = 7 + Math.floor(rng() * 7);
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2;
    const r = rng() * 0.11 * scale;
    // Splay grows with distance from the centre, so the tuft has a fountain profile
    // and the outer blades lie almost flat.
    const t = rng();
    const lean = (0.12 + t * 1.15) * (0.7 + rng() * 0.6);
    const len = (0.4 - t * 0.14) * scale * (0.55 + rng() * 0.9);
    const px = x + Math.cos(a) * r;
    const pz = z + Math.sin(a) * r;
    // A blade, not a spike: wide across, near-zero thick, and bent.
    angled(b, 'fabric', px + Math.cos(a) * Math.sin(lean) * len * 0.42,
      y + Math.cos(lean) * len * 0.45 + 0.01,
      pz + Math.sin(a) * Math.sin(lean) * len * 0.42,
      0.016 * scale, len, 0.004,
      Math.sin(a) * lean, a, -Math.cos(a) * lean,
      { tile: 0.3, color: col.clone().multiplyScalar(0.72 + rng() * 0.55), shadow: 'none' });
  }
  // Seed heads on the dry variety: the bit that catches the low sun.
  if (dry) {
    for (let i = 0; i < 2 + Math.floor(rng() * 3); i++) {
      const a = rng() * Math.PI * 2;
      const h = (0.34 + rng() * 0.22) * scale;
      rod(b, 'fabric', x + Math.cos(a) * 0.06 * scale, y + h * 0.55, z + Math.sin(a) * 0.06 * scale,
        Math.cos(a) * 0.35, 1, Math.sin(a) * 0.35, h, 0.006 * scale,
        col.clone().multiplyScalar(1.25), { seg: 3 });
      rod(b, 'fabric', x + Math.cos(a) * 0.1 * scale, y + h * 1.02, z + Math.sin(a) * 0.1 * scale,
        Math.cos(a) * 0.35, 1, Math.sin(a) * 0.35, 0.09 * scale, 0.018 * scale,
        vary(rng, 0xc0b276, 0.12, 0.03), { seg: 4 });
    }
  }
}

/**
 * Broken concrete chunks and a spill of rubble around a point.
 *
 * `bounds` is [x0, x1, z0, z1] and matters on roofs. A scatter radius is a circle and a
 * roof is a rectangle with a parapet round it, so an unbounded spill puts pale stones
 * *through* the coping — where they read not as debris but as white shards floating off
 * the eave, which is exactly what they were doing on four roofs.
 */
export function rubble(b, rng, stones, x, z, radius, count, o = {}) {
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const v = new THREE.Vector3();
  const s = new THREE.Vector3();
  const bd = o.bounds;
  for (let i = 0; i < count; i++) {
    const a = rng() * Math.PI * 2;
    const r = Math.sqrt(rng()) * radius;
    const sc = (o.scale ?? 1) * (0.24 + rng() * 0.52);
    if (bd) {
      const px = x + Math.cos(a) * r;
      const pz = z + Math.sin(a) * r;
      const pad = sc * 0.6;
      if (px < bd[0] + pad || px > bd[1] - pad || pz < bd[2] + pad || pz > bd[3] - pad) continue;
    }
    e.set(rng() * 3, rng() * 3, rng() * 3);
    q.setFromEuler(e);
    v.set(x + Math.cos(a) * r, (o.y ?? 0) + 0.04 * sc, z + Math.sin(a) * r);
    s.set(sc, sc * (0.28 + rng() * 0.3), sc * (0.7 + rng() * 0.5));
    m.compose(v, q, s);
    stones.place(m.clone(), vary(rng, o.hex ?? 0x8d8578, 0.18, 0.02, { lum: o.lum ?? 0.34 }));
  }
}

/**
 * A mound of collapsed masonry that doubles as a ramp.
 *
 * Two jobs, and they pull against each other. Gameplay needs a solid, monotonic,
 * sub-0.45 m staircase from the ground to the top or the run-up stops working.
 * Art needs the exact opposite: nothing about a collapse is monotonic. So the two
 * are separated — a *core* of full-height tiers carries the collision and the climb,
 * and everything the player actually looks at is non-colliding dressing hung off it:
 * broken slab edges, individual bricks, snapped timber, torn rebar mesh, plaster
 * fragments, and a dust skirt where the pile meets the ground.
 *
 * `dir` is the direction the collapse ran, and it is the difference between a pile
 * and a pile that fell off something. Tiers drift downhill as they rise, the spill
 * fans that way, and the skirt is heaviest on that side.
 */
export function rubbleMound(b, rng, stones, x, z, radius, height, o = {}) {
  const steps = Math.max(4, Math.ceil(height / 0.30));
  const dir = o.dir ?? rng() * Math.PI * 2;
  const dx = Math.cos(dir);
  const dz = Math.sin(dir);
  const hex = o.hex ?? 0x8d8578;
  const brickHex = o.brickHex ?? 0x8a5240;

  /* --- core: the climb. Sub-step jitter only, so no radial path can skip a tier. */
  for (let i = 0; i < steps; i++) {
    const t = i / steps;
    const r = radius * (1 - t * 0.78) + 0.35;
    const top = (height * (i + 1)) / steps;
    // A per-tier drift of a few centimetres, always the same way, so the pile gains
    // a windward face and a lee slope without ever opening a gap in the staircase.
    const ox = -dx * t * radius * 0.11;
    const oz = -dz * t * radius * 0.11;
    b.solid('concrete', x + ox + (rng() - 0.5) * 0.06, top / 2, z + oz + (rng() - 0.5) * 0.06,
      r * 2, top, r * 1.7, {
        rotY: rng() * 0.5, uOff: rng() * 6, vOff: rng() * 6, color: vary(rng, hex, 0.14, 0.03),
        cover: i === 0 ? 'low' : i === steps - 1 && height > 1.4 ? 'high' : undefined,
        matId: 'concrete',
      });
  }

  /* --- broken edges. The core's tier lips are the one place a viewer can read the
     staircase underneath, so they get bombed first: chunks straddling each lip, half
     on the tread and half hanging over the riser, which buries the 90° corner. */
  for (let i = 0; i < steps; i++) {
    const t = i / steps;
    const r = radius * (1 - t * 0.78) + 0.35;
    const top = (height * (i + 1)) / steps;
    const lip = Math.round(3 + r * 1.7);
    for (let k = 0; k < lip; k++) {
      const a = rng() * Math.PI * 2;
      const rr = r * (0.86 + rng() * 0.3);
      const w = 0.3 + rng() * (0.25 + radius * 0.2);
      const masonry = rng() < 0.26;
      angled(b, masonry ? 'brick' : 'concrete',
        x + Math.cos(a) * rr - dx * t * radius * 0.11,
        top - 0.02 - rng() * 0.12,
        z + Math.sin(a) * rr * 0.85 - dz * t * radius * 0.11,
        w, 0.07 + rng() * 0.12, w * (0.5 + rng() * 0.7),
        (rng() - 0.5) * 1.0, rng() * 3, (rng() - 0.5) * 1.0,
        { color: vary(rng, masonry ? brickHex : hex, 0.16, 0.04) });
    }
  }
  /* Larger slab pieces, sparser, for the silhouette read at 20 m. */
  const chunks = Math.round(3 + radius * 1.6);
  for (let i = 0; i < chunks; i++) {
    const a = rng() * Math.PI * 2;
    const t = rng() * 0.8;
    const rr = (radius * (1 - t * 0.78) + 0.32) * (0.8 + rng() * 0.45);
    const w = 0.5 + rng() * (0.3 + radius * 0.24);
    const masonry = rng() < 0.3;
    angled(b, masonry ? 'brick' : 'concrete',
      x + Math.cos(a) * rr - dx * t * radius * 0.11,
      height * t * 0.94 + 0.1,
      z + Math.sin(a) * rr - dz * t * radius * 0.11,
      w, 0.1 + rng() * 0.14, w * (0.45 + rng() * 0.75),
      (rng() - 0.5) * 1.3, rng() * 3, (rng() - 0.5) * 1.3,
      { color: vary(rng, masonry ? brickHex : hex, 0.16, 0.04) });
  }

  /* --- individual bricks. The scale step between "slab" and "grit" that a pile of
     four rectangles has no way to imply. */
  const bricks = Math.round(7 + radius * 6);
  for (let i = 0; i < bricks; i++) {
    const a = rng() * Math.PI * 2;
    const t = rng() * rng();                       // biased low: bricks roll down
    const rr = (radius * (1 - t * 0.7) + 0.3) * (0.7 + rng() * 0.75);
    angled(b, 'brick',
      x + Math.cos(a) * rr - dx * t * radius * 0.11 + dx * (1 - t) * radius * 0.25,
      height * t * 0.92 + 0.05,
      z + Math.sin(a) * rr - dz * t * radius * 0.11 + dz * (1 - t) * radius * 0.25,
      0.215, 0.065, 0.102,   // one brick, actual size
      (rng() - 0.5) * 2.4, rng() * 3, (rng() - 0.5) * 2.4,
      { color: vary(rng, brickHex, 0.2, 0.03, { sat: 0.78 }), shadow: 'cast' });
  }

  /* --- plaster and render fragments: thin, pale, and flat on the slope. They are
     what makes the pile read as a *finished building* rather than a quarry. */
  const plates = Math.round(4 + radius * 3);
  for (let i = 0; i < plates; i++) {
    const a = rng() * Math.PI * 2;
    const t = rng();
    const rr = (radius * (1 - t * 0.72) + 0.3) * (0.8 + rng() * 0.6);
    const w = 0.18 + rng() * 0.34;
    angled(b, 'concrete', x + Math.cos(a) * rr, height * t * 0.9 + 0.06, z + Math.sin(a) * rr,
      w, 0.028, w * (0.5 + rng() * 0.9),
      (rng() - 0.5) * 1.1, rng() * 3, (rng() - 0.5) * 1.1,
      { color: vary(rng, 0xb5ac98, 0.1, 0.02, { sat: 0.5 }), shadow: 'none' });
  }

  /* --- snapped timber: joists and battens, with the splintered end showing.
     Two things had these reading as decorative orange stripes rather than as wood.
     They were built with `angled`, which gives a box the same repeat on every face, so
     a 1.5 m joist got a plank boundary drawn across it every 280 mm — ten bands along
     a single stick. `timberGeo` maps the long axis along the grain instead, so the
     boundary runs *down* the member the way a saw cut does. And the tint was a
     0.82-saturation orange with hue jitter on top; structural timber that has been
     under a roof for thirty years and then spent a month in the sun is a grey-brown,
     so the chroma comes down and the jitter with it. */
  const beams = 2 + Math.floor(rng() * 4);
  for (let i = 0; i < beams; i++) {
    const a = rng() * Math.PI * 2;
    const t = 0.15 + rng() * 0.6;
    const len = 0.9 + rng() * (1.2 + radius * 0.3);
    const px = x + Math.cos(a) * radius * (0.3 + rng() * 0.7);
    const pz = z + Math.sin(a) * radius * (0.3 + rng() * 0.7);
    const py = height * t;
    const ha = rng() * Math.PI * 2;
    const slope = -0.25 - rng() * 0.8;
    const col = vary(rng, 0x6e5c4a, 0.14, 0.015, { sat: 0.55 });
    beam(b, px, py, pz, len, 0.07 + rng() * 0.05, 0.05 + rng() * 0.05,
      0, ha, slope, { color: col, shadow: 'cast' });
    // Splinters off the broken end.
    for (let k = 0; k < 2; k++) {
      rod(b, 'wood',
        px + Math.cos(ha) * len * 0.5, py + Math.sin(-slope) * len * 0.5 + 0.03, pz - Math.sin(ha) * len * 0.5,
        Math.cos(ha) + (rng() - 0.5) * 0.6, Math.sin(-slope) + (rng() - 0.5) * 0.5, -Math.sin(ha) + (rng() - 0.5) * 0.6,
        0.1 + rng() * 0.16, 0.012, col, {});
    }
  }

  /* --- rebar. Not five lone wires: a torn mat, still bent into the shape of the
     slab it was cast into, plus a couple of bars that pulled free of it. */
  const mats = 1 + (rng() < 0.55 ? 1 : 0);
  for (let m = 0; m < mats; m++) {
    const a = rng() * Math.PI * 2;
    const rr = radius * (0.25 + rng() * 0.45);
    const mx = x + Math.cos(a) * rr;
    const mz = z + Math.sin(a) * rr;
    const my = height * (0.45 + rng() * 0.42) + 0.06;
    const yaw = rng() * Math.PI * 2;
    const tilt = 0.3 + rng() * 1.0;                   // peeled up off the slab
    const ca = Math.cos(yaw);
    const sa = Math.sin(yaw);
    // In-plane basis: `u` along the mat, `v` the perpendicular tipped up by `tilt`.
    const ux = ca, uy = 0, uz = sa;
    const ct = Math.cos(tilt);
    const st = Math.sin(tilt);
    const vx = -sa * ct, vy = st, vz = ca * ct;
    const span = 0.75 + rng() * (0.5 + radius * 0.22);
    const pitch = 0.19;
    const n = Math.max(2, Math.round(span / pitch));
    const rc = shade(0x744a2e, 0.5, 0.95);
    for (let i = 0; i <= n; i++) {
      const s = -span / 2 + (span * i) / n;
      rod(b, 'metalPainted', mx + vx * s, my + vy * s, mz + vz * s, ux, uy, uz, span, 0.011, rc, {});
      rod(b, 'metalPainted', mx + ux * s, my + uy * s, mz + uz * s, vx, vy, vz, span, 0.011, rc, {});
    }
    // Two bars that tore out of the mat and now stand proud of the pile.
    for (let k = 0; k < 2; k++) {
      const bl = 0.7 + rng() * 0.8;
      const bd = new THREE.Vector3(ux, uy, uz)
        .lerp(new THREE.Vector3((rng() - 0.5) * 1.4, 0.8 + rng() * 0.8, (rng() - 0.5) * 1.4), 0.6);
      rod(b, 'metalPainted', mx + (rng() - 0.5) * span, my + bl * 0.35, mz + (rng() - 0.5) * span,
        bd.x, bd.y, bd.z, bl, 0.012, rc, { seg: 5 });
    }
  }

  /* --- dust skirt. Everything above is hard-edged; a collapse is not, and the join
     between a pile and the ground is where that shows. Flat spill, fanned downhill. */
  const skirt = Math.round(7 + radius * 4);
  for (let i = 0; i < skirt; i++) {
    const a = rng() * Math.PI * 2;
    const lee = 0.65 + 0.6 * Math.max(0, Math.cos(a - dir));
    const rr = radius * (0.8 + rng() * 0.85) * lee;
    const w = 0.7 + rng() * (0.8 + radius * 0.4);
    b.solid('dirt', x + Math.cos(a) * rr, 0.014 + rng() * 0.012, z + Math.sin(a) * rr,
      w, 0.026, w * (0.5 + rng() * 0.8), {
        rotY: rng() * 3, uOff: rng() * 6, vOff: rng() * 6,
        color: vary(rng, 0xc4bba6, 0.09, 0.02, { sat: 0.45 }),
        collide: false, shadow: 'none',
      });
  }

  /* --- and the loose stuff, biased into the lee where it actually ends up. */
  rubble(b, rng, stones, x + dx * radius * 0.35, z + dz * radius * 0.35,
    radius * 1.5, Math.round(radius * 9), { scale: 1.1, hex });
  rubble(b, rng, stones, x + dx * radius * 0.8, z + dz * radius * 0.8,
    radius * 1.1, Math.round(radius * 7), { scale: 0.55, hex: 0x9a9082, lum: 0.5 });
}

/** Loose junk: bent sheet, a tyre, a bucket, a stack of bricks. */
export function junk(b, rng, stones, x, z) {
  const roll = rng();
  if (roll < 0.25) {
    angled(b, 'corrugated', x, 0.28, z, 1.6, 0.05, 1.1, (rng() - 0.5) * 0.5, rng() * 3, (rng() - 0.5) * 0.5, {
      tile: 0.8, color: vary(rng, 0x6a5a48, 0.16), shadow: 'both',
    });
  } else if (roll < 0.45) {
    b.add('polymer', cylGeo(0.34, 0.34, 0.2, 12, 0.6), trs(x, 0.1, z, (rng() - 0.5) * 0.4, rng() * 3, 0),
      shade(0x1b1a19), 'both');
  } else if (roll < 0.65) {
    for (let i = 0; i < 4 + Math.floor(rng() * 5); i++) {
      b.solid('brick', x + (rng() - 0.5) * 0.6, 0.035 + i * 0.07, z + (rng() - 0.5) * 0.6, 0.22, 0.07, 0.11, {
        rotY: rng() * 3, color: vary(rng, 0x7a4030, 0.16), collide: false, shadow: 'cast',
        uOff: rng() * 5, vOff: rng() * 5,
      });
    }
  } else if (roll < 0.8) {
    b.add('polymer', cylGeo(0.17, 0.13, 0.3, 10, 0.5), trs(x, 0.15, z, (rng() - 0.5) * 0.3, rng() * 3, 0),
      vary(rng, 0x2a4a5a, 0.2), 'both');
  } else {
    rubble(b, rng, stones, x, z, 0.9, 7, { scale: 0.8 });
  }
}

/* -------------------------------------------------------------------------- */
/*                        facade and street micro-detail                       */
/* -------------------------------------------------------------------------- */

/** Cast-iron downpipe with brackets and a shoe at the bottom. */
export function drainpipe(b, rng, x, z, y0, y1, rotY = 0, o = {}) {
  const r = o.r ?? 0.055;
  const col = o.color ?? shade(rng() < 0.5 ? 0x4a453e : 0x5a5044, 0.55);
  const h = y1 - y0;
  tube(b, 'polymer', x, (y0 + y1) / 2, z, r, h, 0, 0, 0, { color: col, seg: 7, tile: 0.4, shadow: 'cast' });
  const n = Math.max(2, Math.round(h / 1.8));
  for (let i = 0; i <= n; i++) {
    b.solid('polymer', x, y0 + (h * i) / n, z, r * 2.6, 0.07, r * 2.6, {
      tile: 0.25, rotY, color: col.clone().multiplyScalar(0.8), collide: false, shadow: 'none',
    });
  }
  // Shoe kicking the water away from the wall.
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  tube(b, 'polymer', x + nx * 0.12, y0 + 0.14, z + nz * 0.12, r, 0.34, Math.PI / 2.6 * nz, rotY, Math.PI / 2.6 * nx, {
    color: col, seg: 6, tile: 0.4, shadow: 'none',
  });
  // The stain it has been making for twenty years.
  b.solid('concrete', x + nx * 0.02, (y0 + y1) / 2, z + nz * 0.02, 0.42, h * 0.9, 0.02, {
    tile: 0.9, rotY, color: shade(0x6f6a5e, 0.66), collide: false, shadow: 'none',
  });
}

/** Meter box / junction box with a conduit stub. */
export function conduitBox(b, rng, x, y, z, rotY = 0) {
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  const col = shade(rng() < 0.5 ? 0x8a8479 : 0x6b6a5e, 0.8);
  b.solid('metalPainted', x + nx * 0.09, y, z + nz * 0.09, 0.36, 0.44, 0.18, {
    tile: 0.3, rotY, color: col, collide: false, shadow: 'cast',
  });
  tube(b, 'metalPainted', x + nx * 0.05, y - 0.5, z + nz * 0.05, 0.022, 0.7, 0, 0, 0, {
    color: shade(0x4a453e, 0.5), seg: 5, shadow: 'none',
  });
}

/**
 * Surviving render on a masonry wall.
 *
 * This used to be authored the other way round — a patch of a *different* material
 * pasted onto the wall — and the direction matters. Painting brick onto corrugated
 * steel produces a floating red rectangle that reads as a texture bug, and painting
 * grey onto brick produces a grey sticker. What actually happens on a rendered wall
 * is that the render survives in islands and falls off in sheets, so the patch is
 * pale plaster standing *proud* of the brick, with a shadowed broken edge and the
 * odd scar where the last of it let go. Masonry only; steel gets `panelStain`.
 */
export function wallPatch(b, rng, x, y, z, rotY = 0, o = {}) {
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  const at = (out, side, up) => [x + nx * out + nz * side, y + up, z + nz * out - nx * side];
  // Two or three overlapping lobes: a single rectangle reads as a decal stuck on the
  // wall, which is worse than no patch at all.
  const lobes = 2 + Math.floor(rng() * 2);
  const w0 = 0.6 + rng() * 1.3;
  const h0 = 0.5 + rng() * 1.1;
  const col = vary(rng, o.hex ?? 0xb8ae9a, 0.08, 0.02, { sat: 0.4 });
  // No `tile` overrides anywhere in here. A patch of render is *the same material at
  // the same scale as the wall it is on* — the moment it carries its own repeat it
  // stops being a patch and becomes a decal, which is the exact bug this function
  // used to have in brick. Omitting the option lets `solid` take the density from
  // SURFACE_TEXEL, which is derived from the forge's own feature counts.
  for (let i = 0; i < lobes; i++) {
    const w = w0 * (0.55 + rng() * 0.6);
    const h = h0 * (0.55 + rng() * 0.6);
    const sx = (rng() - 0.5) * w0 * 0.85;
    const sy = (rng() - 0.5) * h0 * 0.85;
    const [px, py, pz] = at(0.026, sx, sy);
    // Proud, and casting: the shadow under the broken edge is the entire effect.
    b.solid('concrete', px, py, pz, w, h, 0.05, {
      rotY, color: col, collide: false, shadow: 'cast',
      // Slide the UVs instead of rescaling them, so each lobe shows a different part
      // of the same aggregate rather than a different-sized aggregate.
      uOff: rng() * 6, vOff: rng() * 6,
    });
  }
  // Scars where the last of the render came away, and the dust it left below.
  for (let i = 0; i < 2 + Math.floor(rng() * 3); i++) {
    const [px, py, pz] = at(0.016, (rng() - 0.5) * w0 * 1.3, (rng() - 0.5) * h0 * 1.3);
    b.solid('concrete', px, py, pz, 0.1 + rng() * 0.3, 0.08 + rng() * 0.22, 0.02, {
      rotY, color: vary(rng, 0x7d7466, 0.12, 0.02, { lum: 0.62 }),
      collide: false, shadow: 'none', uOff: rng() * 6, vOff: rng() * 6,
    });
  }
}

/**
 * The equivalent for a steel-clad wall, which fails completely differently: it does
 * not shed render, it dents, it streaks rust down from every fixing, and somebody
 * eventually rivets a mismatched sheet over the worst of it.
 */
export function panelStain(b, rng, x, y, z, rotY = 0, o = {}) {
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  const at = (out, side, up) => [x + nx * out + nz * side, y + up, z + nz * out - nx * side];
  const roll = rng();
  if (roll < 0.42) {
    // Rust run from a fixing: narrow, long, and always vertical.
    const h = 0.7 + rng() * (o.drop ?? 1.8);
    const [px, py, pz] = at(0.014, (rng() - 0.5) * 0.5, -h / 2);
    b.solid('metalPainted', px, py, pz, 0.05 + rng() * 0.09, h, 0.012, {
      tile: 0.5, rotY, color: shade(0x6e3b1e, 0.42, 0.95), collide: false, shadow: 'none',
    });
    const [hx, hy, hz] = at(0.02, 0, 0.02);
    b.solid('metalPainted', hx, hy, hz, 0.07, 0.07, 0.03, {
      tile: 0.2, rotY, color: shade(0x5a4030, 0.5, 0.9), collide: false, shadow: 'none',
    });
  } else if (roll < 0.68) {
    // Replacement sheet: right profile, right size (cladding comes in 1 m sheets),
    // wrong age. Big pale rectangles would just be the old brick-decal bug again in
    // a different colour, so it stays close to the wall in tone.
    const w = 0.62 + rng() * 0.45;
    const h = 0.7 + rng() * 1.1;
    const [px, py, pz] = at(0.028, (rng() - 0.5) * 0.4, 0);
    b.solid('corrugated', px, py, pz, w, h, 0.045, {
      rotY, color: vary(rng, [0x8a8478, 0x7d857e, 0x8f8878][Math.floor(rng() * 3)], 0.07, 0.02, { lum: 0.9 }),
      collide: false, shadow: 'cast',
    });
    // Fixings along its edge — the thing that says "riveted on later".
    for (let k = 0; k < 4; k++) {
      const [fx, fy, fz] = at(0.05, (rng() - 0.5) * 0.4 + (k % 2 ? w : -w) * 0.42, (k < 2 ? h : -h) * 0.42);
      b.solid('metalPainted', fx, fy, fz, 0.05, 0.05, 0.02, {
        tile: 0.12, rotY, color: shade(0x5a4030, 0.5), collide: false, shadow: 'none',
      });
    }
  } else {
    // A dent: a shallow panel pushed in, catching the light along one edge only.
    const w = 0.4 + rng() * 0.6;
    const [px, py, pz] = at(-0.02, (rng() - 0.5) * 0.5, 0);
    b.solid('corrugated', px, py, pz, w, w * (0.5 + rng() * 0.8), 0.06, {
      rotY: rotY + (rng() - 0.5) * 0.06, color: shade(0x8e8a7c, 0.78),
      collide: false, shadow: 'none',
    });
  }
}

/** Short concrete bollard. */
export function bollard(b, rng, x, z) {
  const col = vary(rng, 0xa8a091, 0.07);
  b.post('concrete', x, 0.42, z, 0.11, 0.84, { seg: 8, color: col });
  b.post('concrete', x, 0.86, z, 0.13, 0.06, { seg: 8, color: col.clone().multiplyScalar(0.9), collide: false });
}

/**
 * Painted road marking.
 *
 * This was drawn on the `concrete` surface with a bright tint, which is why it came
 * out factory-fresh: it was not paint at all, it was a clean cast strip laid on the
 * road, carrying its own aggregate at its own scale and therefore reading as new
 * concrete rather than as old thermoplastic.
 *
 * Paint is not a material of its own — it is a thin film over whatever is underneath,
 * so the road's aggregate profile telegraphs straight through it. Drawing the marking
 * on `asphalt` at the same texel density as the carriageway puts the film in register
 * with the surface it sits on, and then the wear can be authored on top: each dash
 * broken into sub-segments, the middle scrubbed pale by tyres, ends feathered, and
 * whole stretches scraped back to nothing.
 */
export function roadMark(b, rng, x, z, len, axis = 'z', o = {}) {
  const alongZ = axis === 'z';
  const w = o.w ?? 0.16;
  const dash = o.dash ?? 1.5;
  const segs = Math.max(1, Math.round(len / 2.4));
  const y = (o.y ?? 0.02) + 0.014;
  const put = (t, l, ww, yy, col) => {
    b.solid('asphalt', alongZ ? x : x + t, yy, alongZ ? z + t : z,
      alongZ ? ww : l, 0.016, alongZ ? l : ww,
      { color: col, collide: false, shadow: 'none', uOff: rng() * 8, vOff: rng() * 8 });
  };
  for (let i = 0; i < segs; i++) {
    // Whole dashes missing: scraped off by a plough, a shovel, or a fire.
    if (rng() < 0.2) continue;
    const t0 = -len / 2 + (len * (i + 0.5)) / segs;
    // Age varies down the run — a line is repainted in patches, never all at once.
    const age = 0.45 + rng() * 0.55;
    const bright = shade(0xd8d2c2, 0.85 + age * 0.75);
    // Each dash is two or three pieces with gaps, not one clean rectangle.
    const pieces = 2 + Math.floor(rng() * 2);
    let cursor = -dash / 2;
    for (let k = 0; k < pieces; k++) {
      const l = (dash / pieces) * (0.6 + rng() * 0.5);
      if (rng() > 0.14) {
        put(t0 + cursor + l / 2, l, w * (0.8 + rng() * 0.35), y, bright);
      }
      cursor += (dash / pieces);
    }
    // The tyre-polished core: wheels track the line and burnish the middle of it
    // down to almost nothing, so the paint survives at the edges and not the centre.
    if (rng() < 0.55) {
      put(t0 + (rng() - 0.5) * 0.4, dash * (0.3 + rng() * 0.35), w * 0.55, y + 0.004,
        shade(0x8d8578, 0.7 + rng() * 0.25));
    }
    // Feathered ends, where the film has worn thin rather than stopped.
    for (const s of [-1, 1]) {
      if (rng() < 0.45) {
        put(t0 + s * (dash / 2 + 0.12), 0.24 + rng() * 0.2, w * (0.5 + rng() * 0.4), y - 0.002,
          shade(0xbdb5a4, 0.72 + rng() * 0.3));
      }
    }
    // Overspray and scuff either side of the line.
    if (rng() < 0.3) {
      const side = (rng() < 0.5 ? -1 : 1) * (w * 0.7 + rng() * 0.14);
      b.solid('asphalt', alongZ ? x + side : x + t0 + (rng() - 0.5) * dash, y - 0.004,
        alongZ ? z + t0 + (rng() - 0.5) * dash : z + side,
        alongZ ? 0.06 : 0.3, 0.014, alongZ ? 0.3 : 0.06,
        { color: shade(0xb0a898, 0.78), collide: false, shadow: 'none', uOff: rng() * 8, vOff: rng() * 8 });
    }
  }
}

/** Cast-iron manhole or gully cover. */
export function manhole(b, rng, x, z) {
  b.add('metalPainted', cylGeo(0.34, 0.34, 0.05, 12, 0.4), trs(x, 0.035, z, 0, rng() * 3, 0),
    shade(0x5f584d, 0.6), 'none');
}

/** Wind-blown litter: paper, rag, a bottle, a flattened can. */
export function litter(b, rng, x, z) {
  const roll = rng();
  if (roll < 0.4) {
    b.add('fabric', boxGeo(0.16 + rng() * 0.16, 0.01, 0.12 + rng() * 0.14, 0.3),
      trs(x, 0.028, z, (rng() - 0.5) * 0.3, rng() * 3, (rng() - 0.5) * 0.3),
      shade(0xcfc6b2, 1.25), 'none');
  } else if (roll < 0.7) {
    b.add('fabric', boxGeo(0.3 + rng() * 0.3, 0.02, 0.22 + rng() * 0.2, 0.4),
      trs(x, 0.03, z, (rng() - 0.5) * 0.4, rng() * 3, (rng() - 0.5) * 0.4),
      vary(rng, [0xb0563c, 0x2f6070, 0x9c8a4a][Math.floor(rng() * 3)], 0.16, 0.04, { sat: 0.8 }), 'none');
  } else if (roll < 0.88) {
    b.add('glass', cylGeo(0.035, 0.045, 0.24, 7, 0.3), trs(x, 0.045, z, Math.PI / 2, rng() * 3, 0),
      shade(0x6f8a5e, 0.7, 0.7), 'none');
  } else {
    b.add('metalPainted', cylGeo(0.033, 0.033, 0.11, 8, 0.25), trs(x, 0.02, z, Math.PI / 2, rng() * 3, 0),
      shade(0x9aa0a6, 0.9), 'none');
  }
}

/* -------------------------------------------------------------------------- */
/*                              the surface kit                                */
/* -------------------------------------------------------------------------- */

/**
 * Everything below is authored against a *face*, not a point, and that is the whole
 * idea. The failure this map had was not a shortage of props: it was that a 24 m
 * elevation carried one door, one pipe and four decals, because every previous
 * detail pass worked one hand-placed object at a time. A run-length kit gets applied
 * to a whole elevation in one line and scales with it.
 *
 * A face is `{ axis, at, from, to, rotY }`: `axis` is the direction the wall runs,
 * `at` its position on the other axis, and `rotY` the outward normal's yaw.
 */
function faceAt(face, t, out) {
  const nx = Math.sin(face.rotY);
  const nz = Math.cos(face.rotY);
  return face.axis === 'x'
    ? [face.from + t + (face.slide ?? 0), face.at + nz * out]
    : [face.at + nx * out, face.from + t + (face.slide ?? 0)];
}

/**
 * Plinth / base flashing where cladding meets ground, plus the splash zone above it.
 *
 * No building anywhere just stops at the dirt. The junction is always detailed —
 * because if it were not, water would wick straight up the wall — and its absence is
 * one of the reasons a blockout reads as cardboard pushed into sand however much
 * clutter you scatter in front of it.
 */
export function basePlinth(b, rng, face, o = {}) {
  const len = face.to - face.from;
  const h = o.h ?? 0.42;
  const out = o.out ?? 0.11;
  const hex = o.hex ?? 0x8d8578;
  const put = (t, l, yc, hh, deep, opts) => {
    const [px, pz] = faceAt(face, t + l / 2, deep / 2);
    if (face.axis === 'x') b.solid(opts.surface ?? 'concrete', px, yc, pz, l, hh, deep, opts);
    else b.solid(opts.surface ?? 'concrete', px, yc, pz, deep, hh, l, opts);
  };
  // The plinth itself, in bays with the odd one spalled away.
  const bays = Math.max(1, Math.round(len / 3.4));
  for (let i = 0; i < bays; i++) {
    const t = (i * len) / bays;
    const l = len / bays - 0.06;
    const broken = rng() < 0.16;
    put(t, l, (broken ? h * 0.55 : h) / 2, broken ? h * 0.55 : h, out * 2, {
      surface: 'concrete', rotY: face.rotY, color: vary(rng, hex, 0.08),
      collide: false, shadow: 'cast',
    });
  }
  // Weathering band: the wall above a plinth is always darker where it wets.
  put(0, len, h + 0.26, 0.52, out * 1.1, {
    surface: 'concrete', rotY: face.rotY, color: shade(0x6f6a5e, 0.72),
    collide: false, shadow: 'none',
  });
  // Sand drift, spoil and weeds along the foot.
  const stations = Math.max(2, Math.round(len / 1.7));
  for (let i = 0; i < stations; i++) {
    const t = (len * (i + 0.4 + rng() * 0.4)) / stations;
    const [px, pz] = faceAt(face, t, out + 0.28 + rng() * 0.5);
    if (rng() < 0.75) {
      const w = 0.6 + rng() * 1.4;
      b.solid('dirt', px, 0.028, pz, w, 0.05, w * (0.35 + rng() * 0.4), {
        rotY: face.rotY + (rng() - 0.5) * 0.5, uOff: rng() * 6, vOff: rng() * 6,
        color: vary(rng, 0xbdb49e, 0.1, 0.02, { sat: 0.5 }), collide: false, shadow: 'none',
      });
    }
    if (rng() < 0.4) plant(b, rng, px, 0.03, pz, 0.45 + rng() * 0.6);
  }
}

/** Eaves gutter with brackets, joints and a stop end — the thing downpipes need. */
export function eavesGutter(b, rng, face, y, o = {}) {
  const len = face.to - face.from;
  const out = o.out ?? 0.2;
  const col = o.color ?? shade(0x6a6255, 0.6);
  const put = (t, l, yc, hh, deep, opts) => {
    const [px, pz] = faceAt(face, t + l / 2, out);
    if (face.axis === 'x') b.solid(opts.surface ?? 'metalPainted', px, yc, pz, l, hh, deep, opts);
    else b.solid(opts.surface ?? 'metalPainted', px, yc, pz, deep, hh, l, opts);
  };
  // Sections with visible joints, sagging very slightly between brackets.
  const secs = Math.max(1, Math.round(len / 3.0));
  for (let i = 0; i < secs; i++) {
    const t = (i * len) / secs;
    const l = len / secs - 0.04;
    put(t, l, y - (i % 2) * 0.012, 0.13, 0.16, {
      tile: 0.5, rotY: face.rotY, color: col, collide: false, shadow: 'cast',
    });
    put(t + l, 0.07, y, 0.16, 0.19, {
      tile: 0.2, rotY: face.rotY, color: col.clone().multiplyScalar(0.85), collide: false, shadow: 'none',
    });
  }
  // Fascia behind it, so the gutter hangs off something.
  put(0, len, y + 0.16, 0.2, 0.06, {
    surface: 'wood', rotY: face.rotY, color: vary(rng, 0x7a6a52, 0.1), collide: false, shadow: 'cast',
  });
  // Brackets.
  const n = Math.max(2, Math.round(len / 1.2));
  for (let i = 0; i <= n; i++) {
    put((len * i) / n, 0.04, y - 0.02, 0.1, 0.22, {
      tile: 0.15, rotY: face.rotY, color: col.clone().multiplyScalar(0.8), collide: false, shadow: 'none',
    });
  }
  // Outlets: where the downpipes you already have actually get their water.
  for (const t of o.outlets ?? []) {
    const [px, pz] = faceAt(face, t, out);
    b.add('metalPainted', cylGeo(0.075, 0.055, 0.2, 8, 0.3), trs(px, y - 0.14, pz), col, 'none');
  }
}

/** Wall ladder with back hoops — reads as access, and as height. */
export function wallLadder(b, x, z, y0, y1, rotY = 0, o = {}) {
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  const tx = nz;
  const tz = -nx;
  const col = o.color ?? shade(0x6a6255, 0.55);
  const w = o.w ?? 0.46;
  for (const s of [-1, 1]) {
    tube(b, 'metalPainted', x + nx * 0.17 + tx * s * w / 2, (y0 + y1) / 2, z + nz * 0.17 + tz * s * w / 2,
      0.028, y1 - y0, 0, 0, 0, { color: col, seg: 6, shadow: 'cast' });
  }
  const rungs = Math.max(2, Math.floor((y1 - y0) / 0.3));
  for (let i = 0; i <= rungs; i++) {
    const ry = y0 + ((y1 - y0) * i) / rungs;
    b.solid('metalPainted', x + nx * 0.17, ry, z + nz * 0.17, Math.abs(tx) * w + 0.026, 0.024, Math.abs(tz) * w + 0.026, {
      tile: 0.2, color: col, collide: false, shadow: 'none',
    });
    // Wall fixings every fourth rung.
    if (i % 4 === 2) {
      for (const s of [-1, 1]) {
        b.solid('metalPainted', x + nx * 0.085 + tx * s * w / 2, ry, z + nz * 0.085 + tz * s * w / 2, 0.06, 0.05, 0.06,
          { tile: 0.15, rotY, color: col.clone().multiplyScalar(0.8), collide: false, shadow: 'none' });
      }
    }
  }
  // Safety hoops over the upper half.
  const hoopFrom = y0 + (y1 - y0) * 0.45;
  for (let hy = hoopFrom; hy < y1 - 0.2; hy += 0.7) {
    for (const a of [-1, -0.5, 0, 0.5, 1]) {
      const ax = nx * (0.17 + Math.cos(a * 1.3) * 0.36) + tx * Math.sin(a * 1.3) * 0.36;
      const az = nz * (0.17 + Math.cos(a * 1.3) * 0.36) + tz * Math.sin(a * 1.3) * 0.36;
      b.solid('metalPainted', x + ax, hy, z + az, 0.09, 0.02, 0.09, {
        tile: 0.1, rotY, color: col, collide: false, shadow: 'none',
      });
    }
  }
}

/** Louvred vent panel: blades in a frame, with a dark plenum behind. */
export function ventLouvre(b, rng, x, y, z, w, h, rotY = 0, o = {}) {
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  const col = o.color ?? shade(0x8a8479, 0.78);
  b.solid('metalPainted', x + nx * 0.01, y, z + nz * 0.01, w, h, 0.03, {
    tile: 0.4, rotY, color: shade(0x2a2724, 0.24), collide: false, shadow: 'none',
  });
  const blades = Math.max(3, Math.round(h / 0.13));
  for (let i = 0; i < blades; i++) {
    const by = y - h / 2 + (h * (i + 0.5)) / blades;
    angled(b, 'metalPainted', x + nx * 0.055, by, z + nz * 0.055, w - 0.05, 0.085, 0.05,
      nz * 0.55, rotY, -nx * 0.55, { tile: 0.2, color: col, shadow: 'none' });
  }
  // Frame.
  for (const [ox, oy, fw, fh] of [[0, h / 2 + 0.03, w + 0.1, 0.07], [0, -h / 2 - 0.03, w + 0.1, 0.07]]) {
    b.solid('metalPainted', x + nx * 0.05 + nz * ox, y + oy, z + nz * 0.05 - nx * ox, fw, fh, 0.09, {
      tile: 0.2, rotY, color: col.clone().multiplyScalar(0.85), collide: false, shadow: 'cast',
    });
  }
  for (const s of [-1, 1]) {
    b.solid('metalPainted', x + nx * 0.05 + nz * s * (w / 2 + 0.04), y, z + nz * 0.05 - nx * s * (w / 2 + 0.04), 0.08, h + 0.1, 0.09, {
      tile: 0.2, rotY, color: col.clone().multiplyScalar(0.85), collide: false, shadow: 'cast',
    });
  }
}

/** Extract cowl on a short duct — roof-line silhouette for an industrial shed. */
export function extractCowl(b, rng, x, y, z, o = {}) {
  const r = o.r ?? 0.3;
  const col = o.color ?? shade(0x8a8479, 0.72);
  b.add('metalPainted', cylGeo(r * 0.8, r * 0.8, o.duct ?? 0.7, 10, 0.6), trs(x, y + (o.duct ?? 0.7) / 2, z), col, 'cast');
  const ty = y + (o.duct ?? 0.7);
  b.add('metalPainted', cylGeo(r * 1.25, r * 0.9, 0.16, 10, 0.5), trs(x, ty + 0.08, z), col.clone().multiplyScalar(0.9), 'cast');
  // Rotating cowl head: a truncated cone with a cap and vanes.
  b.add('metalPainted', cylGeo(r * 0.35, r * 1.2, 0.42, 10, 0.6), trs(x, ty + 0.38, z), col, 'cast');
  b.add('metalPainted', cylGeo(r * 0.55, r * 0.15, 0.14, 10, 0.4), trs(x, ty + 0.64, z), col.clone().multiplyScalar(1.08), 'cast');
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    b.solid('metalPainted', x + Math.cos(a) * r * 0.8, ty + 0.38, z + Math.sin(a) * r * 0.8, 0.03, 0.36, 0.16, {
      tile: 0.15, rotY: -a, color: col.clone().multiplyScalar(0.86), collide: false, shadow: 'none',
    });
  }
}

/** A big painted numeral on a wall — loading-dock bay numbers. */
export function dockNumber(b, rng, x, y, z, rotY = 0, digits = 1, o = {}) {
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  const size = o.size ?? 0.8;
  const cw = size * 0.22;
  const ch = size * 0.22;
  const col = o.color ?? shade(0xe4dcc6, 1.4, 0.2);
  for (let g = 0; g < digits; g++) {
    const bits = GLYPHS[Math.floor(rng() * 10)];
    const gx = (g - (digits - 1) / 2) * size * 0.82;
    for (let r = 0; r < 5; r++) {
      for (let cN = 0; cN < 3; cN++) {
        if (!(bits & (1 << (14 - (r * 3 + cN))))) continue;
        const side = gx + (cN - 1) * cw * 1.04;
        b.solid('concrete', x + nx * 0.02 + nz * side, y + (2 - r) * ch * 1.04, z + nz * 0.02 - nx * side, cw, ch, 0.02, {
          rotY, color: col, collide: false, shadow: 'none', uOff: rng() * 4, vOff: rng() * 4,
        });
      }
    }
  }
}

/** External corner angle — trim, and a hard vertical highlight on a flat corner. */
export function cornerTrim(b, x, z, y0, y1, o = {}) {
  const col = o.color ?? tint(0x8f8778);
  const w = o.w ?? 0.16;
  b.solid('metalPainted', x, (y0 + y1) / 2, z, w, y1 - y0, w, {
    tile: 0.6, rotY: o.rotY ?? 0, color: col, collide: false, shadow: 'cast',
  });
}

/** A dark stain soaked into the ground — oil, water, or twenty years of both. */
export function groundStain(b, rng, x, z, r, o = {}) {
  const lobes = 2 + Math.floor(rng() * 3);
  for (let i = 0; i < lobes; i++) {
    const w = r * (0.7 + rng() * 0.8);
    b.solid(o.surface ?? 'concrete', x + (rng() - 0.5) * r, (o.y ?? 0) + 0.014 + i * 0.002, z + (rng() - 0.5) * r,
      w, 0.02, w * (0.5 + rng() * 0.8), {
        rotY: rng() * 3, uOff: rng() * 6, vOff: rng() * 6, color: shade(o.hex ?? 0x3d3831, o.lum ?? 0.46),
        collide: false, shadow: 'none',
      });
  }
}

/* -------------------------------------------------------------------------- */
/*                             the interior kit                                */
/* -------------------------------------------------------------------------- */

/** Steel-framed workbench with a timber top, a vice, and the junk on it. */
export function workBench(b, rng, x, z, len, rotY = 0, o = {}) {
  const d = 0.72;
  const h = 0.88;
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const steel = vary(rng, 0x5f6a58, 0.1);
  const top = vary(rng, 0x8a6a44, 0.12);
  b.solid('wood', x, h, z, len, 0.07, d, { rotY, color: top, matId: 'wood', collide: false });
  b.solid('metalPainted', x, h - 0.09, z, len - 0.1, 0.1, d - 0.12, { rotY, color: steel, collide: false, shadow: 'none' });
  const legs = Math.max(2, Math.round(len / 1.6));
  for (let i = 0; i <= legs; i++) {
    for (const sz of [-1, 1]) {
      const [px, pz] = at(-len / 2 + (len * i) / legs, sz * (d / 2 - 0.08));
      b.solid('metalPainted', px, h / 2, pz, 0.07, h, 0.07, { tile: 0.3, rotY, color: steel, collide: false, shadow: 'cast' });
    }
  }
  // Lower shelf with stock on it.
  b.solid('wood', x, 0.24, z, len - 0.1, 0.05, d - 0.16, { rotY, color: top.clone().multiplyScalar(0.85), collide: false, shadow: 'none' });
  const [shw, shd] = rotExtent(len, d, rotY);
  b.collider(x - shw, 0, z - shd, x + shw, h + 0.07, z + shd, 'wood');
  b.markCover(x, z, shw, shd, h + 0.07, 'low');
  // Bench vice at one end — the single most legible workshop object there is.
  const [vx, vz] = at(len / 2 - 0.35, -0.12);
  b.solid('metalPainted', vx, h + 0.11, vz, 0.28, 0.16, 0.14, { tile: 0.15, rotY, color: shade(0x4a5a62, 0.5), collide: false, shadow: 'cast' });
  b.solid('metalPainted', vx - 0.16 * c, h + 0.18, vz + 0.16 * s, 0.1, 0.2, 0.16, { tile: 0.12, rotY, color: shade(0x4a5a62, 0.5), collide: false, shadow: 'none' });
  tube(b, 'metalPainted', vx, h + 0.26, vz, 0.016, 0.34, 0, 0, Math.PI / 2, { color: shade(0x6a655c, 0.6), seg: 5 });
  // Clutter on the top.
  for (let i = 0; i < 3 + Math.floor(rng() * 3); i++) {
    const [px, pz] = at((rng() - 0.5) * (len - 0.7), (rng() - 0.5) * 0.4);
    const roll = rng();
    if (roll < 0.4) {
      b.solid('metalPainted', px, h + 0.09, pz, 0.16 + rng() * 0.2, 0.1, 0.13, {
        tile: 0.2, rotY: rng() * 3, color: vary(rng, 0x7a6a52, 0.16), collide: false, shadow: 'cast',
      });
    } else if (roll < 0.7) {
      b.add('metalPainted', cylGeo(0.05, 0.05, 0.14, 8, 0.3), trs(px, h + 0.11, pz, 0, rng() * 3, 0),
        vary(rng, 0x8a7a3a, 0.14), 'cast');
    } else {
      b.solid('wood', px, h + 0.06, pz, 0.3 + rng() * 0.3, 0.04, 0.2, {
        rotY: rng() * 3, color: vary(rng, 0x9a7c52, 0.14), collide: false, shadow: 'none',
      });
    }
  }
}

/** Pegboard of tools above a bench. Pure silhouette, but unmistakable. */
export function toolBoard(b, rng, x, y, z, w, h, rotY = 0) {
  const nx = Math.sin(rotY);
  const nz = Math.cos(rotY);
  b.solid('wood', x + nx * 0.03, y, z + nz * 0.03, w, h, 0.04, {
    rotY, color: vary(rng, 0x6f5c42, 0.1), collide: false, shadow: 'cast',
  });
  const n = Math.round(w / 0.22);
  for (let i = 0; i < n; i++) {
    const side = -w / 2 + (w * (i + 0.5)) / n;
    if (rng() < 0.22) continue;
    const px = x + nx * 0.07 + nz * side;
    const pz = z + nz * 0.07 - nx * side;
    const roll = rng();
    const col = shade(0x54504a, 0.5);
    if (roll < 0.4) {
      // Spanner: a shaft with a head.
      b.solid('metalPainted', px, y + 0.02, pz, 0.035, 0.22 + rng() * 0.12, 0.02, { tile: 0.1, rotY, color: col, collide: false, shadow: 'none' });
      b.solid('metalPainted', px, y + 0.15, pz, 0.07, 0.07, 0.02, { tile: 0.1, rotY, color: col, collide: false, shadow: 'none' });
    } else if (roll < 0.7) {
      // Hammer / mallet.
      b.solid('wood', px, y - 0.02, pz, 0.03, 0.26, 0.025, { rotY, color: vary(rng, 0x9a7c52, 0.12), collide: false, shadow: 'none' });
      b.solid('metalPainted', px, y + 0.14, pz, 0.13, 0.05, 0.045, { rotY, color: col, collide: false, shadow: 'none' });
    } else {
      // Coil of hose or cable on a hook.
      b.add('polymer', cylGeo(0.11, 0.11, 0.05, 10, 0.3), trs(px, y, pz, Math.PI / 2 * nz, rotY, Math.PI / 2 * nx),
        vary(rng, 0x35404a, 0.16), 'none');
    }
  }
}

/** Painted floor markings: an aisle, or a hatched keep-clear box. */
export function floorMarking(b, rng, x, z, w, d, rotY = 0, o = {}) {
  const y = (o.y ?? 0) + 0.012;
  const col = o.color ?? accent(o.hex ?? 0xd8c23a, { lum: 1.35 });
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  if (o.kind === 'hatch') {
    // 45° hatching, chord length solved so the stripes stay inside the box.
    const step = 0.34;
    const half = (w + d) / 2;
    for (let t = -half; t <= half; t += step) {
      const chord = Math.min(w, d) * Math.SQRT2 - Math.abs(t) * 1.0;
      if (chord < 0.15) continue;
      const [px, pz] = at(t * 0.707, t * 0.707);
      if (rng() < 0.12) continue;
      angled(b, 'concrete', px, y, pz, 0.11, 0.018, chord, 0, rotY + Math.PI / 4, 0,
        { color: col, shadow: 'none' });
    }
    // Border.
    for (const [ox, oz, bw, bd] of [[0, -d / 2, w, 0.1], [0, d / 2, w, 0.1], [-w / 2, 0, 0.1, d], [w / 2, 0, 0.1, d]]) {
      const [px, pz] = at(ox, oz);
      b.solid('concrete', px, y, pz, bw, 0.018, bd, { rotY, color: col, collide: false, shadow: 'none' });
    }
    return;
  }
  // Aisle: two broken parallel lines.
  for (const sz of [-1, 1]) {
    const segs = Math.max(1, Math.round(d / 1.6));
    for (let i = 0; i < segs; i++) {
      if (rng() < 0.14) continue;
      const [px, pz] = at(sz * w / 2, -d / 2 + (d * (i + 0.5)) / segs);
      b.solid('concrete', px, y, pz, 0.1, 0.018, d / segs - 0.08, { rotY, color: col, collide: false, shadow: 'none' });
    }
  }
}

/** Suspended fluorescent batten. The reason an interior has a light source at all. */
export function battenLight(b, x, y, z, len, rotY = 0, o = {}) {
  const col = shade(0x8e8a80, 0.75);
  for (const s of [-1, 1]) {
    const dx = Math.cos(rotY) * s * len * 0.38;
    const dz = -Math.sin(rotY) * s * len * 0.38;
    tube(b, 'metalPainted', x + dx, y + 0.22, z + dz, 0.008, 0.44, 0, 0, 0, { color: shade(0x2a2724, 0.3), seg: 4, shadow: 'none' });
  }
  b.solid('metalPainted', x, y, z, len, 0.08, 0.12, { tile: 0.5, rotY, color: col, collide: false, shadow: 'cast' });
  b.add('glass', boxGeo(len - 0.1, 0.05, 0.09, 0.5), trs(x, y - 0.06, z, 0, rotY, 0),
    tint(0xffe6c0, { lum: 1.0 }), 'none', true,
    // Same emissive key as Level._glow on purpose: matOverrides are part of the
    // batch key, so an off-by-one intensity here costs a whole extra draw call.
    { emissive: o.hex ?? 0xffc27a, emissiveIntensity: o.intensity ?? 3.2, toneMapped: true, roughness: 0.9, metalness: 0 });
}

/**
 * Purlins and rafters under a roof deck.
 *
 * A ceiling that is one flat plane is the most common unfinished surface in any
 * level, and it is also the one the player stares at every time they look up in a
 * fight. Structure up there costs almost nothing and reads instantly.
 */
export function purlins(b, x0, x1, z0, z1, y, o = {}) {
  const along = o.along ?? (x1 - x0 > z1 - z0 ? 'x' : 'z');
  const col = o.color ?? tint(0x6e675c);
  const pitch = o.pitch ?? 1.7;
  const depth = o.depth ?? 0.22;
  if (along === 'x') {
    const n = Math.max(1, Math.round((z1 - z0) / pitch));
    for (let i = 0; i <= n; i++) {
      const pz = z0 + ((z1 - z0) * i) / n;
      b.solid('metalPainted', (x0 + x1) / 2, y - depth / 2, pz, x1 - x0, depth, 0.1, {
        tile: 2.0, color: col, collide: false, shadow: 'cast',
      });
      b.solid('metalPainted', (x0 + x1) / 2, y - depth, pz, x1 - x0, 0.05, 0.2, {
        tile: 1.5, color: col.clone().multiplyScalar(0.9), collide: false, shadow: 'none',
      });
    }
    // Secondary members running the other way.
    const m = Math.max(1, Math.round((x1 - x0) / 5.0));
    for (let i = 0; i <= m; i++) {
      const px = x0 + ((x1 - x0) * i) / m;
      b.solid('metalPainted', px, y - depth * 1.5, (z0 + z1) / 2, 0.12, 0.3, z1 - z0, {
        tile: 2.0, color: col.clone().multiplyScalar(0.94), collide: false, shadow: 'cast',
      });
    }
  } else {
    const n = Math.max(1, Math.round((x1 - x0) / pitch));
    for (let i = 0; i <= n; i++) {
      const px = x0 + ((x1 - x0) * i) / n;
      b.solid('metalPainted', px, y - depth / 2, (z0 + z1) / 2, 0.1, depth, z1 - z0, {
        tile: 2.0, color: col, collide: false, shadow: 'cast',
      });
    }
    const m = Math.max(1, Math.round((z1 - z0) / 5.0));
    for (let i = 0; i <= m; i++) {
      const pz = z0 + ((z1 - z0) * i) / m;
      b.solid('metalPainted', (x0 + x1) / 2, y - depth * 1.5, pz, x1 - x0, 0.3, 0.12, {
        tile: 2.0, color: col.clone().multiplyScalar(0.94), collide: false, shadow: 'cast',
      });
    }
  }
}

/** Inspection pit: a recessed rectangle with a kerb, a ladder and a dark bottom. */
export function inspectionPit(b, rng, x, z, w, d, rotY = 0, o = {}) {
  const y = o.y ?? 0;
  const depth = o.depth ?? 1.2;
  const col = tint(0x8f8778);
  // The void, faked as a dark box sunk just below the slab. Cutting a real hole in
  // an AABB collision world costs a walkable trap for the player and buys nothing.
  b.solid('concrete', x, y - 0.06, z, w - 0.2, 0.1, d - 0.2, {
    rotY, color: shade(0x241f1a, 0.16), collide: false, shadow: 'none',
  });
  for (let i = 0; i < 4; i++) {
    b.solid('concrete', x, y - 0.02 - i * 0.02, z, w - 0.2 - i * 0.12, 0.02, d - 0.2 - i * 0.1, {
      rotY, color: shade(0x2e2822, 0.1 + i * 0.02), collide: false, shadow: 'none',
    });
  }
  // Kerb around the lip.
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  for (const [ox, oz, bw, bd] of [[0, -d / 2, w + 0.2, 0.2], [0, d / 2, w + 0.2, 0.2], [-w / 2, 0, 0.2, d], [w / 2, 0, 0.2, d]]) {
    b.solid('concrete', x + ox * c + oz * s, y + 0.05, z - ox * s + oz * c, bw, 0.1, bd, {
      rotY, color: col, collide: false, shadow: 'cast',
    });
  }
  // A plank thrown across it, and the oil in the bottom.
  b.solid('wood', x, y + 0.11, z, 0.34, 0.05, d + 0.3, {
    rotY: rotY + 0.06, color: vary(rng, 0x9a7c52, 0.12), collide: false, shadow: 'cast',
  });
  groundStain(b, rng, x + (rng() - 0.5) * w, z + (rng() - 0.5) * d, 0.8, { y });
}

/* -------------------------------------------------------------------------- */
/*                                 roof kit                                    */
/* -------------------------------------------------------------------------- */

/**
 * Felt roof covering: overlapping sheets, capped seams, a fall toward an outlet,
 * upstand flashing where it turns up the parapet, and the outlet itself.
 *
 * Roofs are this map's verticality, which makes them the surface the player spends
 * the most time standing on and looking across — and they were flat untextured
 * slabs. A roof with no fall and no outlet does not read as a roof; it reads as the
 * top of a box.
 */
export function roofDeck(b, rng, x0, x1, z0, z1, y, o = {}) {
  // Surface choice, not a tile override. Roofing felt is bitumen with a mineral chip
  // rolled into it — the same thing the road is made of — so `asphalt` carries it at
  // its own derived density and needs no per-call fudging. It was on `fabric`, which
  // is jute sacking at 0.18 m, and only looked plausible because every call stretched
  // it by a factor of three to seven.
  const col = o.color ?? shade(0x55514a, 0.6);
  const seam = o.seam ?? 0.95;
  const outX = o.outletX ?? (x0 + x1) / 2;
  const outZ = o.outletZ ?? z1 - 1.2;
  // Sheets, laid in bays, each very slightly different in tone and lap.
  const bays = Math.max(1, Math.round((x1 - x0) / seam));
  for (let i = 0; i < bays; i++) {
    const a = x0 + ((x1 - x0) * i) / bays;
    const bw = (x1 - x0) / bays;
    b.solid('asphalt', a + bw / 2, y + 0.014, (z0 + z1) / 2, bw - 0.01, 0.028, z1 - z0, {
      color: vary(rng, 0x5c574e, 0.12, 0.02, { sat: 0.5 }), collide: false, shadow: 'none',
      uOff: rng() * 8, vOff: rng() * 8,
    });
    // Capped seam over each lap.
    if (i > 0) {
      b.solid('asphalt', a, y + 0.032, (z0 + z1) / 2, 0.1, 0.03, z1 - z0, {
        color: col, collide: false, shadow: 'cast',
      });
    }
  }
  // The fall: shallow stepped screed toward the outlet, which is what makes water
  // go somewhere instead of standing in a puddle the size of the building.
  for (let i = 0; i < 4; i++) {
    const t = 1 - i / 4;
    b.solid('concrete', outX, y + 0.012 + i * 0.014, outZ, (x1 - x0) * t * 0.8, 0.02, (z1 - z0) * t * 0.5, {
      color: shade(0x5f5a52, 0.62), collide: false, shadow: 'none',
    });
  }
  // Upstand flashing where the covering turns up the parapet.
  const h = o.upstand ?? 0.28;
  for (const [ax, az, aw, ad] of [[(x0 + x1) / 2, z0, x1 - x0, 0.1], [(x0 + x1) / 2, z1, x1 - x0, 0.1],
                                  [x0, (z0 + z1) / 2, 0.1, z1 - z0], [x1, (z0 + z1) / 2, 0.1, z1 - z0]]) {
    b.solid('asphalt', ax, y + h / 2, az, aw, h, ad, { color: col, collide: false, shadow: 'cast' });
    b.solid('metalPainted', ax, y + h + 0.02, az, aw + 0.02, 0.05, ad + 0.06, {
      color: shade(0x8a8479, 0.85), collide: false, shadow: 'cast',
    });
  }
  // Rainwater outlet with a leaf guard, sitting in its sump.
  b.solid('concrete', outX, y + 0.008, outZ, 0.62, 0.03, 0.62, {
    color: shade(0x4a453e, 0.42), collide: false, shadow: 'none',
  });
  b.add('metalPainted', cylGeo(0.15, 0.15, 0.07, 10, 0.3), trs(outX, y + 0.03, outZ), shade(0x5f584d, 0.5), 'none');
  b.add('metalPainted', cylGeo(0.03, 0.11, 0.22, 8, 0.3), trs(outX, y + 0.14, outZ), shade(0x6a655c, 0.55), 'cast');
  groundStain(b, rng, outX, outZ, 1.1, { y, hex: 0x3a352c, lum: 0.42 });
}

/**
 * Saw-tooth roof monitor: a kerb upstand, a pitched glazed face and a solid back.
 *
 * A 4 cm slab of glass lying flat on the deck is a tarpaulin, not a skylight. The
 * upstand is the part that does the work — it is what stops water getting in, and
 * visually it is what makes the roof have a north light rather than a shiny puddle.
 */
export function sawtoothMonitor(b, rng, x0, x1, z, y, o = {}) {
  const depth = o.depth ?? 1.7;
  const rise = o.rise ?? 1.1;
  const kerb = o.kerb ?? 0.3;
  const frame = o.color ?? shade(0x6f6a5e, 0.62);
  const w = x1 - x0;
  const cx = (x0 + x1) / 2;
  // Kerb upstand all the way round.
  b.solid('concrete', cx, y + kerb / 2, z, w + 0.16, kerb, 0.16, { color: tint(0x8f8778), collide: false, shadow: 'cast' });
  b.solid('concrete', cx, y + kerb / 2, z + depth, w + 0.16, kerb, 0.16, { color: tint(0x8f8778), collide: false, shadow: 'cast' });
  for (const s of [-1, 1]) {
    // Triangular gable cheek: slices stepping down from the high back edge to the
    // low front one, which is what gives the monitor its wedge in silhouette.
    const slices = 6;
    for (let i = 0; i < slices; i++) {
      const t = (i + 0.5) / slices;
      const hTop = kerb + rise * (1 - t);
      b.solid('concrete', cx + s * (w / 2 + 0.05), y + hTop / 2, z + depth * t,
        0.12, hTop, depth / slices + 0.01, {
          color: tint(0x968e80), collide: false, shadow: 'cast',
        });
    }
    // Vertical back post.
    b.solid('metalPainted', cx + s * (w / 2 + 0.05), y + kerb + rise / 2, z + 0.08, 0.1, rise, 0.14, {
      tile: 0.4, color: frame, collide: false, shadow: 'cast',
    });
  }
  // The glazed slope itself, pitched from the high back edge down to the low front.
  const slope = Math.atan2(rise, depth);
  const len = Math.hypot(rise, depth);
  angled(b, 'glass', cx, y + kerb + rise / 2 + 0.02, z + depth / 2, w, 0.06, len, slope, 0, 0, {
    color: tint(0x9fb4bd, { lum: 0.9 }), shadow: 'none',
  });
  // Glazing bars across the slope — without them a skylight is a mirror, not a
  // window — plus transoms up it, because a 2 m pane does not exist in a factory.
  const bars = Math.max(2, Math.round(w / 0.95));
  for (let i = 0; i <= bars; i++) {
    const px = x0 + (w * i) / bars;
    angled(b, 'metalPainted', px, y + kerb + rise / 2 + 0.06, z + depth / 2, 0.07, 0.07, len, slope, 0, 0,
      { tile: 0.3, color: frame, shadow: 'cast' });
  }
  for (const t of [0.34, 0.67]) {
    angled(b, 'metalPainted', cx, y + kerb + rise * (1 - t) + 0.06, z + depth * t, w, 0.06, 0.07, slope, 0, 0,
      { tile: 0.4, color: frame, shadow: 'cast' });
  }
  // Solid opaque back, so the monitor has a dark side and a light side.
  b.solid('corrugated', cx, y + kerb + rise / 2, z + 0.02, w, rise, 0.1, {
    tile: TEXEL.corrugated, color: shade(0x7d7668, 0.7), collide: false, shadow: 'cast',
  });
  b.solid('metalPainted', cx, y + kerb + rise + 0.05, z + depth / 2, w + 0.2, 0.09, 0.24, {
    tile: 0.6, color: frame.clone().multiplyScalar(1.1), collide: false, shadow: 'cast',
  });
  // A pane or two broken out.
  for (let i = 0; i < 2; i++) {
    if (rng() > 0.45) continue;
    const px = x0 + w * (0.15 + rng() * 0.7);
    angled(b, 'concrete', px, y + kerb + rise * 0.55, z + depth * 0.5, w / bars * 0.8, 0.05, len * 0.5,
      slope, 0, 0, { color: shade(0x2a2620, 0.2), shadow: 'none' });
  }
}

/** Header tank on a stand — a rooftop's tallest, most legible silhouette element. */
export function tankStand(b, rng, x, y, z, o = {}) {
  const h = o.h ?? 1.5;
  const r = o.r ?? 0.68;
  const col = shade(0x6a6255, 0.6);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const px = x + sx * r * 0.72;
      const pz = z + sz * r * 0.72;
      b.solid('metalPainted', px, y + h / 2, pz, 0.09, h, 0.09, { tile: 0.4, color: col, collide: false, shadow: 'cast' });
    }
  }
  for (const s of [-1, 1]) {
    angled(b, 'metalPainted', x, y + h / 2, z + s * r * 0.72, r * 2.0, 0.05, 0.05, 0, 0, Math.atan2(h, r * 1.44),
      { tile: 0.3, color: col, shadow: 'none' });
  }
  b.solid('metalPainted', x, y + h + 0.05, z, r * 1.7, 0.1, r * 1.7, { tile: 0.5, color: col, collide: false, shadow: 'cast' });
  b.add('polymer', cylGeo(r, r, 1.0, 12, 0.9), trs(x, y + h + 0.6, z), vary(rng, 0x2c3a44, 0.1), 'both');
  b.add('polymer', cylGeo(r * 0.34, r * 0.34, 0.1, 10, 0.4), trs(x, y + h + 1.14, z), shade(0x243038, 0.32), 'cast');
  tube(b, 'polymer', x + r * 0.8, y + h * 0.5, z, 0.03, h + 0.6, 0, 0, 0, { color: shade(0x9a9488, 0.8), seg: 5 });
  b.collider(x - r, y, z - r, x + r, y + h + 1.1, z + r, 'metal');
  b.markCover(x, z, r, r, y + h + 1.1, 'high', y);
}

/** Guyed aerial mast base — a plate, bolts, three stays and a whip. */
export function aerialBase(b, rng, x, y, z, o = {}) {
  const h = o.h ?? 2.6;
  const col = shade(0x6a6255, 0.55);
  b.solid('concrete', x, y + 0.08, z, 0.6, 0.16, 0.6, { color: tint(0x8f8778), collide: false, shadow: 'cast' });
  b.solid('metalPainted', x, y + 0.19, z, 0.3, 0.05, 0.3, { color: col, collide: false, shadow: 'none' });
  tube(b, 'metalPainted', x, y + 0.22 + h / 2, z, 0.03, h, 0, 0, 0, { color: col, seg: 6, shadow: 'cast' });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + rng();
    const ax = x + Math.cos(a) * 1.5;
    const az = z + Math.sin(a) * 1.5;
    rod(b, 'metalPainted', (x + ax) / 2, y + 0.1 + h * 0.35, (z + az) / 2,
      ax - x, -(h * 0.7), az - z, Math.hypot(1.5, h * 0.7), 0.008, shade(0x4a453e, 0.4), { seg: 3 });
    b.solid('concrete', ax, y + 0.1, az, 0.26, 0.2, 0.26, { color: tint(0x8f8778), collide: false, shadow: 'cast' });
  }
  for (let k = 0; k < 4; k++) {
    b.solid('metalPainted', x, y + 0.22 + h * 0.55 + k * 0.19, z, 0.5, 0.025, 0.025, {
      tile: 0.2, rotY: rng() * 0.4, color: col, collide: false, shadow: 'none',
    });
  }
}

/* -------------------------------------------------------------------------- */
/*                      the props that are not containers                      */
/* -------------------------------------------------------------------------- */

/** Timber cable reel. Big, round, unmistakable, and excellent standing cover. */
export function cableReel(b, rng, x, z, rotY = 0, o = {}) {
  const r = o.r ?? 0.95;
  const w = o.w ?? 0.8;
  const wood = vary(rng, 0x8a6a44, 0.14);
  const lying = o.lying ?? false;
  if (lying) {
    // Fallen flat: a low round table of a thing, and crouch cover.
    b.add('wood', cylGeo(r, r, 0.07, 14, 0.7), trs(x, 0.04, z, 0, rotY, 0), wood, 'both');
    b.add('wood', cylGeo(r, r, 0.07, 14, 0.7), trs(x, w - 0.04, z, 0, rotY, 0), wood.clone().multiplyScalar(0.94), 'both');
    b.add('polymer', cylGeo(r * 0.6, r * 0.6, w - 0.14, 14, 0.5), trs(x, w / 2, z, 0, rotY, 0), shade(0x2e3540, 0.4), 'both');
    b.collider(x - r, 0, z - r, x + r, w, z + r, 'wood');
    b.markCover(x, z, r, r, w, 'low');
    return;
  }
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  for (const sgn of [-1, 1]) {
    const px = x + s * sgn * w / 2;
    const pz = z + c * sgn * w / 2;
    b.add('wood', cylGeo(r, r, 0.08, 16, 0.7), trs(px, r, pz, 0, rotY, Math.PI / 2), wood, 'both');
    // Flange ribs, spoked round the axle and standing proud of the disc, so the
    // flange reads as a built-up timber wheel rather than a plywood circle.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI;
      angled(b, 'wood', px + s * sgn * 0.085, r, pz + c * sgn * 0.085,
        0.12, r * 1.86, 0.06, 0, rotY, a, { color: wood.clone().multiplyScalar(0.8), shadow: 'cast' });
    }
    // Hub and axle stub.
    b.add('wood', cylGeo(r * 0.24, r * 0.24, 0.12, 10, 0.4), trs(px + s * sgn * 0.09, r, pz + c * sgn * 0.09, 0, rotY, Math.PI / 2),
      wood.clone().multiplyScalar(0.76), 'cast');
    b.add('metalPainted', cylGeo(0.045, 0.045, 0.18, 8, 0.3), trs(px + s * sgn * 0.16, r, pz + c * sgn * 0.16, 0, rotY, Math.PI / 2),
      shade(0x5a5248, 0.5), 'none');
  }
  // The drum and the cable wound on it.
  b.add('wood', cylGeo(r * 0.42, r * 0.42, w - 0.1, 12, 0.6), trs(x, r, z, 0, rotY, Math.PI / 2), wood.clone().multiplyScalar(0.9), 'both');
  const turns = Math.max(3, Math.round((w - 0.24) / 0.09));
  for (let i = 0; i < turns; i++) {
    const t = -(w - 0.24) / 2 + ((w - 0.24) * i) / (turns - 1);
    b.add('polymer', cylGeo(r * 0.72, r * 0.72, 0.075, 12, 0.4), trs(x + s * t, r, z + c * t, 0, rotY, Math.PI / 2),
      shade(0x2e3540, 0.42 + rng() * 0.08), 'both');
  }
  const [hw, hd] = rotExtent(w, r * 2, rotY);
  b.collider(x - hw, 0, z - hd, x + hw, r * 2, z + hd, 'wood');
  b.markCover(x, z, hw, hd, r * 2, r * 2 > 1.3 ? 'high' : 'low');
}

/** Stack of large-bore concrete pipes. Cover you can see through and shoot under. */
export function concretePipes(b, rng, x, z, rotY = 0, o = {}) {
  const r = o.r ?? 0.5;
  const len = o.len ?? 2.4;
  const rows = o.rows ?? 2;
  const perRow = o.perRow ?? 3;
  const col = () => vary(rng, 0xa39a88, 0.09);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  let maxSpan = 0;
  for (let row = 0; row < rows; row++) {
    const n = perRow - row;
    if (n < 1) break;
    const span = n * r * 2;
    maxSpan = Math.max(maxSpan, span);
    for (let i = 0; i < n; i++) {
      const t = (i - (n - 1) / 2) * r * 2.02;
      const px = x + c * t;
      const pz = z - s * t;
      const py = r + row * r * 1.74;
      b.add('concrete', cylGeo(r, r, len, 14, TEXEL.concrete, true), trs(px, py, pz, 0, rotY, Math.PI / 2), col(), 'both');
      b.add('concrete', cylGeo(r * 0.78, r * 0.78, len - 0.02, 12, TEXEL.concrete, true), trs(px, py, pz, 0, rotY, Math.PI / 2),
        shade(0x4a453e, 0.34), 'none');
      // Socket collar at one end.
      b.add('concrete', cylGeo(r * 1.1, r * 1.1, 0.14, 14, TEXEL.concrete), trs(px + s * (len / 2 - 0.07), py, pz + c * (len / 2 - 0.07), 0, rotY, Math.PI / 2),
        col(), 'cast');
    }
  }
  const h = r * 2 + (rows - 1) * r * 1.74;
  const [hw, hd] = rotExtent(maxSpan, len, rotY);
  b.collider(x - hw, 0, z - hd, x + hw, h, z + hd, 'concrete');
  b.markCover(x, z, hw, hd, h, h > 1.3 ? 'high' : 'low');
  // Timber chocks so the bottom row is not resting on nothing.
  for (const sgn of [-1, 1]) {
    b.solid('wood', x + s * sgn * (len / 2 - 0.3), 0.06, z + c * sgn * (len / 2 - 0.3), maxSpan + 0.2, 0.12, 0.16, {
      rotY, color: vary(rng, 0x6b5540, 0.14), collide: false, shadow: 'cast',
    });
  }
}

/** Builder's skip. Ubiquitous, instantly readable, and full of the last collapse. */
export function skipBin(b, rng, stones, x, z, rotY = 0, o = {}) {
  const L = o.len ?? 3.4;
  const W = 1.72;
  const h = 1.24;
  const col = o.color ?? accent([0xc47a1e, 0x9a3f2c, 0x3f6a72][Math.floor(rng() * 3)], { lum: 0.72 });
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  // Tapered body, faked with two stacked boxes so it is wider at the top.
  b.solid('metalPainted', x, h * 0.32, z, L - 0.5, h * 0.64, W - 0.3, { tile: 0.8, rotY, color: col, matId: 'metal', cover: 'low' });
  b.solid('metalPainted', x, h * 0.82, z, L, h * 0.4, W, { tile: 0.8, rotY, color: col, collide: false, shadow: 'cast' });
  // Top rail, corner posts, chain lugs.
  b.solid('metalPainted', x, h + 0.04, z, L + 0.06, 0.09, W + 0.06, { tile: 0.5, rotY, color: col.clone().multiplyScalar(0.82), collide: false, shadow: 'cast' });
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const [px, pz] = at(sx * (L / 2 - 0.08), sz * (W / 2 - 0.06));
      b.solid('metalPainted', px, h * 0.6, pz, 0.1, h, 0.1, { tile: 0.3, rotY, color: col.clone().multiplyScalar(0.82), collide: false, shadow: 'none' });
    }
    const [lx2, lz2] = at(sx * (L / 2 - 0.6), 0);
    b.solid('metalPainted', lx2, h * 0.5, lz2, 0.08, 0.5, W + 0.12, { tile: 0.3, rotY, color: col.clone().multiplyScalar(0.7), collide: false, shadow: 'none' });
  }
  // Interior: dark, and heaped over the rail.
  b.solid('metalPainted', x, h * 0.6, z, L - 0.16, h * 0.8, W - 0.16, { tile: 0.6, rotY, color: shade(0x2e2822, 0.2), collide: false, shadow: 'none' });
  for (let i = 0; i < 9; i++) {
    const [px, pz] = at((rng() - 0.5) * (L - 0.6), (rng() - 0.5) * (W - 0.5));
    const w = 0.3 + rng() * 0.6;
    angled(b, rng() < 0.4 ? 'brick' : 'concrete', px, h * 0.95 + rng() * 0.2, pz, w, 0.1 + rng() * 0.14, w * 0.7,
      (rng() - 0.5) * 1.5, rng() * 3, (rng() - 0.5) * 1.5,
      { color: vary(rng, rng() < 0.4 ? 0x8a5240 : 0x8d8578, 0.16) });
  }
  for (let i = 0; i < 3; i++) {
    const [px, pz] = at((rng() - 0.5) * L, (rng() - 0.5) * W);
    rod(b, 'wood', px, h + 0.3 + rng() * 0.3, pz, (rng() - 0.5), 0.6 + rng(), (rng() - 0.5), 0.9 + rng() * 0.8, 0.035,
      vary(rng, 0x7a5f42, 0.16), { seg: 4 });
  }
  if (stones) rubble(b, rng, stones, x, z, L * 0.8, 10, { scale: 0.7 });
  b.markCover(x, z, ...rotExtent(L, W, rotY), h, 'low');
}

/** Horizontal fuel tank on saddles, with a bund and a fill point. */
export function oilTank(b, rng, x, z, rotY = 0, o = {}) {
  const r = o.r ?? 0.72;
  const len = o.len ?? 3.0;
  const col = o.color ?? vary(rng, [0x8a8a72, 0x6a7a68, 0x8a7050][Math.floor(rng() * 3)], 0.1);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const y = 0.62;
  b.add('metalPainted', cylGeo(r, r, len, 14, 0.9), trs(x, y + r, z, 0, rotY, Math.PI / 2), col, 'both');
  for (const sgn of [-1, 1]) {
    b.add('metalPainted', cylGeo(r * 0.98, r * 0.6, 0.22, 14, 0.6), trs(x + s * sgn * (len / 2 + 0.09), y + r, z + c * sgn * (len / 2 + 0.09), 0, rotY, sgn * Math.PI / 2),
      col.clone().multiplyScalar(0.94), 'cast');
    // Saddle.
    const [px, pz] = [x + s * sgn * (len / 2 - 0.6), z + c * sgn * (len / 2 - 0.6)];
    b.solid('metalPainted', px, y / 2, pz, 0.28, y, r * 1.7, { tile: 0.5, rotY, color: shade(0x5a5248, 0.5), collide: false, shadow: 'cast' });
  }
  // Bund the whole thing sits in, and the spill it has clearly had.
  const [hw, hd] = rotExtent(len + 0.9, r * 2 + 0.9, rotY);
  for (const [ox, oz, bw, bd] of [[0, -1, 1, 0], [0, 1, 1, 0], [-1, 0, 0, 1], [1, 0, 0, 1]]) {
    b.solid('concrete', x + ox * hw * c + oz * hd * s, 0.12, z - ox * hw * s + oz * hd * c,
      bw ? hw * 2 : 0.2, 0.24, bd ? hd * 2 : 0.2, { rotY: 0, color: tint(0x8f8778), collide: false, shadow: 'cast' });
  }
  groundStain(b, rng, x, z, 1.3, {});
  // Fill point and gauge.
  const [fx, fz] = [x + s * (len * 0.2), z + c * (len * 0.2)];
  tube(b, 'metalPainted', fx, y + r * 2 + 0.12, fz, 0.05, 0.24, 0, 0, 0, { color: shade(0x5a5248, 0.5), seg: 6 });
  b.solid('metalPainted', fx, y + r * 2 + 0.26, fz, 0.14, 0.05, 0.14, { tile: 0.15, rotY, color: shade(0x8a8479, 0.8), collide: false, shadow: 'none' });
  b.collider(x - hw + 0.2, 0, z - hd + 0.2, x + hw - 0.2, y + r * 2, z + hd - 0.2, 'metal');
  b.markCover(x, z, hw - 0.2, hd - 0.2, y + r * 2, 'high');
}

/** Banded pallet of blocks or bricks, shrink-wrapped and half unloaded. */
export function brickPallet(b, rng, x, z, rotY = 0, o = {}) {
  pallet(b, rng, x, 0, z, { rotY });
  const rows = o.rows ?? (4 + Math.floor(rng() * 4));
  const hex = o.hex ?? (rng() < 0.5 ? 0x8a5240 : 0x9a938a);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const bw = 0.23;
  const bd = 0.11;
  const bh = 0.075;
  for (let r = 0; r < rows; r++) {
    const cols = r > rows - 2 ? 3 : 4;         // top course part-taken
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < 6; j++) {
        if (r === rows - 1 && rng() < 0.4) continue;
        const lx = (i - (cols - 1) / 2) * (bw + 0.01) + (r % 2) * 0.02;
        const lz = (j - 2.5) * (bd + 0.01);
        b.solid('brick', x + lx * c + lz * s, TUNING.palletH + bh / 2 + r * bh, z - lx * s + lz * c, bw, bh, bd, {
          rotY, color: vary(rng, hex, 0.14, 0.04, { sat: 0.9 }), collide: false, shadow: 'cast',
          uOff: rng() * 5, vOff: rng() * 5,
        });
      }
    }
  }
  const h = TUNING.palletH + rows * bh;
  b.collider(x - 0.6, 0, z - 0.45, x + 0.6, h, z + 0.45, 'concrete');
  b.markCover(x, z, 0.6, 0.45, h, h > 0.7 ? 'low' : undefined);
  // Banding straps.
  for (const sgn of [-1, 1]) {
    b.solid('polymer', x + sgn * 0.28 * c, TUNING.palletH + rows * bh * 0.55, z - sgn * 0.28 * s, 0.03, rows * bh * 1.02, 0.7, {
      tile: 0.2, rotY, color: shade(0x9a9080, 0.85), collide: false, shadow: 'none',
    });
  }
}

/**
 * A small masonry kiosk — substation, guard post, lock-up. The cheapest way to put a
 * *building* somewhere a lane needs stopping, rather than yet another container.
 */
export function kiosk(b, rng, x, z, o = {}) {
  const w = o.w ?? 3.2;
  const d = o.d ?? 2.6;
  const h = o.h ?? 2.9;
  const rotY = o.rotY ?? 0;
  const col = o.color ?? vary(rng, o.hex ?? 0x9c9382, 0.06);
  b.solid(o.surface ?? 'concrete', x, h / 2, z, w, h, d, { rotY, color: col, cover: 'high' });
  // Capping and a slight roof overhang, which is most of what says "built".
  b.solid('concrete', x, h + 0.09, z, w + 0.34, 0.18, d + 0.34, { rotY, color: tint(0x8f8778), collide: false, shadow: 'cast' });
  b.solid('concrete', x, h + 0.24, z, w + 0.2, 0.12, d + 0.2, { rotY, color: tint(0x9a9285), collide: false, shadow: 'cast' });
  // Plinth.
  b.solid('concrete', x, 0.16, z, w + 0.18, 0.32, d + 0.18, { rotY, color: tint(0x8d8578), collide: false, shadow: 'cast' });
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  // A steel door on the long face, with a louvre beside it.
  const [dx, dz] = [x + s * (d / 2 + 0.03), z + c * (d / 2 + 0.03)];
  b.solid('metalPainted', dx, 1.06, dz, 0.95, 2.05, 0.08, {
    tile: 0.6, rotY, color: vary(rng, 0x5a6a5c, 0.12, 0.04, { sat: 0.9 }), collide: false, shadow: 'cast',
  });
  b.solid('concrete', dx, 2.18, dz, 1.2, 0.16, 0.16, { rotY, color: tint(0xa8a08e), collide: false, shadow: 'cast' });
  ventLouvre(b, rng, x + s * (d / 2) + c * (w * 0.3), 1.6, z + c * (d / 2) - s * (w * 0.3), 0.7, 0.8, rotY, {});
  if (o.hazard !== false) {
    // A hazard placard: one of the map's few genuinely saturated objects.
    b.solid('metalPainted', dx, 1.9, dz, 0.3, 0.3, 0.03, {
      tile: 0.2, rotY, color: accent(0xd8c23a, { lum: 1.3 }), collide: false, shadow: 'none',
    });
  }
  return h;
}

/**
 * Panel van, burnt out.
 *
 * The map's vehicle library ran car (4.3 m) → truck (6.6 m) → bus (10.6 m), and the
 * gap between the first two is exactly the size a shipping container was being used to
 * fill. A van is the right object for a 5 m blocker in a street: it is standing cover,
 * it has a shape a container does not, and unlike a container it belongs where the
 * traffic is.
 */
export function vanWreck(b, rng, x, z, rotY = 0, o = {}) {
  const L = 5.0;
  const W = 2.0;
  const col = o.color ?? vary(rng, o.hex ?? 0x8a8579, 0.14);
  const dark = col.clone().multiplyScalar(0.82);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const front = o.front ?? 1;

  // Box body: the tall rear two thirds, standing cover on its own.
  const [bx, bz] = at(-0.9, 0);
  b.solid('metalPainted', bx, 1.42, bz, 3.3, 1.86, W, { tile: 0.8, rotY, color: col, cover: 'high', matId: 'metal' });
  // Cab, lower, with a raked screen and a bonnet.
  const [cx, cz] = at(1.75, 0);
  b.solid('metalPainted', cx, 1.14, cz, 1.7, 1.3, W - 0.1, { tile: 0.7, rotY, color: col, cover: 'high', matId: 'metal' });
  const [wx, wz] = at(1.2, 0);
  angled(b, 'polymer', wx, 1.62, wz, 0.14, 0.78, W - 0.34, 0, rotY, -0.28,
    { tile: 0.7, color: shade(0x5a6a74, 0.8, 0.6), shadow: 'none' });
  const [nx2, nz2] = at(front * 2.5, 0);
  b.solid('metalPainted', nx2, 0.9, nz2, 0.5, 0.42, W - 0.16, { tile: 0.6, rotY, color: dark, collide: false, shadow: 'cast' });
  // Roof, ribbed, with a vent — the silhouette read from every roof on the map.
  b.solid('metalPainted', bx, 2.37, bz, 3.34, 0.08, W + 0.02, { tile: 0.9, rotY, color: dark, collide: false, shadow: 'cast' });
  for (let i = -1; i <= 1; i++) {
    const [rx, rz] = at(-0.9 + i * 1.0, 0);
    b.solid('metalPainted', rx, 2.43, rz, 0.1, 0.05, W - 0.06, { tile: 0.4, rotY, color: dark, collide: false, shadow: 'none' });
  }
  const [vx, vz] = at(-1.6, 0.3);
  b.solid('polymer', vx, 2.47, vz, 0.42, 0.12, 0.42, { tile: 0.3, rotY, color: shade(0x9a9488, 0.9), collide: false, shadow: 'cast' });
  // A livery band and a sign panel on the flank: this was somebody's business.
  b.solid('metalPainted', bx, 1.06, bz, 3.32, 0.3, W + 0.04, {
    tile: 0.5, rotY, color: o.band ?? accent([0x2f6a8c, 0xb04a2a, 0x6a8a3a][Math.floor(rng() * 3)], { lum: 0.8 }),
    collide: false, shadow: 'none',
  });
  // Rear doors, one swung wide, and the dark load space behind them.
  const [dx, dz] = at(-L / 2 - 0.06, 0);
  b.solid('metalPainted', dx, 1.42, dz, 0.07, 1.7, W - 0.2, { tile: 0.6, rotY, color: shade(0x2a2620, 0.2), collide: false, shadow: 'none' });
  for (const sgn of [-1, 1]) {
    const swing = sgn > 0 ? 1.2 + rng() * 0.5 : 0.15;
    const lw = W / 2 - 0.1;
    const hz2 = sgn * (W / 2 - 0.06);
    const [px, pz] = at(-L / 2 - Math.sin(swing) * lw / 2, hz2 - sgn * (1 - Math.cos(swing)) * lw / 2);
    angled(b, 'metalPainted', px, 1.42, pz, lw, 1.68, 0.06, 0, rotY - swing * sgn, 0,
      { tile: 0.6, color: dark });
  }
  // Side door slid back into its pocket.
  const [sx2, sz2] = at(0.3, W / 2 + 0.03);
  b.solid('metalPainted', sx2, 1.3, sz2, 1.2, 1.5, 0.07, { tile: 0.5, rotY, color: dark, collide: false, shadow: 'cast' });
  const [ox, oz] = at(-0.55, W / 2 + 0.01);
  b.solid('metalPainted', ox, 1.3, oz, 1.2, 1.5, 0.05, { tile: 0.5, rotY, color: shade(0x2a2620, 0.22), collide: false, shadow: 'none' });
  // Lamps, bumper, mirror arms.
  for (const sgn of [-1, 1]) {
    const [hx, hz] = at(front * (L / 2 + 0.04), sgn * (W / 2 - 0.34));
    b.solid('polymer', hx, 0.72, hz, 0.08, 0.22, 0.28, { tile: 0.3, rotY, color: shade(0xb9b6ac, 1.0), collide: false, shadow: 'none' });
    const [mx, mz] = at(front * 1.25, sgn * (W / 2 + 0.16));
    tube(b, 'metalPainted', mx, 1.72, mz, 0.018, 0.34, 0, 0, Math.PI / 2, { color: shade(0x4a453e, 0.4), seg: 4 });
    b.solid('polymer', mx, 1.62, mz, 0.08, 0.26, 0.12, { tile: 0.2, rotY, color: shade(0x2e2b26, 0.3), collide: false, shadow: 'none' });
  }
  const [px3, pz3] = at(front * (L / 2 + 0.1), 0);
  b.solid('polymer', px3, 0.56, pz3, 0.16, 0.24, W - 0.1, { tile: 0.4, rotY, color: shade(0x35322c, 0.34), collide: false, shadow: 'cast' });
  wheels(b, x, o.flat ? -0.06 : 0, z, rotY, [[1.55, 0.92], [1.55, -0.92], [-1.5, 0.94], [-1.5, -0.94]], o.flat ? 0.32 : 0.36, 0.24);
  const [hw, hd] = rotExtent(L, W, rotY);
  b.collider(x - hw, 0, z - hd, x + hw, 2.3, z + hd, 'metal');
  b.markCover(x, z, hw, hd, 2.3, 'high');
}

/**
 * Stack of sawn timber on bearers.
 *
 * A yard blocker that is not a box: the ragged ends, the sticker gaps between courses
 * and the couple of boards that have slid off give it a horizontally striped silhouette
 * that reads as *material waiting to be used* rather than as freight.
 */
export function timberStack(b, rng, x, z, rotY = 0, o = {}) {
  const len = o.len ?? 4.2;
  const w = o.w ?? 1.15;
  const courses = o.courses ?? (5 + Math.floor(rng() * 4));
  const bh = 0.075;
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const hex = o.hex ?? 0x9a7c52;
  // Bearers off the dirt — timber never sits on the ground.
  for (const sgn of [-1, 1]) {
    const [px, pz] = at(sgn * (len / 2 - 0.6), 0);
    b.solid('wood', px, 0.07, pz, 0.16, 0.14, w + 0.25, {
      rotY, color: vary(rng, 0x6b5540, 0.14), collide: false, shadow: 'cast',
    });
  }
  const perCourse = Math.max(3, Math.round(w / 0.2));
  let y = 0.14;
  for (let r = 0; r < courses; r++) {
    // A sticker course every third layer, which is what leaves the visible air gaps.
    const stick = r > 0 && r % 3 === 0;
    if (stick) {
      for (let k = 0; k < 3; k++) {
        const [px, pz] = at((k - 1) * (len / 3.2), 0);
        b.add('wood', timberGeo(0.09, 0.04, w + 0.1), trs(px, y + 0.02, pz, 0, rotY + Math.PI / 2, 0),
          vary(rng, hex, 0.12, 0.02, { sat: 0.6 }), 'none');
      }
      y += 0.04;
    }
    const n = r === courses - 1 ? Math.max(1, perCourse - 1 - Math.floor(rng() * 2)) : perCourse;
    for (let i = 0; i < n; i++) {
      const lz = (i - (perCourse - 1) / 2) * (w / perCourse);
      // Ends are not flush: boards get cut to different lengths and stacked anyway.
      const bl = len * (0.86 + rng() * 0.14);
      const off = (rng() - 0.5) * (len - bl);
      const [px, pz] = at(off, lz);
      b.add('wood', timberGeo(bl, bh, w / perCourse - 0.012), trs(px, y + bh / 2, pz, 0, rotY, 0),
        vary(rng, hex, 0.13, 0.02, { sat: 0.62 }), 'both');
    }
    y += bh;
  }
  // A board or two that has slid off and is leaning against the stack.
  for (let k = 0; k < 1 + Math.floor(rng() * 2); k++) {
    const sgn = rng() < 0.5 ? -1 : 1;
    const [px, pz] = at((rng() - 0.5) * len * 0.5, sgn * (w / 2 + 0.28));
    b.add('wood', timberGeo(len * 0.8, bh, 0.16), trs(px, y * 0.45, pz, 0, rotY + (rng() - 0.5) * 0.2, sgn * 0.5),
      vary(rng, hex, 0.13, 0.02, { sat: 0.62 }), 'cast');
  }
  const [hw, hd] = rotExtent(len, w, rotY);
  b.collider(x - hw, 0, z - hd, x + hw, y, z + hd, 'wood');
  b.markCover(x, z, hw, hd, y, y > 1.25 ? 'high' : 'low');
  return y;
}

/**
 * Stacked jute sacks on a pallet, with a tarpaulin thrown over one end.
 *
 * Every blocker on this map is a hard-edged rectangle. A sack stack is the same
 * footprint made of something that *slumps*, and one soft silhouette in a lane of
 * containers and barriers does more for the read than another ten hard ones.
 */
export function sackStack(b, rng, x, z, rotY = 0, o = {}) {
  const rows = o.rows ?? (4 + Math.floor(rng() * 2));
  const sw = 0.56;
  const sh = 0.24;
  const cols = o.cols ?? 2;
  const deep = o.deep ?? 2;
  pallet(b, rng, x, 0, z, { rotY });
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const hex = SACK_HEX[Math.floor(rng() * SACK_HEX.length)];
  let top = TUNING.palletH;
  for (let r = 0; r < rows; r++) {
    // Alternate courses cross-stacked, the way a sack stack is actually built.
    const cross = r % 2 === 1;
    const nx2 = cross ? deep : cols;
    const nz2 = cross ? cols : deep;
    for (let i = 0; i < nx2; i++) {
      for (let j = 0; j < nz2; j++) {
        // The top course is short a few sacks and the ones left have been dragged.
        if (r === rows - 1 && rng() < 0.45) continue;
        const lx = (i - (nx2 - 1) / 2) * (cross ? sw * 0.62 : sw) + (rng() - 0.5) * 0.05;
        const lz = (j - (nz2 - 1) / 2) * (cross ? sw : sw * 0.62) + (rng() - 0.5) * 0.05;
        const [px, pz] = at(lx, lz);
        const bulge = 1 - r / (rows * 2.4);          // lower sacks are squashed flatter
        b.add('fabric', sphereGeo(0.5, 8, 5, TEXEL.fabric),
          trs(px, TUNING.palletH + sh * (r + 0.5), pz,
            0, rotY + (cross ? Math.PI / 2 : 0) + (rng() - 0.5) * 0.3, (rng() - 0.5) * 0.16,
            sw * (cross ? 0.68 : 1.02) * bulge, sh * 1.24, sw * (cross ? 1.02 : 0.68) * bulge),
          vary(rng, hex, 0.12, 0.02), 'both');
      }
    }
    top = TUNING.palletH + sh * (r + 1);
  }
  // Tarpaulin over one end, tied down — and the reason the stack has a soft corner.
  if (o.tarp !== false) {
    const tw = sw * (cols + 0.6);
    const [tx2, tz2] = at(-sw * 0.35, 0);
    b.add('fabric',
      sheetGeo(tw, sw * (deep + 0.5), 7, 4,
        (u, v) => -Math.abs(u) * 0.5 * tw * 0.5 - v * v * 0.36 + Math.cos(u * Math.PI * 2) * 0.03,
        {
          tile: TEXEL.canvas,
          color: vary(rng, rng() < 0.5 ? 0x8fa2a8 : 0x9a9280, 0.1, 0.02, { sat: 0.6 }),
          shade: (u, v) => 0.78 + 0.3 * (1 - Math.abs(u) * 2) - v * 0.12,
        }),
      trs(tx2, top + 0.06, tz2, 0, rotY, 0), null, 'both', true, CLOTH_MAT);
    for (const sgn of [-1, 1]) {
      const [rx, rz] = at(-sw * 0.35 + sgn * tw * 0.45, sw * (deep + 0.5) * 0.8);
      cable(b, new THREE.Vector3(rx, top - 0.1, rz), new THREE.Vector3(rx + sgn * 0.3, 0.05, rz + 0.2),
        0.04, { r: 0.012, color: shade(0x8a8070, 0.7) });
    }
  }
  const [hw, hd] = rotExtent(sw * cols + 0.3, sw * deep + 0.3, rotY);
  b.collider(x - hw, 0, z - hd, x + hw, top, z + hd, 'sand');
  b.markCover(x, z, hw, hd, top, top > 1.25 ? 'high' : 'low');
  return top;
}

/** Two-wheeled water bowser: a tank on a drawbar chassis. Reads as camp logistics. */
export function bowser(b, rng, x, z, rotY = 0, o = {}) {
  const r = o.r ?? 0.62;
  const len = o.len ?? 2.3;
  const col = o.color ?? vary(rng, [0x7a8a6a, 0x8a8272, 0x6a7a8a][Math.floor(rng() * 3)], 0.1);
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const y = 0.66;
  // Chassis and drawbar.
  b.solid('metalPainted', x, y - 0.1, z, len + 0.3, 0.14, 0.7, { tile: 0.6, rotY, color: shade(0x4a453e, 0.42), collide: false, shadow: 'cast' });
  const [dx, dz] = at(len / 2 + 0.55, 0);
  b.solid('metalPainted', dx, y - 0.06, dz, 1.0, 0.1, 0.16, { tile: 0.4, rotY, color: shade(0x54504a, 0.46), collide: false, shadow: 'cast' });
  tube(b, 'metalPainted', dx + Math.cos(rotY) * 0.4, y * 0.55, dz - Math.sin(rotY) * 0.4, 0.03, y, 0, 0, 0,
    { color: shade(0x54504a, 0.46), seg: 5 });
  b.add('polymer', cylGeo(0.1, 0.1, 0.06, 8, 0.3), trs(dx + Math.cos(rotY) * 0.4, 0.1, dz - Math.sin(rotY) * 0.4, 0, rotY, Math.PI / 2),
    shade(0x1b1a19), 'cast');
  // Tank, dished ends, hoops.
  b.add('metalPainted', cylGeo(r, r, len, 14, 0.9), trs(x, y + r, z, 0, rotY, Math.PI / 2), col, 'both');
  for (const sgn of [-1, 1]) {
    b.add('metalPainted', cylGeo(r * 0.96, r * 0.55, 0.2, 14, 0.6),
      trs(x + s * sgn * (len / 2 + 0.08), y + r, z + c * sgn * (len / 2 + 0.08), 0, rotY, sgn * Math.PI / 2),
      col.clone().multiplyScalar(0.94), 'cast');
    const [px, pz] = at(sgn * (len / 2 - 0.35), 0);
    b.add('metalPainted', cylGeo(r + 0.03, r + 0.03, 0.07, 14, 0.4), trs(px, y + r, pz, 0, rotY, Math.PI / 2),
      col.clone().multiplyScalar(0.84), 'none');
  }
  // Filler hatch, delivery tap and the puddle under it.
  b.add('metalPainted', cylGeo(0.19, 0.19, 0.08, 10, 0.3), trs(x, y + r * 2 + 0.02, z), shade(0x8a8479, 0.85), 'cast');
  const [tx2, tz2] = at(-len / 2 + 0.3, 0.3);
  tube(b, 'metalPainted', tx2, y + 0.2, tz2, 0.026, 0.3, Math.PI / 2, rotY, 0, { color: shade(0x6a655c, 0.55), seg: 5 });
  groundStain(b, rng, tx2, tz2 + 0.3, 0.7, { hex: 0x4a4a42, lum: 0.5 });
  b.solid('metalPainted', x, y + r * 1.5, z + (rotY ? 0 : r + 0.02), 0.3, 0.3, 0.03, {
    tile: 0.2, rotY, color: accent(0xd8c23a, { lum: 1.25 }), collide: false, shadow: 'none',
  });
  wheels(b, x, 0, z, rotY, [[-0.2, 0.62], [-0.2, -0.62]], 0.34, 0.2);
  const [hw, hd] = rotExtent(len + 0.4, r * 2 + 0.3, rotY);
  b.collider(x - hw, 0, z - hd, x + hw, y + r * 2, z + hd, 'metal');
  b.markCover(x, z, hw, hd, y + r * 2, 'high');
}

/**
 * A gangway between two roofs.
 *
 * Roofs on this map were eleven separate islands: every one of them was a place you
 * climbed to, stood on, and climbed back down from. A plank bridge is the cheapest
 * possible fix — it turns two dead ends into a route, and because it is narrow, open
 * and overlooked it is a route with a real cost attached.
 */
export function gangway(b, rng, x, z, len, axis = 'x', y = 4.0, o = {}) {
  const w = o.w ?? 1.0;
  const alongX = axis === 'x';
  const hw = alongX ? len / 2 : w / 2;
  const hd = alongX ? w / 2 : len / 2;
  const col = o.color ?? shade(0x6a6255, 0.55);
  // Stringers, then the deck boards across them.
  for (const sgn of [-1, 1]) {
    const px = x + (alongX ? 0 : sgn * (w / 2 - 0.05));
    const pz = z + (alongX ? sgn * (w / 2 - 0.05) : 0);
    b.solid('metalPainted', px, y - 0.11, pz, alongX ? len : 0.09, 0.18, alongX ? 0.09 : len, {
      tile: 0.6, color: col, collide: false, shadow: 'cast',
    });
  }
  const boards = Math.max(3, Math.round(len / 0.34));
  for (let i = 0; i < boards; i++) {
    const t = -len / 2 + (len * (i + 0.5)) / boards;
    const px = x + (alongX ? t : 0);
    const pz = z + (alongX ? 0 : t);
    b.add('wood', timberGeo(w, 0.05, len / boards - 0.03), trs(px, y - 0.02, pz, 0, alongX ? Math.PI / 2 : 0, 0),
      vary(rng, 0x9a7c52, 0.13, 0.02, { sat: 0.62 }), 'both');
  }
  b.collider(x - hw, y - 0.14, z - hd, x + hw, y + 0.02, z + hd, 'wood');
  // Handrail one side only: a gangway you can be shot off is a decision, not a corridor.
  const side = o.rail ?? 1;
  railing(b, {
    x: x + (alongX ? 0 : side * (w / 2 + 0.04)),
    y,
    z: z + (alongX ? side * (w / 2 + 0.04) : 0),
    len: len - 0.1, axis, height: 1.0, color: col,
  });
  b.markCover(x, z, hw, hd, y + 1.0, 'high', y);
}

/* -------------------------------------------------------------------------- */
/*                          somebody lived here                                */
/* -------------------------------------------------------------------------- */

/**
 * A domestic room, authored as one moment rather than as scattered props.
 *
 * The tenement's ground floor was a white ceramic showroom furnished with a market
 * stall and warehouse racking — nothing anyone has ever lived in. The point of this
 * is not the furniture count; it is that a player walking in can read what somebody
 * was doing when they left, which is the one thing the whole map did not have.
 */
export function domestic(b, rng, x, z, rotY = 0, o = {}) {
  const y = o.y ?? 0;
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  const wood = vary(rng, 0x8a6a44, 0.12);

  // Rug: the thing that turns a slab into a floor.
  b.solid('fabric', x, y + 0.012, z, 2.6, 0.024, 1.8, {
    tile: TEXEL.canvas, rotY: rotY + 0.06, color: vary(rng, 0x8c4436, 0.12, 0.05, { sat: 1.0 }),
    collide: false, shadow: 'none',
  });
  b.solid('fabric', x, y + 0.02, z, 2.2, 0.022, 1.4, {
    tile: TEXEL.canvas, rotY: rotY + 0.06, color: vary(rng, 0x6a4a58, 0.12, 0.05, { sat: 1.0 }),
    collide: false, shadow: 'none',
  });

  // Low table with the meal still on it.
  const [tx, tz] = at(0, 0);
  b.solid('wood', tx, y + 0.42, tz, 1.15, 0.06, 0.78, { rotY, color: wood, collide: false, shadow: 'cast' });
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const [px, pz] = at(sx * 0.5, sz * 0.32);
      b.solid('wood', px, y + 0.21, pz, 0.07, 0.42, 0.07, { rotY, color: wood.clone().multiplyScalar(0.85), collide: false, shadow: 'none' });
    }
  }
  b.collider(tx - 0.62, y, tz - 0.45, tx + 0.62, y + 0.48, tz + 0.45, 'wood');
  for (let i = 0; i < 3; i++) {
    const [px, pz] = at((rng() - 0.5) * 0.8, (rng() - 0.5) * 0.5);
    b.add('metalPainted', cylGeo(0.11 + rng() * 0.05, 0.09, 0.05, 10, 0.25), trs(px, y + 0.47, pz),
      shade(0xb9b6ac, 1.05), 'cast');
  }
  const [px2, pz2] = at(-0.3, 0.1);
  b.add('metalPainted', cylGeo(0.14, 0.14, 0.16, 10, 0.3), trs(px2, y + 0.53, pz2), shade(0x4a453e, 0.42), 'cast');

  // Two stools, one knocked over.
  for (const [lx, lz, down] of [[-1.1, 0.15, false], [0.95, -0.4, true]]) {
    const [sx2, sz2] = at(lx, lz);
    if (down) {
      angled(b, 'wood', sx2, y + 0.16, sz2, 0.36, 0.34, 0.36, Math.PI / 2 * 0.9, rotY + rng(), 0.2, { color: wood });
    } else {
      b.solid('wood', sx2, y + 0.4, sz2, 0.34, 0.05, 0.34, { rotY: rotY + rng(), color: wood, collide: false, shadow: 'cast' });
      for (const sx3 of [-1, 1]) for (const sz3 of [-1, 1]) {
        b.solid('wood', sx2 + sx3 * 0.13, y + 0.19, sz2 + sz3 * 0.13, 0.04, 0.38, 0.04, { color: wood.clone().multiplyScalar(0.85), collide: false, shadow: 'none' });
      }
    }
  }

  // Mattress and bedding against a wall.
  const [mx, mz] = at(o.bedX ?? -2.4, o.bedZ ?? 0.6);
  b.solid('fabric', mx, y + 0.09, mz, 1.9, 0.18, 1.0, {
    tile: TEXEL.canvas, rotY: rotY + 0.04, color: vary(rng, 0xa8a190, 0.1), collide: false, shadow: 'cast',
  });
  b.solid('fabric', mx + 0.25 * c, y + 0.21, mz - 0.25 * s, 1.2, 0.12, 0.95, {
    tile: TEXEL.canvas, rotY: rotY - 0.08, color: vary(rng, 0x486a68, 0.14, 0.05, { sat: 1.0 }), collide: false, shadow: 'cast',
  });
  b.add('fabric', sphereGeo(0.5, 8, 6, TEXEL.fabric), trs(mx - 0.7 * c, y + 0.24, mz + 0.7 * s, 0, rotY, 0, 0.52, 0.2, 0.36),
    vary(rng, 0xc8bfa8, 0.1), 'cast');

  // Cook stove with a pot, and the soot up the wall behind it.
  const [kx, kz] = at(o.stoveX ?? 2.2, o.stoveZ ?? -1.0);
  b.solid('metalPainted', kx, y + 0.2, kz, 0.5, 0.4, 0.44, { tile: 0.4, rotY, color: shade(0x4a453e, 0.4), collide: false, shadow: 'cast' });
  b.add('metalPainted', cylGeo(0.19, 0.17, 0.2, 10, 0.3), trs(kx, y + 0.5, kz), shade(0x35302a, 0.3), 'cast');
  b.add('metalPainted', cylGeo(0.2, 0.2, 0.02, 10, 0.3), trs(kx, y + 0.61, kz), shade(0x9a9488, 0.85), 'none');
  for (let i = 0; i < 4; i++) {
    const [wx, wz] = at((o.stoveX ?? 2.2) + (rng() - 0.5) * 0.7, (o.stoveZ ?? -1.0) + (rng() - 0.5) * 0.5);
    rod(b, 'wood', wx, y + 0.05, wz, rng() - 0.5, 0.1, rng() - 0.5, 0.35 + rng() * 0.3, 0.025,
      vary(rng, 0x6b5540, 0.16), { seg: 4 });
  }

  // Shelf with crockery, and a picture frame face-down on the floor.
  const [hx, hz] = at(o.shelfX ?? 2.3, o.shelfZ ?? 1.2);
  b.solid('wood', hx, y + 1.15, hz, 1.3, 0.05, 0.28, { rotY, color: wood.clone().multiplyScalar(0.9), collide: false, shadow: 'cast' });
  for (let i = 0; i < 4; i++) {
    const [cx2, cz2] = at((o.shelfX ?? 2.3) + (i - 1.5) * 0.28, (o.shelfZ ?? 1.2));
    b.add('metalPainted', cylGeo(0.075, 0.06, 0.11 + rng() * 0.08, 8, 0.2), trs(cx2, y + 1.24, cz2),
      vary(rng, 0xc0b8a4, 0.12), 'cast');
  }
  const [fx2, fz2] = at(0.6, 1.3);
  b.solid('wood', fx2, y + 0.02, fz2, 0.3, 0.03, 0.24, { rotY: rotY + rng(), color: vary(rng, 0x6f5c42, 0.12), collide: false, shadow: 'none' });
}
